import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Container, Table, Button, Badge, Form, InputGroup, Dropdown, Modal, Card, Row, Col, Offcanvas, Tabs, Tab, ListGroup, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  useGetLeadsQuery,
  useCreateLeadMutation,
  useCreateContactMutation,
  useCreateClientMutation,
  useCreateDealMutation,
  useUpdateLeadMutation,
  useDeleteLeadMutation,
  useGetMeetingsQuery,
  useCreateMeetingMutation,
  useGetEmailsQuery,
  useGetDealsQuery,
  useGetContactsQuery,
  useGetClientsQuery,
  useGetRulesEngineConfigQuery,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
  // Accounts hooks kept for future use; Contract Renewal is per-record.
  useGetAccountsQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
} from '../services/api';
import { DEFAULT_RULES_ENGINE_CONFIG, normalizeRulesEngineConfig } from '../constants/rulesEngine';
import { buildEmailThreadsForEntity, mergeEmailResponses } from '../utils/crmEmailThreads';
import { useUser } from '../contexts/UserContext';
import {
  preferencesTenantWriteSpread,
  useTenantIdForPreferences,
  useUserPreferencesQueryArg,
} from '../hooks/useTenantIdForPreferences';
import {
  getDateKeyInTimezone,
  getRelativeDayDifferenceInTimezone,
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
  toLocaleTimeStringInTimezone,
} from '../utils/tenantTimezone';
import { normalizeLabelKey } from '../utils/crmLabels';
import {
  formatCompactRenewalLabelFromRawDate,
  formatHtmlDateValueToUsStorage,
  getDaysUntilContractRenewalDate,
  isContractRenewalOverdue,
  normalizeClientDateToUsStorage,
} from '../utils/crmContractRenewal';
import { 
  Search, 
  Plus, 
  Folder, 
  Lightning,
  Trash, 
  ChevronDown, 
  ThreeDotsVertical, 
  Gear, 
  Person,
  ExclamationTriangle,
  Calendar,
  Phone,
  X,
  Envelope,
  FileText,
  Grid3x3,
  Briefcase,
  Tag,
  CurrencyDollar,
  Clock,
  CheckSquare,
  ArrowRight,
  InfoCircle,
  Dot,
  Pencil,
  Inbox,
  Archive,
  BuildingFill,
  GeoAltFill,
  Globe2,
  Linkedin,
  PeopleFill,
  ArrowLeft,
  Check,
  TypeBold,
  TypeItalic,
  TypeUnderline,
  Link45deg,
  At,
  ListUl,
  ListOl,
  Indent,
  TypeStrikethrough,
  Pin,
  Paperclip,
  Star,
  StarFill,
  Funnel,
  LockFill,
  Upload,
  Download,
  ArrowClockwise,
  Diamond,
  DiamondFill
} from 'react-bootstrap-icons';

interface Lead {
  id: string;
  name: string;
  visibility?: 'global' | 'personal';
  title: string;
  status?: string;
  accountId?: string;
  organization: string;
  accountStage?: string;
  contactStage?: string;
  labels: string[];
  nextActivity?: {
    type: 'phone' | 'calendar' | 'none';
    date?: string;
  };
  leadCreated: string;
  value?: string;
  currency?: string;
  expectedCloseDate?: string;
  contractRenewalDate?: string;
  inStore?: boolean;
  businessUnit?: string;
  owner?: string;
  sourceOrigin?: string;
  sourceChannel?: string;
  sourceChannelId?: string;
  email?: string;
  phone?: string;
  phoneType?: string;
  emailType?: string;
  workPhone?: string;
  cellPhone?: string;
  assistantName?: string;
  assistantPhone?: string;
  assistantEmail?: string;
  address?: string;
  website?: string;
  linkedInProfile?: string;
  numberOfEmployees?: string;
  annualRevenue?: string;
  industry?: string;
  country?: string;
  businessType?: string;
  totalLocations?: string;
  rfpDate?: string;
  rfpExpirationDate?: string;
  securedLocations?: string;
  addedOnLinkedIn?: string;
  kkPlan?: string;
  goal?: string;
  found?: string;
  challenges?: string;
  timing?: string;
  dealScore?: string;
  dealSource?: string;
  eyeballs?: string;
  decisionMaker?: boolean;
  notes?: LeadNote[];
}

interface LeadNote {
  id: string;
  content: string;
  timestamp: string;
  author: string;
  pinned?: boolean;
  pinnedAt?: string;
}

interface ScheduledMeetingActivity {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  leadId?: string;
  meetingType?: 'in-person' | 'video' | 'phone';
  tags?: string[];
}

interface ActivityPurposeOption {
  id: string;
  name: string;
  color?: string;
}

interface DetailedActivityFormData {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  location: string;
  meetingType: 'in-person' | 'video' | 'phone';
  meetingUrl: string;
  clientId: string;
  contactId: string;
  dealId: string;
  leadId: string;
  purposeTypeId: string;
  notes: string;
  priority: 'low' | 'normal' | 'high';
  freeBusy: 'free' | 'busy';
  markAsDone: boolean;
  videoCallProvider: '' | 'zoom' | 'teams' | 'google-meet';
}

const DEFAULT_ACTIVITY_PURPOSE_OPTIONS: ActivityPurposeOption[] = [
  { id: 'internal-sales-meeting', name: 'Internal Sales Meeting', color: '#ff8c42' },
  { id: 'internal-developer-meetings', name: 'Internal Developer Meetings', color: '#6f42c1' },
  { id: 'client-sales-meetings', name: 'Client Sales Meetings', color: '#0d6efd' },
];

const DEFAULT_ACTIVITY_PURPOSE_ID = 'client-sales-meetings';
const ACTIVITY_CALENDAR_VISIBLE_HOURS = 12;
const ACTIVITY_CALENDAR_SLOT_HEIGHT = 30;
const ACTIVITY_CALENDAR_PANEL_WIDTH = 320;
const DEFAULT_OWNER_FILTER_LABEL = 'Everyone';
const DEFAULT_SELF_OWNER_LABEL = 'PhiLL Kross';
const LEAD_OWNER_FAVORITES_STORAGE_KEY = 'leadOwnerFavorites';
const LEAD_FILTER_FAVORITES_STORAGE_KEY = 'leadFilterFavorites';

type OwnerFilterPanelTab = 'favorites' | 'owners' | 'filters';

interface LeadVisibilityFilterOption {
  id: string;
  label: string;
  description: string;
}

type LeadFilterConditionScope = 'activity' | 'lead' | 'organization' | 'owner';

interface LeadFilterConditionRow {
  id: string;
  scope: LeadFilterConditionScope;
  field: string;
}

interface LeadSavedFilterCondition {
  scope: LeadFilterConditionScope;
  field: string;
}

interface LeadSavedVisibilityFilter extends LeadVisibilityFilterOption {
  visibility: 'private' | 'shared';
  saveColumns: boolean;
  allConditions: LeadSavedFilterCondition[];
  anyConditions: LeadSavedFilterCondition[];
  createdAt: string;
}

const LEAD_FILTER_SCOPE_OPTIONS: Array<{ value: LeadFilterConditionScope; label: string }> = [
  { value: 'activity', label: 'Activity' },
  { value: 'lead', label: 'Lead' },
  { value: 'organization', label: 'Organization' },
  { value: 'owner', label: 'Owner' },
];

const LEAD_FILTER_FIELD_OPTIONS_BY_SCOPE: Record<LeadFilterConditionScope, Array<{ value: string; label: string }>> = {
  activity: [
    { value: 'no-next-activity', label: 'No next activity' },
    { value: 'has-next-activity', label: 'Has next activity' },
    { value: 'overdue-next-activity', label: 'Next activity is overdue' },
  ],
  lead: [
    { value: 'has-labels', label: 'Has labels' },
    { value: 'missing-contact-stage', label: 'Missing contact stage' },
    { value: 'missing-organization', label: 'Missing organization' },
  ],
  organization: [
    { value: 'has-organization', label: 'Organization is set' },
    { value: 'missing-organization', label: 'Organization is empty' },
  ],
  owner: [
    { value: 'owner-is-me', label: 'Owner is me' },
    { value: 'owner-is-unassigned', label: 'Owner is unassigned' },
  ],
};

const LEAD_CUSTOM_FILTERS_STORAGE_KEY = 'leadCustomFilters';

const LEAD_FILTER_CONDITION_SCOPES: LeadFilterConditionScope[] = ['activity', 'lead', 'organization', 'owner'];

const isLeadFilterConditionScope = (value: string): value is LeadFilterConditionScope => {
  return LEAD_FILTER_CONDITION_SCOPES.includes(value as LeadFilterConditionScope);
};

const getLeadFilterFieldLabel = (scope: LeadFilterConditionScope, field: string): string => {
  const option = (LEAD_FILTER_FIELD_OPTIONS_BY_SCOPE[scope] || []).find((entry) => entry.value === field);
  return option?.label || field;
};

const normalizeLeadSavedFilterCondition = (candidate: any): LeadSavedFilterCondition | null => {
  if (!candidate || typeof candidate !== 'object') return null;
  const scopeRaw = String(candidate.scope || '').trim();
  const field = String(candidate.field || '').trim();
  if (!isLeadFilterConditionScope(scopeRaw) || !field) return null;
  return {
    scope: scopeRaw,
    field,
  };
};

const buildLeadSavedFilterDescription = (
  allConditions: LeadSavedFilterCondition[],
  anyConditions: LeadSavedFilterCondition[]
): string => {
  const allLabels = allConditions.map((condition) => getLeadFilterFieldLabel(condition.scope, condition.field));
  const anyLabels = anyConditions.map((condition) => getLeadFilterFieldLabel(condition.scope, condition.field));

  if (allLabels.length === 1 && anyLabels.length === 0) return allLabels[0];
  if (allLabels.length === 2 && anyLabels.length === 0) return `${allLabels[0]} and ${allLabels[1]}`;
  if (allLabels.length === 0 && anyLabels.length === 1) return anyLabels[0];

  const allSummary = `${allLabels.length} all-condition${allLabels.length === 1 ? '' : 's'}`;
  const anySummary = `${anyLabels.length} any-condition${anyLabels.length === 1 ? '' : 's'}`;
  if (allLabels.length > 0 && anyLabels.length > 0) return `${allSummary}, ${anySummary}`;
  if (allLabels.length > 0) return allSummary;
  if (anyLabels.length > 0) return anySummary;
  return 'Custom filter';
};

const readLeadSavedFiltersFromStorage = (): LeadSavedVisibilityFilter[] => {
  try {
    const stored = localStorage.getItem(LEAD_CUSTOM_FILTERS_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];

    return parsed.flatMap((entry: any) => {
      if (!entry || typeof entry !== 'object') return [];
      const id = String(entry.id || '').trim();
      const label = String(entry.label || '').trim();
      if (!id || !label) return [];
      const allConditionsRaw = Array.isArray(entry.allConditions) ? entry.allConditions : [];
      const anyConditionsRaw = Array.isArray(entry.anyConditions) ? entry.anyConditions : [];
      const allConditions = allConditionsRaw
        .map((condition: any) => normalizeLeadSavedFilterCondition(condition))
        .filter((condition: LeadSavedFilterCondition | null): condition is LeadSavedFilterCondition => Boolean(condition));
      const anyConditions = anyConditionsRaw
        .map((condition: any) => normalizeLeadSavedFilterCondition(condition))
        .filter((condition: LeadSavedFilterCondition | null): condition is LeadSavedFilterCondition => Boolean(condition));
      const descriptionCandidate = String(entry.description || '').trim();
      const description = descriptionCandidate || buildLeadSavedFilterDescription(allConditions, anyConditions);
      const visibility = String(entry.visibility || '').toLowerCase() === 'shared' ? 'shared' : 'private';
      const saveColumns = Boolean(entry.saveColumns);
      const createdAtCandidate = String(entry.createdAt || '').trim();
      const createdAt = createdAtCandidate || new Date().toISOString();

      return [{
        id,
        label,
        description,
        visibility,
        saveColumns,
        allConditions,
        anyConditions,
        createdAt,
      }];
    });
  } catch {
    return [];
  }
};

const isLeadNextActivityOverdue = (lead: Lead): boolean => {
  if (!lead.nextActivity || lead.nextActivity.type === 'none') return false;
  const rawDate = String(lead.nextActivity.date || '').trim();
  if (!rawDate) return false;
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.getTime() < Date.now();
};

const doesLeadMatchConditionField = (
  lead: Lead,
  field: string,
  selfOwnerBaseLabel: string = DEFAULT_SELF_OWNER_LABEL
): boolean => {
  switch (field) {
    case 'no-next-activity':
      return !lead.nextActivity || lead.nextActivity.type === 'none';
    case 'has-next-activity':
      return Boolean(lead.nextActivity) && lead.nextActivity?.type !== 'none';
    case 'overdue-next-activity':
      return isLeadNextActivityOverdue(lead);
    case 'with-labels':
    case 'has-labels':
      return Array.isArray(lead.labels) && lead.labels.length > 0;
    case 'missing-contact-stage':
      return String(lead.contactStage || '').trim().length === 0;
    case 'missing-organization':
      return String(lead.organization || '').trim().length === 0;
    case 'has-organization':
      return String(lead.organization || '').trim().length > 0;
    case 'owner-is-me':
      return normalizeOwnerKey(String(lead.owner || '').trim()) === normalizeOwnerKey(selfOwnerBaseLabel);
    case 'owner-is-unassigned':
      return String(lead.owner || '').trim().length === 0;
    default:
      return false;
  }
};

const doesLeadMatchSavedVisibilityFilter = (
  lead: Lead,
  filter: LeadSavedVisibilityFilter,
  selfOwnerBaseLabel: string = DEFAULT_SELF_OWNER_LABEL
): boolean => {
  const allMatch = filter.allConditions.every((condition) => (
    doesLeadMatchConditionField(lead, condition.field, selfOwnerBaseLabel)
  ));
  if (!allMatch) return false;
  if (filter.anyConditions.length === 0) return true;
  return filter.anyConditions.some((condition) => (
    doesLeadMatchConditionField(lead, condition.field, selfOwnerBaseLabel)
  ));
};

const createLeadFilterConditionRow = (scope: LeadFilterConditionScope = 'activity'): LeadFilterConditionRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  scope,
  field: '',
});

const LEAD_VISIBILITY_FILTER_OPTIONS: LeadVisibilityFilterOption[] = [
  {
    id: 'all',
    label: 'All leads',
    description: 'No additional filtering',
  },
  {
    id: 'no-next-activity',
    label: 'Leads with no next activity',
    description: 'Shows leads that need a follow-up scheduled',
  },
  {
    id: 'has-next-activity',
    label: 'Leads with scheduled activity',
    description: 'Shows leads that already have a next step',
  },
  {
    id: 'with-labels',
    label: 'Leads that have labels',
    description: 'Shows leads that are tagged',
  },
  {
    id: 'missing-contact-stage',
    label: 'Leads missing contact stage',
    description: 'Shows leads without a contact stage value',
  },
  {
    id: 'missing-organization',
    label: 'Leads missing organization',
    description: 'Shows leads where organization is empty',
  },
];

const LEAD_SYSTEM_VISIBILITY_FILTER_IDS = new Set(
  LEAD_VISIBILITY_FILTER_OPTIONS.map((option) => option.id)
);

const isSystemVisibilityFilterId = (filterId: string): boolean =>
  LEAD_SYSTEM_VISIBILITY_FILTER_IDS.has(String(filterId || '').trim());

const normalizeOwnerKey = (value: string): string =>
  String(value || '')
    .replace(/\(\s*you\s*\)/gi, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const readStringArrayFromStorage = (storageKey: string): string[] => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => String(entry || '').trim())
      .filter((entry) => entry.length > 0);
  } catch {
    return [];
  }
};

const doesLeadMatchVisibilityFilter = (
  lead: Lead,
  filterId: string,
  selfOwnerBaseLabel: string = DEFAULT_SELF_OWNER_LABEL
): boolean => {
  if (filterId === 'all') return true;
  return doesLeadMatchConditionField(lead, filterId, selfOwnerBaseLabel);
};

const formatActivityCalendarHourLabel = (hour: number): string => {
  const normalizedHour = ((hour % 24) + 24) % 24;
  const meridiem = normalizedHour >= 12 ? 'PM' : 'AM';
  const twelveHour = normalizedHour % 12 === 0 ? 12 : normalizedHour % 12;
  return `${twelveHour} ${meridiem}`;
};

// Mock data - replace with actual API call later
const mockLeads: Lead[] = [
  {
    id: '1',
    name: 'Sunny Gandhi',
    title: 'Area Director',
    organization: 'GA Restaurants SA',
    labels: ['HOT', '1-10 LOCATIONS', 'FRANCHISE'],
    nextActivity: { type: 'calendar', date: 'September 25' },
    leadCreated: 'Sep 9, 2025 at 2:05 PM'
  },
  {
    id: '2',
    name: 'Steven Sosunov',
    title: 'Steven Sosunov',
    organization: 'Douglas INFINITI',
    labels: ['COLD'],
    nextActivity: { type: 'none' },
    leadCreated: 'Sep 9, 2025 at 2:27 PM'
  },
  {
    id: '3',
    name: 'Eduardo Caceres',
    title: 'Eduardo Caceres',
    organization: 'Golden Chick',
    labels: ['WARM'],
    nextActivity: { type: 'calendar', date: 'September 24' },
    leadCreated: 'Sep 9, 2025 at 4:11 PM'
  },
  {
    id: '4',
    name: 'Tricia Nichols',
    title: 'Tricia Nichols',
    organization: 'Independent Pet Partners (IPP)',
    labels: ['QUARTER 2', 'COLD'],
    nextActivity: { type: 'none' },
    leadCreated: 'May 3, 2023 at 1:40 AM'
  },
  {
    id: '5',
    name: 'Gordon Smith',
    title: 'Gordon Smith',
    organization: 'Cato Corporation',
    labels: ['QUARTER 2', 'COLD'],
    nextActivity: { type: 'calendar', date: 'August 9, 2021' },
    leadCreated: 'May 3, 2023 at 1:26 AM'
  }
];

// Load label colors from localStorage or use defaults
const getLabelColors = (): Record<string, string> => {
  const defaultColors: Record<string, string> = {
    'HOT': 'danger',
    'COLD': 'primary',
    'WARM': 'warning',
    'QUARTER 2': 'secondary',
    'QUARTER 1': 'info',
    'QUARTER 3': 'info',
    'QUARTER 4': 'warning',
    'NTTM': 'info',
    'FRANCHISE': 'success',
    '1-10 LOCATIONS': 'warning',
    'CHAINS': 'success',
    'I.P.M.': 'secondary'
  };
  
  try {
    const stored = localStorage.getItem('labelColors');
    if (stored) {
      const storedColors = JSON.parse(stored);
      return { ...defaultColors, ...storedColors };
    }
  } catch {
    // If parsing fails, return defaults
  }
  
  return defaultColors;
};

const HIDDEN_LEAD_LABELS_STORAGE_KEY = 'hiddenLeadLabels';

const DEFAULT_SOURCE_CHANNEL_OPTIONS = [
  'Prospector',
  'Lead Suggestions',
  'Web forms',
  'Chatbot',
  'Live chat',
  'Web visitors',
  'Campaigns',
  'Marketplace',
  'Messaging Inbox',
];

const DEFAULT_BUSINESS_UNIT_OPTIONS = [
  'In-Store',
];

const normalizeSourceChannelOption = (value: string): string => value.trim().replace(/\s+/g, ' ');

const dedupeSourceChannelOptions = (values: string[]): string[] => {
  const seen = new Set<string>();
  const deduped: string[] = [];

  values.forEach((value) => {
    const normalized = normalizeSourceChannelOption(value);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(normalized);
  });

  return deduped;
};

const normalizeBusinessUnitOption = (value: string): string => value.trim().replace(/\s+/g, ' ');

const dedupeBusinessUnitOptions = (values: string[]): string[] => {
  const seen = new Set<string>();
  const deduped: string[] = [];

  values.forEach((value) => {
    const normalized = normalizeBusinessUnitOption(value);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(normalized);
  });

  return deduped;
};

const detailFieldIconStyle: React.CSSProperties = {
  width: '16px',
  minWidth: '16px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexShrink: 0,
};

const IconOnlyDropdownToggle = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ children, onClick, className, style, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    className={`btn btn-outline-secondary btn-sm ${className || ''}`.trim()}
    onClick={(event) => {
      event.preventDefault();
      onClick?.(event);
    }}
    style={{
      width: '32px',
      height: '32px',
      padding: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderColor: '#ced4da',
      ...style
    }}
    {...rest}
  >
    {children}
  </button>
));
IconOnlyDropdownToggle.displayName = 'IconOnlyDropdownToggle';

interface LeadImportRow {
  rowNumber: number;
  values: Record<string, string>;
}

interface LeadImportDuplicateInsight {
  isDuplicate: boolean;
  reasons: string[];
}

interface LeadImportFailedRow {
  rowNumber: number;
  reason: string;
  values: Record<string, string>;
}

const LEAD_IMPORT_HEADER_ALIASES: Record<string, string> = {
  name: 'name',
  contactperson: 'name',
  contact: 'name',
  fullname: 'name',
  person: 'name',
  title: 'title',
  jobtitle: 'title',
  organization: 'organization',
  company: 'organization',
  accountname: 'organization',
  org: 'organization',
  accountstage: 'accountStage',
  contactstage: 'contactStage',
  labels: 'labels',
  tags: 'labels',
  value: 'value',
  amount: 'value',
  currency: 'currency',
  expectedclosedate: 'expectedCloseDate',
  closedate: 'expectedCloseDate',
  sourcechannel: 'sourceChannel',
  source: 'sourceChannel',
  leadsource: 'sourceChannel',
  sourcechannelid: 'sourceChannelId',
  businessunit: 'businessUnit',
  email: 'email',
  emailaddress: 'email',
  phone: 'phone',
  phonenumber: 'phone',
  mobile: 'phone',
  phonetype: 'phoneType',
  emailtype: 'emailType',
  status: 'status',
  visibility: 'visibility',
};

const LEAD_IMPORT_TEMPLATE_HEADERS = [
  'name',
  'title',
  'organization',
  'accountStage',
  'contactStage',
  'labels',
  'value',
  'currency',
  'expectedCloseDate',
  'sourceChannel',
  'sourceChannelId',
  'businessUnit',
  'email',
  'phone',
  'phoneType',
  'emailType',
  'status',
  'visibility',
];

const LEAD_IMPORT_STEP_LABELS = ['File info', 'Upload file', 'Map', 'Preview', 'Finish'];

const LEAD_IMPORT_TARGET_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'ignore', label: "Don't import" },
  { key: 'name', label: 'Lead name' },
  { key: 'title', label: 'Lead title' },
  { key: 'organization', label: 'Organization name' },
  { key: 'accountStage', label: 'Account stage' },
  { key: 'contactStage', label: 'Contact stage' },
  { key: 'labels', label: 'Labels' },
  { key: 'value', label: 'Value' },
  { key: 'currency', label: 'Currency' },
  { key: 'expectedCloseDate', label: 'Expected close date' },
  { key: 'sourceChannel', label: 'Source channel' },
  { key: 'sourceChannelId', label: 'Source channel ID' },
  { key: 'businessUnit', label: 'Business unit' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'phoneType', label: 'Phone type' },
  { key: 'emailType', label: 'Email type' },
  { key: 'status', label: 'Status' },
  { key: 'visibility', label: 'Visibility' },
];

const getLeadImportTargetFieldLabel = (fieldKey: string): string => {
  return LEAD_IMPORT_TARGET_FIELDS.find((option) => option.key === fieldKey)?.label || fieldKey;
};

const normalizeLeadImportHeader = (header: string): string => {
  return String(header || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
};

const resolveLeadImportHeader = (header: string): string | null => {
  const normalizedHeader = normalizeLeadImportHeader(header);
  if (!normalizedHeader) return null;
  return LEAD_IMPORT_HEADER_ALIASES[normalizedHeader] || null;
};

const normalizeLeadImportText = (value: string): string => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
};

const normalizeLeadImportPhone = (value: string): string => {
  const digits = String(value || '').replace(/\D+/g, '');
  return digits.length >= 7 ? digits : '';
};

const parseCsvTable = (csvText: string): string[][] => {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = '';
  let insideQuotes = false;

  for (let index = 0; index < csvText.length; index += 1) {
    const currentChar = csvText[index];

    if (currentChar === '"') {
      const nextChar = csvText[index + 1];
      if (insideQuotes && nextChar === '"') {
        currentCell += '"';
        index += 1;
      } else {
        insideQuotes = !insideQuotes;
      }
      continue;
    }

    if (!insideQuotes && currentChar === ',') {
      currentRow.push(currentCell);
      currentCell = '';
      continue;
    }

    if (!insideQuotes && (currentChar === '\n' || currentChar === '\r')) {
      if (currentChar === '\r' && csvText[index + 1] === '\n') {
        index += 1;
      }
      currentRow.push(currentCell);
      rows.push(currentRow);
      currentRow = [];
      currentCell = '';
      continue;
    }

    currentCell += currentChar;
  }

  if (currentCell.length > 0 || currentRow.length > 0) {
    currentRow.push(currentCell);
    rows.push(currentRow);
  }

  return rows;
};

const buildLeadImportSourceFromTable = (
  table: string[][],
  fileLabel: string
): { headers: string[]; rows: string[][]; errors: string[] } => {
  const errors: string[] = [];

  if (table.length === 0) {
    return { headers: [], rows: [], errors: [`The ${fileLabel} file is empty.`] };
  }

  const [headerRowRaw, ...rawRows] = table;
  const headers = headerRowRaw.map((header, index) => {
    const normalized = String(header || '').trim();
    if (normalized) return normalized;
    return `Column ${index + 1}`;
  });
  if (headers.length === 0) {
    return { headers: [], rows: [], errors: [`No header row was found in this ${fileLabel} file.`] };
  }

  const rows = rawRows.filter((rawRow) => rawRow.some((value) => String(value || '').trim().length > 0));
  if (rows.length === 0) {
    errors.push(`No data rows were found in this ${fileLabel} file.`);
  }

  return { headers, rows, errors };
};

const parseLeadImportSourceCsv = (
  csvText: string
): { headers: string[]; rows: string[][]; errors: string[] } => {
  const table = parseCsvTable(csvText);
  return buildLeadImportSourceFromTable(table, 'CSV');
};

const parseLeadImportSourceXlsx = async (
  file: File
): Promise<{ headers: string[]; rows: string[][]; errors: string[] }> => {
  const xlsxModule = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = xlsxModule.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    return { headers: [], rows: [], errors: ['No worksheet was found in this XLSX file.'] };
  }

  const firstSheet = workbook.Sheets[firstSheetName];
  const rawTable = xlsxModule.utils.sheet_to_json(firstSheet, {
    header: 1,
    raw: false,
    defval: '',
    blankrows: false,
  }) as Array<Array<string | number | boolean | null | undefined>>;

  const table = rawTable.map((row) => row.map((cell) => String(cell ?? '')));
  return buildLeadImportSourceFromTable(table, 'XLSX');
};

const buildLeadImportRowsFromMapping = (
  headers: string[],
  sourceRows: string[][],
  mapping: Record<string, string>
): LeadImportRow[] => {
  return sourceRows.flatMap((sourceRow, rowIndex) => {
    const values: Record<string, string> = {};

    headers.forEach((header, headerIndex) => {
      const targetKey = mapping[header] || 'ignore';
      if (!targetKey || targetKey === 'ignore') return;
      const value = String(sourceRow[headerIndex] ?? '').trim();
      if (!value) return;
      if (targetKey === 'labels' && values[targetKey]) {
        values[targetKey] = `${values[targetKey]}|${value}`;
        return;
      }
      values[targetKey] = value;
    });

    if (Object.keys(values).length === 0) return [];
    return [
      {
        rowNumber: rowIndex + 2,
        values,
      },
    ];
  });
};

const escapeCsvValue = (value: string | number | null | undefined): string => {
  const normalized = String(value ?? '');
  const escaped = normalized.replace(/"/g, '""');
  return `"${escaped}"`;
};

const LEAD_EXPORT_COLUMNS: Array<{ header: string; getValue: (lead: Lead) => string }> = [
  { header: 'name', getValue: (lead) => String(lead.name || '') },
  { header: 'title', getValue: (lead) => String(lead.title || '') },
  { header: 'organization', getValue: (lead) => String(lead.organization || '') },
  { header: 'accountStage', getValue: (lead) => String(lead.accountStage || '') },
  { header: 'contactStage', getValue: (lead) => String(lead.contactStage || '') },
  { header: 'labels', getValue: (lead) => (Array.isArray(lead.labels) ? lead.labels.join('|') : '') },
  { header: 'value', getValue: (lead) => String(lead.value || '') },
  { header: 'currency', getValue: (lead) => String(lead.currency || '') },
  { header: 'expectedCloseDate', getValue: (lead) => String(lead.expectedCloseDate || '') },
  { header: 'sourceChannel', getValue: (lead) => String(lead.sourceChannel || '') },
  { header: 'sourceChannelId', getValue: (lead) => String(lead.sourceChannelId || '') },
  { header: 'businessUnit', getValue: (lead) => String(lead.businessUnit || '') },
  { header: 'email', getValue: (lead) => String(lead.email || '') },
  { header: 'phone', getValue: (lead) => String(lead.phone || '') },
  { header: 'status', getValue: (lead) => String(lead.status || '') },
  { header: 'owner', getValue: (lead) => String(lead.owner || '') },
  { header: 'leadCreated', getValue: (lead) => String(lead.leadCreated || '') },
];

type ScheduleActivityType =
  | 'call'
  | 'text'
  | 'meeting'
  | 'task'
  | 'deadline'
  | 'email'
  | 'lunch';

type QuickSchedulePreset = 'in1h' | 'in3h' | 'tomorrow' | 'nextWeek' | 'other';

const SCHEDULE_ACTIVITY_LABELS: Record<ScheduleActivityType, string> = {
  call: 'Call',
  text: 'Text',
  meeting: 'Meeting',
  task: 'Task',
  deadline: 'Deadline',
  email: 'Email',
  lunch: 'Lunch',
};

const SCHEDULE_ACTIVITY_OPTIONS: Array<{ type: ScheduleActivityType; label: string }> = [
  { type: 'call', label: 'Call' },
  { type: 'text', label: 'Text' },
  { type: 'meeting', label: 'Meeting' },
  { type: 'task', label: 'Task' },
  { type: 'deadline', label: 'Deadline' },
  { type: 'email', label: 'Email' },
  { type: 'lunch', label: 'Lunch' },
];

const Leads: React.FC = () => {
  const { currentUser } = useUser();
  // API hooks
  const { data: leadsData, isLoading: isLoadingLeads, error: leadsError, refetch: refetchLeads } = useGetLeadsQuery({});
  const { data: meetingsData, refetch: refetchMeetings } = useGetMeetingsQuery({});
  const { data: rulesEngineConfigData } = useGetRulesEngineConfigQuery(undefined);
  const [createLead, { isLoading: isCreatingLead }] = useCreateLeadMutation();
  const [createContact] = useCreateContactMutation();
  const [createClient] = useCreateClientMutation();
  const [createDeal] = useCreateDealMutation();
  const [createMeeting, { isLoading: isCreatingMeeting }] = useCreateMeetingMutation();
  const { data: accountsData, refetch: refetchAccounts } = useGetAccountsQuery({});
  const [createAccount] = useCreateAccountMutation();
  const [updateAccount] = useUpdateAccountMutation();
  const [updateLead, { isLoading: isUpdatingLead }] = useUpdateLeadMutation();
  const [deleteLead, { isLoading: isDeletingLead }] = useDeleteLeadMutation();
  const tenantIdForPrefs = useTenantIdForPreferences();
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const timezoneOption = tenantTimezone ? { timeZone: tenantTimezone } : {};
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();

  const selfOwnerBaseLabel = useMemo(() => {
    const explicitName = String(currentUser?.name || '').trim();
    const combinedName = `${String(currentUser?.firstName || '').trim()} ${String(currentUser?.lastName || '').trim()}`.trim();
    return explicitName || combinedName || DEFAULT_SELF_OWNER_LABEL;
  }, [currentUser?.firstName, currentUser?.lastName, currentUser?.name]);
  const selfOwnerFilterLabel = useMemo(
    () => `${selfOwnerBaseLabel} (you)`,
    [selfOwnerBaseLabel]
  );

  // Extract leads from API response
  const leads = leadsData || [];
  const accounts = accountsData || [];
  const accountNameMap = useMemo(() => {
    const map = new Map<string, any>();
    (accounts || []).forEach((account: any) => {
      const name = String(account?.name || '').trim();
      if (!name) return;
      map.set(name.toLowerCase(), account);
    });
    return map;
  }, [accounts]);
  const meetings = meetingsData || [];
  const rulesEngineConfig = useMemo(
    () => normalizeRulesEngineConfig(rulesEngineConfigData || DEFAULT_RULES_ENGINE_CONFIG),
    [rulesEngineConfigData]
  );
  const accountStageOptions = rulesEngineConfig.accountStages;
  const contactStageOptions = rulesEngineConfig.contactStages;
  
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isConvertingLead, setIsConvertingLead] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('notes');
  const leadEmailAddress = String(selectedLead?.email || '').trim().toLowerCase();
  const shouldLoadLeadEmailThreads = showSidebar && activeTab === 'email' && leadEmailAddress.length > 0;
  const {
    data: leadInboxEmailThreadsData,
    isLoading: isLoadingLeadInboxEmailThreads,
    isFetching: isRefreshingLeadInboxEmailThreads,
    error: leadInboxEmailThreadsError,
    refetch: refetchLeadInboxEmailThreads
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'inbox',
      limit: 200,
    },
    {
      skip: !shouldLoadLeadEmailThreads,
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: leadSentEmailThreadsData,
    isLoading: isLoadingLeadSentEmailThreads,
    isFetching: isRefreshingLeadSentEmailThreads,
    error: leadSentEmailThreadsError,
    refetch: refetchLeadSentEmailThreads
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'sent',
      limit: 200,
    },
    {
      skip: !shouldLoadLeadEmailThreads,
      refetchOnMountOrArgChange: true,
    }
  );
  const leadEmailThreadsData = useMemo(
    () => mergeEmailResponses(leadInboxEmailThreadsData, leadSentEmailThreadsData),
    [leadInboxEmailThreadsData, leadSentEmailThreadsData]
  );
  const isLoadingLeadEmailThreads = isLoadingLeadInboxEmailThreads || isLoadingLeadSentEmailThreads;
  const isRefreshingLeadEmailThreads = isRefreshingLeadInboxEmailThreads || isRefreshingLeadSentEmailThreads;
  const leadEmailThreadsError = leadInboxEmailThreadsError && leadSentEmailThreadsError ? leadInboxEmailThreadsError : null;
  const refetchLeadEmailThreads = (): void => {
    refetchLeadInboxEmailThreads();
    refetchLeadSentEmailThreads();
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showLeadImportModal, setShowLeadImportModal] = useState(false);
  const [leadImportStep, setLeadImportStep] = useState<number>(1);
  const [leadImportSourceHeaders, setLeadImportSourceHeaders] = useState<string[]>([]);
  const [leadImportSourceRows, setLeadImportSourceRows] = useState<string[][]>([]);
  const [leadImportColumnMapping, setLeadImportColumnMapping] = useState<Record<string, string>>({});
  const [leadImportFileName, setLeadImportFileName] = useState('');
  const [leadImportParseErrors, setLeadImportParseErrors] = useState<string[]>([]);
  const [leadImportRowErrors, setLeadImportRowErrors] = useState<string[]>([]);
  const [leadImportFailedRows, setLeadImportFailedRows] = useState<LeadImportFailedRow[]>([]);
  const [leadImportSummary, setLeadImportSummary] = useState<{
    total: number;
    attempted: number;
    imported: number;
    failed: number;
    skippedDuplicates: number;
  } | null>(null);
  const [leadImportDuplicatePolicy, setLeadImportDuplicatePolicy] = useState<'skip' | 'import-all'>('skip');
  const [isImportingLeads, setIsImportingLeads] = useState(false);
  const [isLeadImportDragOver, setIsLeadImportDragOver] = useState(false);
  const leadImportFileInputRef = useRef<HTMLInputElement | null>(null);
  const [showCustomizeColumnsModal, setShowCustomizeColumnsModal] = useState(false);
  const [showLabelsFilter, setShowLabelsFilter] = useState(false);
  const [showOwnerFilter, setShowOwnerFilter] = useState(false);
  const [ownerFilterTab, setOwnerFilterTab] = useState<OwnerFilterPanelTab>('owners');
  const [ownerFilterSearchQuery, setOwnerFilterSearchQuery] = useState('');
  const [hoveredOwnerRowKey, setHoveredOwnerRowKey] = useState<string | null>(null);
  const [isHoveringAddFilterButton, setIsHoveringAddFilterButton] = useState(false);
  const [showCreateFilterModal, setShowCreateFilterModal] = useState(false);
  const [createFilterEditingId, setCreateFilterEditingId] = useState<string | null>(null);
  const [showCreateFilterArchiveHint, setShowCreateFilterArchiveHint] = useState(true);
  const [createFilterAllConditions, setCreateFilterAllConditions] = useState<LeadFilterConditionRow[]>([
    createLeadFilterConditionRow('activity'),
  ]);
  const [createFilterAnyConditions, setCreateFilterAnyConditions] = useState<LeadFilterConditionRow[]>([]);
  const [createFilterName, setCreateFilterName] = useState('Activity');
  const [createFilterVisibility, setCreateFilterVisibility] = useState<'private' | 'shared'>('private');
  const [createFilterSaveColumns, setCreateFilterSaveColumns] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>('All labels');
  const [selectedOwner, setSelectedOwner] = useState<string>(DEFAULT_OWNER_FILTER_LABEL);
  const [contractRenewalFilter, setContractRenewalFilter] = useState<'all' | 'overdue' | 'next30' | 'next60' | 'next90'>('all');
  const [selectedVisibilityFilterId, setSelectedVisibilityFilterId] = useState<string>('all');
  const [savedVisibilityFilters, setSavedVisibilityFilters] = useState<LeadSavedVisibilityFilter[]>(
    () => readLeadSavedFiltersFromStorage()
  );
  const [favoriteOwnerKeys, setFavoriteOwnerKeys] = useState<string[]>(() => {
    const persisted = readStringArrayFromStorage(LEAD_OWNER_FAVORITES_STORAGE_KEY)
      .map((entry) => normalizeOwnerKey(entry))
      .filter(Boolean);
    return persisted.length > 0 ? persisted : [normalizeOwnerKey(selfOwnerFilterLabel)];
  });
  const [favoriteVisibilityFilterIds, setFavoriteVisibilityFilterIds] = useState<string[]>(() => {
    return readStringArrayFromStorage(LEAD_FILTER_FAVORITES_STORAGE_KEY);
  });
  const [showCreateLabelModal, setShowCreateLabelModal] = useState(false);
  const [showEditLabelModal, setShowEditLabelModal] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);
  const [labelSearchQuery, setLabelSearchQuery] = useState('');
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('secondary');
  const [editingLabelName, setEditingLabelName] = useState('');
  const [editingLabelOriginalName, setEditingLabelOriginalName] = useState('');
  const [editingLabelColor, setEditingLabelColor] = useState('secondary');
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const [customLabels, setCustomLabels] = useState<string[]>(() => {
    // Load custom labels from localStorage on mount
    try {
      const stored = localStorage.getItem('customLabels');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [hiddenLabels, setHiddenLabels] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(HIDDEN_LEAD_LABELS_STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      return Array.from(new Set(parsed.map((entry) => normalizeLabelKey(entry)).filter(Boolean)));
    } catch {
      return [];
    }
  });
  const [sourceChannelOptions, setSourceChannelOptions] = useState<string[]>([...DEFAULT_SOURCE_CHANNEL_OPTIONS]);
  const [showSourceChannelDropdown, setShowSourceChannelDropdown] = useState(false);
  const [sourceChannelSearchQuery, setSourceChannelSearchQuery] = useState('');
  const [showCreateSourceChannel, setShowCreateSourceChannel] = useState(false);
  const [newSourceChannelOption, setNewSourceChannelOption] = useState('');
  const [editingSourceChannelOption, setEditingSourceChannelOption] = useState<string | null>(null);
  const [editingSourceChannelOptionDraft, setEditingSourceChannelOptionDraft] = useState('');
  const [hoveredSourceChannelOption, setHoveredSourceChannelOption] = useState<string | null>(null);
  const [businessUnitOptions, setBusinessUnitOptions] = useState<string[]>([...DEFAULT_BUSINESS_UNIT_OPTIONS]);
  const [showBusinessUnitDropdown, setShowBusinessUnitDropdown] = useState(false);
  const [businessUnitSearchQuery, setBusinessUnitSearchQuery] = useState('');
  const [showCreateBusinessUnit, setShowCreateBusinessUnit] = useState(false);
  const [newBusinessUnitOption, setNewBusinessUnitOption] = useState('');
  const [editingBusinessUnitOption, setEditingBusinessUnitOption] = useState<string | null>(null);
  const [editingBusinessUnitOptionDraft, setEditingBusinessUnitOptionDraft] = useState('');
  const [hoveredBusinessUnitOption, setHoveredBusinessUnitOption] = useState<string | null>(null);
  const [showAllFields, setShowAllFields] = useState(false);
  const [showAllPersonFields, setShowAllPersonFields] = useState(false);
  const [showAllOrganizationFields, setShowAllOrganizationFields] = useState(false);
  const [leadMailboxView, setLeadMailboxView] = useState<'inbox' | 'archive'>('inbox');
  const [addedFields, setAddedFields] = useState<Set<string>>(new Set(['addToSequence', 'labels', 'value', 'expectedCloseDate', 'businessUnit', 'owner', 'sourceOrigin', 'sourceChannel', 'sourceChannelId']));
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isHoveringName, setIsHoveringName] = useState(false);
  const [isEditingValueDetails, setIsEditingValueDetails] = useState(false);
  const [valueDetails, setValueDetails] = useState('');
  const [currencyDetails, setCurrencyDetails] = useState('US Dollar (USD)');
  const [isHoveringValueDetails, setIsHoveringValueDetails] = useState(false);
  const [isHoveringExpectedCloseDate, setIsHoveringExpectedCloseDate] = useState(false);
  const [isEditingExpectedCloseDate, setIsEditingExpectedCloseDate] = useState(false);
  const [expectedCloseDateDraft, setExpectedCloseDateDraft] = useState('');
  const [editingDetailFieldId, setEditingDetailFieldId] = useState<string | null>(null);
  const [detailFieldDraftValue, setDetailFieldDraftValue] = useState('');
  const [detailFieldDraftChecked, setDetailFieldDraftChecked] = useState(false);
  const [isHoveringLabels, setIsHoveringLabels] = useState(false);
  
  // Notes state
  const [notes, setNotes] = useState<LeadNote[]>([]);
  const [currentNoteContent, setCurrentNoteContent] = useState('');
  const [isNoteEditorFocused, setIsNoteEditorFocused] = useState(false);
  const [noteEditorRef, setNoteEditorRef] = useState<HTMLDivElement | null>(null);
  const [scheduledActivityType, setScheduledActivityType] = useState<ScheduleActivityType>('call');
  const [scheduledActivityText, setScheduledActivityText] = useState('Call');
  const [showScheduledActivityTypeDropdown, setShowScheduledActivityTypeDropdown] = useState(false);
  const [showActivityCalendar, setShowActivityCalendar] = useState(false);
  const [showCalendarToggleTooltip, setShowCalendarToggleTooltip] = useState(false);
  const [activityNowTick, setActivityNowTick] = useState(() => Date.now());
  const [activitySchedulerForm, setActivitySchedulerForm] = useState<DetailedActivityFormData>(() => {
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const toLocal = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return {
      title: 'Call',
      description: '',
      startTime: toLocal(now),
      endTime: toLocal(end),
      location: '',
      meetingType: 'phone',
      meetingUrl: '',
      clientId: '',
      contactId: '',
      dealId: '',
      leadId: '',
      purposeTypeId: DEFAULT_ACTIVITY_PURPOSE_ID,
      notes: '',
      priority: 'normal',
      freeBusy: 'free',
      markAsDone: false,
      videoCallProvider: '',
    };
  });
  const [activityDealLeadSearch, setActivityDealLeadSearch] = useState('');
  const [showActivityDealLeadDropdown, setShowActivityDealLeadDropdown] = useState(false);
  const [activityPeopleSearch, setActivityPeopleSearch] = useState('');
  const [showActivityPeopleDropdown, setShowActivityPeopleDropdown] = useState(false);
  const [activityOrganizationSearch, setActivityOrganizationSearch] = useState('');
  const [showActivityOrganizationDropdown, setShowActivityOrganizationDropdown] = useState(false);

  const shouldLoadActivitySchedulerLookups = showSidebar && activeTab === 'activity' && !!selectedLead;
  const { data: dealsData } = useGetDealsQuery({}, { skip: !shouldLoadActivitySchedulerLookups });
  const { data: contactsData } = useGetContactsQuery({}, { skip: !shouldLoadActivitySchedulerLookups });
  const { data: clientsData } = useGetClientsQuery({}, { skip: !shouldLoadActivitySchedulerLookups });

  useEffect(() => {
    try {
      const normalized = Array.from(
        new Set(favoriteOwnerKeys.map((entry) => normalizeOwnerKey(entry)).filter(Boolean))
      );
      localStorage.setItem(LEAD_OWNER_FAVORITES_STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // Ignore localStorage persistence failures
    }
  }, [favoriteOwnerKeys]);

  useEffect(() => {
    try {
      const normalized = Array.from(
        new Set(favoriteVisibilityFilterIds.map((entry) => String(entry || '').trim()).filter(Boolean))
      );
      localStorage.setItem(LEAD_FILTER_FAVORITES_STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // Ignore localStorage persistence failures
    }
  }, [favoriteVisibilityFilterIds]);

  useEffect(() => {
    try {
      localStorage.setItem(LEAD_CUSTOM_FILTERS_STORAGE_KEY, JSON.stringify(savedVisibilityFilters));
    } catch {
      // Ignore localStorage persistence failures
    }
  }, [savedVisibilityFilters]);

  const meetingPurposeOptions = useMemo<ActivityPurposeOption[]>(() => {
    const persisted = (userPreferencesData as any)?.preferences?.crmMeetingPurposeTypes;
    if (!Array.isArray(persisted)) return DEFAULT_ACTIVITY_PURPOSE_OPTIONS;

    const seen = new Set<string>();
    const normalized: ActivityPurposeOption[] = [];
    persisted.forEach((item: any) => {
      if (!item || typeof item !== 'object') return;
      const name = String(item.name || item.label || '').trim().replace(/\s+/g, ' ');
      if (!name) return;
      const id = String(item.id || name)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      if (!id || seen.has(id)) return;
      seen.add(id);
      normalized.push({
        id,
        name,
        color: String(item.color || '').trim() || undefined,
      });
    });

    return normalized.length > 0 ? normalized : DEFAULT_ACTIVITY_PURPOSE_OPTIONS;
  }, [userPreferencesData]);

  useEffect(() => {
    if (meetingPurposeOptions.length === 0) return;
    setActivitySchedulerForm((prev) => {
      if (meetingPurposeOptions.some((option) => option.id === prev.purposeTypeId)) return prev;
      return {
        ...prev,
        purposeTypeId: meetingPurposeOptions[0].id,
      };
    });
  }, [meetingPurposeOptions]);

  const activityDealRecords = useMemo(
    () => (Array.isArray(dealsData) ? (dealsData as Array<{ id: string; name?: string }>) : []),
    [dealsData]
  );
  const activityContactRecords = useMemo(
    () => (
      Array.isArray(contactsData)
        ? (contactsData as Array<{ id: string; firstName?: string; lastName?: string; organization?: string; email?: string }>)
        : []
    ),
    [contactsData]
  );
  const activityClientRecords = useMemo(
    () => (Array.isArray(clientsData) ? (clientsData as Array<{ id: string; name?: string; organization?: string }>) : []),
    [clientsData]
  );

  const filteredActivityDealsAndLeads = useMemo(() => {
    const query = activityDealLeadSearch.toLowerCase().trim();
    const dealItems = activityDealRecords.map((deal) => ({
      id: deal.id,
      type: 'deal' as const,
      label: deal.name || 'Untitled deal',
    }));
    const leadItems = (leads as Lead[]).map((lead) => ({
      id: lead.id,
      type: 'lead' as const,
      label: lead.organization ? `${lead.name || 'Untitled'} (${lead.organization})` : (lead.name || 'Untitled lead'),
    }));
    const allItems = [...dealItems, ...leadItems];
    if (!query) return allItems.slice(0, 12);
    return allItems.filter((item) => item.label.toLowerCase().includes(query)).slice(0, 12);
  }, [activityDealRecords, leads, activityDealLeadSearch]);

  const activityDealLeadDisplayLabel = useMemo(() => {
    if (activitySchedulerForm.dealId) {
      const deal = activityDealRecords.find((item) => item.id === activitySchedulerForm.dealId);
      return deal?.name || 'Deal';
    }
    if (activitySchedulerForm.leadId) {
      const lead = (leads as Lead[]).find((item) => item.id === activitySchedulerForm.leadId);
      if (!lead) return 'Lead';
      return lead.organization ? `${lead.name || ''} (${lead.organization})` : (lead.name || 'Lead');
    }
    return '';
  }, [activitySchedulerForm.dealId, activitySchedulerForm.leadId, activityDealRecords, leads]);

  const filteredActivityContacts = useMemo(() => {
    const query = activityPeopleSearch.toLowerCase().trim();
    const list = activityContactRecords.map((contact) => {
      const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
      const label = contact.organization ? `${fullName} (${contact.organization})` : (fullName || 'Untitled contact');
      const searchText = `${fullName} ${contact.organization || ''} ${contact.email || ''}`.toLowerCase();
      return { id: contact.id, label, searchText };
    });
    if (!query) return list.slice(0, 12).map(({ id, label }) => ({ id, label }));
    return list.filter((item) => item.searchText.includes(query)).slice(0, 12).map(({ id, label }) => ({ id, label }));
  }, [activityContactRecords, activityPeopleSearch]);

  const activityContactDisplayLabel = useMemo(() => {
    if (!activitySchedulerForm.contactId) return '';
    const contact = activityContactRecords.find((item) => item.id === activitySchedulerForm.contactId);
    if (!contact) return 'Contact';
    const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    return contact.organization ? `${fullName} (${contact.organization})` : (fullName || 'Contact');
  }, [activitySchedulerForm.contactId, activityContactRecords]);

  const filteredActivityOrganizations = useMemo(() => {
    const query = activityOrganizationSearch.toLowerCase().trim();
    const list = activityClientRecords.map((client) => {
      const label = client.name || client.organization || 'Organization';
      return {
        id: client.id,
        label,
        searchText: `${label}`.toLowerCase(),
      };
    });
    if (!query) return list.slice(0, 12);
    return list.filter((item) => item.searchText.includes(query)).slice(0, 12);
  }, [activityClientRecords, activityOrganizationSearch]);

  const activityOrganizationDisplayLabel = useMemo(() => {
    if (!activitySchedulerForm.clientId) return '';
    const client = activityClientRecords.find((item) => item.id === activitySchedulerForm.clientId);
    return client?.name || client?.organization || 'Organization';
  }, [activitySchedulerForm.clientId, activityClientRecords]);

  const getDetailedActivityDefaultStartEnd = () => {
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const toLocal = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { start: toLocal(now), end: toLocal(end) };
  };

  const createDefaultDetailedActivityFormData = (
    lead: Lead | null,
    activityType: ScheduleActivityType,
    titleOverride?: string
  ): DetailedActivityFormData => {
    const { start, end } = getDetailedActivityDefaultStartEnd();
    const title = String(titleOverride || '').trim() || SCHEDULE_ACTIVITY_LABELS[activityType];
    return {
      title,
      description: '',
      startTime: start,
      endTime: end,
      location: String(lead?.organization || '').trim(),
      meetingType: (activityType === 'call' || activityType === 'text') ? 'phone' : 'in-person',
      meetingUrl: '',
      clientId: '',
      contactId: '',
      dealId: '',
      leadId: String(lead?.id || ''),
      purposeTypeId: meetingPurposeOptions[0]?.id || DEFAULT_ACTIVITY_PURPOSE_ID,
      notes: '',
      priority: 'normal',
      freeBusy: 'free',
      markAsDone: false,
      videoCallProvider: '',
    };
  };

  const handleActivitySchedulerFieldChange = (field: keyof DetailedActivityFormData, value: string | boolean) => {
    setActivitySchedulerForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectDetailedActivityType = (activityType: ScheduleActivityType) => {
    const nextLabel = SCHEDULE_ACTIVITY_LABELS[activityType];
    setScheduledActivityType(activityType);
    setScheduledActivityText(nextLabel);
    setActivitySchedulerForm((prev) => ({
      ...prev,
      title: nextLabel,
      meetingType:
        activityType === 'call' || activityType === 'text'
          ? 'phone'
          : (prev.videoCallProvider ? 'video' : 'in-person'),
    }));
  };

  const getActivitySchedulerDisplayDate = () => {
    if (activitySchedulerForm.startTime) {
      const parsed = new Date(activitySchedulerForm.startTime);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  };

  const shiftActivitySchedulerDay = (delta: number) => {
    const nextDate = getActivitySchedulerDisplayDate();
    nextDate.setDate(nextDate.getDate() + delta);
    const pad = (n: number) => String(n).padStart(2, '0');
    const datePrefix = `${nextDate.getFullYear()}-${pad(nextDate.getMonth() + 1)}-${pad(nextDate.getDate())}T`;
    const fallback = getDetailedActivityDefaultStartEnd();
    const startTime = activitySchedulerForm.startTime || fallback.start;
    const endTime = activitySchedulerForm.endTime || fallback.end;
    const startClock = startTime.slice(11, 16);
    const endClock = endTime.slice(11, 16);
    setActivitySchedulerForm((prev) => ({
      ...prev,
      startTime: `${datePrefix}${startClock}`,
      endTime: `${datePrefix}${endClock}`,
    }));
  };

  const handleActivitySchedulerPrevDay = () => shiftActivitySchedulerDay(-1);
  const handleActivitySchedulerNextDay = () => shiftActivitySchedulerDay(1);
  const handleActivitySchedulerToday = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const datePrefix = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T`;
    const fallback = getDetailedActivityDefaultStartEnd();
    const startTime = activitySchedulerForm.startTime || fallback.start;
    const endTime = activitySchedulerForm.endTime || fallback.end;
    const startClock = startTime.slice(11, 16);
    const endClock = endTime.slice(11, 16);
    setActivitySchedulerForm((prev) => ({
      ...prev,
      startTime: `${datePrefix}${startClock}`,
      endTime: `${datePrefix}${endClock}`,
    }));
  };

  const activityCalendarStartHour = useMemo(() => {
    const displayDate = getActivitySchedulerDisplayDate();
    const now = new Date(activityNowTick);
    const isDisplayToday =
      displayDate.getDate() === now.getDate()
      && displayDate.getMonth() === now.getMonth()
      && displayDate.getFullYear() === now.getFullYear();
    const selectedStart = new Date(activitySchedulerForm.startTime);
    const hasSelectedStart = !Number.isNaN(selectedStart.getTime());
    const sameDayAsDisplay =
      hasSelectedStart
      && selectedStart.getDate() === displayDate.getDate()
      && selectedStart.getMonth() === displayDate.getMonth()
      && selectedStart.getFullYear() === displayDate.getFullYear();

    const selectedHour = sameDayAsDisplay ? selectedStart.getHours() : displayDate.getHours();
    const nowHour = now.getHours();
    let proposedStart = selectedHour - 4;

    if (isDisplayToday && sameDayAsDisplay && Math.abs(selectedHour - nowHour) < ACTIVITY_CALENDAR_VISIBLE_HOURS) {
      proposedStart = Math.min(selectedHour, nowHour) - 1;
    }

    if (isDisplayToday) {
      const minStartForNow = nowHour - ACTIVITY_CALENDAR_VISIBLE_HOURS + 1;
      const maxStartForNow = nowHour;
      proposedStart = Math.max(proposedStart, minStartForNow);
      proposedStart = Math.min(proposedStart, maxStartForNow);
    }

    return Math.max(0, Math.min(24 - ACTIVITY_CALENDAR_VISIBLE_HOURS, proposedStart));
  }, [activitySchedulerForm.startTime, activityNowTick]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActivityNowTick(Date.now());
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  const handleActivityCalendarGridClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const relativeY = Math.max(0, Math.min(rect.height - 1, event.clientY - rect.top));
    const slotOffset = relativeY / ACTIVITY_CALENDAR_SLOT_HEIGHT;
    const hourOffset = Math.floor(slotOffset);
    const minutes = (slotOffset - hourOffset) >= 0.5 ? 30 : 0;
    const clickedHour = Math.min(23, activityCalendarStartHour + hourOffset);

    const displayDate = getActivitySchedulerDisplayDate();
    const startDate = new Date(displayDate);
    startDate.setHours(clickedHour, minutes, 0, 0);

    const existingStart = new Date(activitySchedulerForm.startTime);
    const existingEnd = new Date(activitySchedulerForm.endTime);
    const fallbackDurationMs = 60 * 60 * 1000;
    const existingDurationMs =
      !Number.isNaN(existingStart.getTime())
      && !Number.isNaN(existingEnd.getTime())
      && existingEnd.getTime() > existingStart.getTime()
        ? (existingEnd.getTime() - existingStart.getTime())
        : fallbackDurationMs;
    const endDate = new Date(startDate.getTime() + existingDurationMs);

    setActivitySchedulerForm((prev) => ({
      ...prev,
      startTime: toLocalDateTimeInputValue(startDate),
      endTime: toLocalDateTimeInputValue(endDate),
    }));
  };

  const handleCancelDetailedActivity = () => {
    if (!selectedLead) return;
    setActivitySchedulerForm(
      createDefaultDetailedActivityFormData(selectedLead, scheduledActivityType, scheduledActivityText)
    );
    setActivityDealLeadSearch('');
    setActivityPeopleSearch('');
    setActivityOrganizationSearch('');
    setShowActivityDealLeadDropdown(false);
    setShowActivityPeopleDropdown(false);
    setShowActivityOrganizationDropdown(false);
  };

  const handleSaveDetailedActivity = async () => {
    if (!selectedLead) return;

    const currentLead = selectedLead;
    const title = String(activitySchedulerForm.title || '').trim() || SCHEDULE_ACTIVITY_LABELS[scheduledActivityType];
    const startDate = new Date(activitySchedulerForm.startTime);
    const endDate = new Date(activitySchedulerForm.endTime);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      alert('Please set a valid activity date and time.');
      return;
    }

    const purposeTag = `purpose:${activitySchedulerForm.purposeTypeId || (meetingPurposeOptions[0]?.id || DEFAULT_ACTIVITY_PURPOSE_ID)}`;
    const tags = Array.from(new Set([`activity:${scheduledActivityType}`, purposeTag]));

    try {
      await createMeeting({
        title,
        description: activitySchedulerForm.description || undefined,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
        location: activitySchedulerForm.location || undefined,
        meetingType: activitySchedulerForm.videoCallProvider ? 'video' : activitySchedulerForm.meetingType,
        meetingUrl: activitySchedulerForm.meetingUrl || undefined,
        clientId: activitySchedulerForm.clientId || undefined,
        contactId: activitySchedulerForm.contactId || undefined,
        dealId: activitySchedulerForm.dealId || undefined,
        leadId: currentLead.id,
        purposeTypeId: activitySchedulerForm.purposeTypeId || (meetingPurposeOptions[0]?.id || DEFAULT_ACTIVITY_PURPOSE_ID),
        notes: activitySchedulerForm.notes || undefined,
        tags,
        visibility: 'personal',
      }).unwrap();

      const isPhoneActivity = scheduledActivityType === 'call' || scheduledActivityType === 'text';
      const nextActivityDate = toLocaleDateStringInTimezone(
        startDate,
        'en-US',
        { month: 'long', day: 'numeric' },
        tenantTimezone
      );

      try {
        await updateLead({
          leadId: currentLead.id,
          nextActivity: {
            type: isPhoneActivity ? 'phone' : 'calendar',
            date: nextActivityDate,
          },
        }).unwrap();

        setSelectedLead((prev) =>
          prev
            ? {
                ...prev,
                nextActivity: {
                  type: isPhoneActivity ? 'phone' : 'calendar',
                  date: nextActivityDate,
                },
              }
            : prev
        );
      } catch (leadUpdateError: any) {
        console.error('Failed to sync next activity to lead:', leadUpdateError);
      }

      refetchMeetings();
      refetchLeads();
      setActivitySchedulerForm(
        createDefaultDetailedActivityFormData(currentLead, scheduledActivityType, SCHEDULE_ACTIVITY_LABELS[scheduledActivityType])
      );
      setActivityDealLeadSearch('');
      setActivityPeopleSearch('');
      setActivityOrganizationSearch('');
      setShowActivityDealLeadDropdown(false);
      setShowActivityPeopleDropdown(false);
      setShowActivityOrganizationDropdown(false);
    } catch (error: any) {
      console.error('Error creating detailed activity:', error);
      alert(`Failed to schedule activity: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const toDateInputValue = (dateValue?: string): string => {
    if (!dateValue) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;
    const usMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usMatch) {
      const month = usMatch[1].padStart(2, '0');
      const day = usMatch[2].padStart(2, '0');
      const year = usMatch[3];
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(dateValue);
    if (!Number.isNaN(parsed.getTime())) {
      return getDateKeyInTimezone(parsed, tenantTimezone) || '';
    }
    return '';
  };

  const getWorkPhoneFromLead = (lead?: Lead | null): string => {
    if (!lead) return '';
    const explicitWorkPhone = String(lead.workPhone || '').trim();
    if (explicitWorkPhone) return explicitWorkPhone;

    const legacyPhone = String(lead.phone || '').trim();
    const legacyType = String(lead.phoneType || 'Work').trim().toLowerCase();
    return legacyPhone && (legacyType === '' || legacyType === 'work') ? legacyPhone : '';
  };

  const getCellPhoneFromLead = (lead?: Lead | null): string => {
    if (!lead) return '';
    const explicitCellPhone = String(lead.cellPhone || '').trim();
    if (explicitCellPhone) return explicitCellPhone;

    const legacyPhone = String(lead.phone || '').trim();
    const legacyType = String(lead.phoneType || '').trim().toLowerCase();
    return legacyPhone && (legacyType === 'cell' || legacyType === 'mobile') ? legacyPhone : '';
  };

  const parseLabelsInput = (raw: string): string[] => {
    const seen = new Set<string>();
    const parsed: string[] = [];
    raw
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((label) => {
        const key = label.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        parsed.push(label);
      });
    return parsed;
  };

  const includesLabelKey = (labels: string[] | undefined, label: string): boolean => {
    const targetKey = normalizeLabelKey(label);
    if (!targetKey || !Array.isArray(labels)) return false;
    return labels.some((entry) => normalizeLabelKey(entry) === targetKey);
  };

  const persistHiddenLabels = (nextHiddenLabels: string[]) => {
    const deduped = Array.from(new Set(nextHiddenLabels.map((entry) => normalizeLabelKey(entry)).filter(Boolean)));
    setHiddenLabels(deduped);
    localStorage.setItem(HIDDEN_LEAD_LABELS_STORAGE_KEY, JSON.stringify(deduped));
  };

  const hideLabelFromCatalog = (labelName: string) => {
    const labelKey = normalizeLabelKey(labelName);
    if (!labelKey) return;
    persistHiddenLabels([...hiddenLabels, labelKey]);
  };

  const unhideLabelFromCatalog = (labelName: string) => {
    const labelKey = normalizeLabelKey(labelName);
    if (!labelKey) return;
    persistHiddenLabels(hiddenLabels.filter((entry) => normalizeLabelKey(entry) !== labelKey));
  };

  const removeLabelFromLeads = async (labelName: string) => {
    const labelKey = normalizeLabelKey(labelName);
    if (!labelKey) return;

    const leadsWithLabel = leads.filter((lead: Lead) => {
      const leadLabels = Array.isArray(lead.labels) ? lead.labels : [];
      return leadLabels.some((label: string) => normalizeLabelKey(label) === labelKey);
    });

    const failedLeadIds: string[] = [];
    for (const lead of leadsWithLabel) {
      const nextLabels = (Array.isArray(lead.labels) ? lead.labels : []).filter(
        (label: string) => normalizeLabelKey(label) !== labelKey
      );

      try {
        await updateLead({
          leadId: lead.id,
          labels: nextLabels
        }).unwrap();
      } catch (error) {
        console.error(`Failed to remove label from lead ${lead.id}:`, error);
        failedLeadIds.push(lead.id);
      }
    }

    setSelectedLead((prev) => {
      if (!prev) return prev;
      const currentLabels = Array.isArray(prev.labels) ? prev.labels : [];
      const nextLabels = currentLabels.filter((label: string) => normalizeLabelKey(label) !== labelKey);
      if (nextLabels.length === currentLabels.length) return prev;
      return { ...prev, labels: nextLabels };
    });

    if (normalizeLabelKey(selectedLabel) === labelKey) {
      setSelectedLabel('All labels');
    }

    refetchLeads();

    if (failedLeadIds.length > 0) {
      throw new Error(`Could not remove label from ${failedLeadIds.length} lead(s).`);
    }
  };

  const resetLabelEditorState = () => {
    setShowEditLabelModal(false);
    setEditingLabelName('');
    setEditingLabelOriginalName('');
    setEditingLabelColor('secondary');
  };

  const handleDeleteLabel = async (
    labelName: string,
    options: { closeFilter?: boolean; closeLabelsModal?: boolean } = {}
  ) => {
    const normalizedLabel = String(labelName || '').trim();
    if (!normalizedLabel) return;

    if (!window.confirm(`Are you sure you want to delete the label "${normalizedLabel}"?`)) {
      return;
    }

    const labelKey = normalizeLabelKey(normalizedLabel);
    const updatedLabels = customLabels.filter((label) => normalizeLabelKey(label) !== labelKey);
    setCustomLabels(updatedLabels);
    localStorage.setItem('customLabels', JSON.stringify(updatedLabels));

    const storedColors = JSON.parse(localStorage.getItem('labelColors') || '{}');
    delete storedColors[labelKey];
    localStorage.setItem('labelColors', JSON.stringify(storedColors));

    hideLabelFromCatalog(normalizedLabel);

    try {
      await removeLabelFromLeads(normalizedLabel);
    } catch (error: any) {
      alert(`Label removed, but some leads could not be updated automatically. ${error?.message || ''}`.trim());
    }

    resetLabelEditorState();
    if (options.closeFilter) setShowLabelsFilter(false);
    if (options.closeLabelsModal) setShowLabelsModal(false);
  };

  const parseLeadNotes = (rawNotes: unknown): LeadNote[] => {
    if (!Array.isArray(rawNotes)) return [];

    return rawNotes
      .map((note, index) => {
        if (!note || typeof note !== 'object') return null;
        const noteRecord = note as Partial<LeadNote>;
        const content = String(noteRecord.content || '').trim();
        const timestamp = String(noteRecord.timestamp || '').trim();
        if (!content || !timestamp) return null;

        return {
          id: String(noteRecord.id || `note-${timestamp}-${index}`),
          content: String(noteRecord.content),
          timestamp,
          author: String(noteRecord.author || 'PhiLL Kross'),
          pinned: Boolean((noteRecord as any).pinned),
          pinnedAt: String((noteRecord as any).pinnedAt || '').trim() || undefined,
        } as LeadNote;
      })
      .filter((note): note is LeadNote => Boolean(note))
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };

  const isLeadArchived = (lead?: Lead | null): boolean =>
    String(lead?.status || '').trim().toLowerCase() === 'archived';

  useEffect(() => {
    setSelectedLeads(new Set());
    if (!selectedLead) return;

    const shouldCloseSidebar =
      (leadMailboxView === 'inbox' && isLeadArchived(selectedLead))
      || (leadMailboxView === 'archive' && !isLeadArchived(selectedLead));

    if (shouldCloseSidebar) {
      setShowSidebar(false);
      setSelectedLead(null);
    }
  }, [leadMailboxView, selectedLead]);

  // Reset editing state when selectedLead changes
  useEffect(() => {
    if (selectedLead) {
      setIsEditingName(false);
      setEditedName(selectedLead.name);
      setIsHoveringName(false);
      // Load value and currency from selected lead
      setValueDetails(selectedLead.value || '');
      setCurrencyDetails(selectedLead.currency || 'US Dollar (USD)');
      setExpectedCloseDateDraft(toDateInputValue(selectedLead.expectedCloseDate));
      setIsEditingExpectedCloseDate(false);
      setIsHoveringExpectedCloseDate(false);
      setEditingDetailFieldId(null);
      setDetailFieldDraftValue('');
      setDetailFieldDraftChecked(false);
      setShowSourceChannelDropdown(false);
      setSourceChannelSearchQuery('');
      setShowCreateSourceChannel(false);
      setNewSourceChannelOption('');
      setEditingSourceChannelOption(null);
      setEditingSourceChannelOptionDraft('');
      setHoveredSourceChannelOption(null);
      setShowBusinessUnitDropdown(false);
      setBusinessUnitSearchQuery('');
      setShowCreateBusinessUnit(false);
      setNewBusinessUnitOption('');
      setEditingBusinessUnitOption(null);
      setEditingBusinessUnitOptionDraft('');
      setHoveredBusinessUnitOption(null);
      const nextScheduledType: ScheduleActivityType =
        selectedLead.nextActivity?.type === 'calendar'
          ? 'meeting'
          : selectedLead.nextActivity?.type === 'phone'
            ? 'call'
            : 'call';
      setScheduledActivityType(nextScheduledType);
      setScheduledActivityText(SCHEDULE_ACTIVITY_LABELS[nextScheduledType]);
      setShowScheduledActivityTypeDropdown(false);
      setActivitySchedulerForm(
        createDefaultDetailedActivityFormData(selectedLead, nextScheduledType, SCHEDULE_ACTIVITY_LABELS[nextScheduledType])
      );
      setActivityDealLeadSearch('');
      setActivityPeopleSearch('');
      setActivityOrganizationSearch('');
      setShowActivityDealLeadDropdown(false);
      setShowActivityPeopleDropdown(false);
      setShowActivityOrganizationDropdown(false);
      setNotes(parseLeadNotes(selectedLead.notes));
      setCurrentNoteContent('');
      if (noteEditorRef) {
        noteEditorRef.innerHTML = '';
        noteEditorRef.setAttribute('data-placeholder', 'Take a note, @name...');
      }
      setIsNoteEditorFocused(false);
    }
  }, [selectedLead?.id]);

  useEffect(() => {
    const persistedOptions = (userPreferencesData as any)?.preferences?.crmSourceChannelOptions;
    if (!Array.isArray(persistedOptions)) return;

    const normalized = dedupeSourceChannelOptions(persistedOptions.map((value: any) => String(value)));
    setSourceChannelOptions(normalized.length > 0 ? normalized : [...DEFAULT_SOURCE_CHANNEL_OPTIONS]);
  }, [userPreferencesData]);

  useEffect(() => {
    const persistedOptions = (userPreferencesData as any)?.preferences?.crmBusinessUnitOptions;
    if (!Array.isArray(persistedOptions)) return;

    const normalized = dedupeBusinessUnitOptions(persistedOptions.map((value: any) => String(value)));
    setBusinessUnitOptions(normalized.length > 0 ? normalized : [...DEFAULT_BUSINESS_UNIT_OPTIONS]);
  }, [userPreferencesData]);
  
  // Form state for Add Lead modal
  const [formData, setFormData] = useState({
    contactPerson: '',
    organization: '',
    accountStage: '',
    contactStage: '',
    visibility: 'global' as 'global' | 'personal',
    title: '',
    value: '',
    currency: 'US Dollar (USD)',
    labels: '',
    expectedCloseDate: '',
    sourceChannel: '',
    sourceChannelId: '',
    businessUnit: '',
    phone: '',
    phoneType: 'Work',
    email: '',
    emailType: 'Work',
    decisionMaker: false
  });

  const ownerFieldDisplayLabel = useMemo(() => {
    const explicitOwner = String(selectedLead?.owner || '').trim();
    if (explicitOwner) return explicitOwner;
    if (selectedLead?.visibility === 'global') return DEFAULT_OWNER_FILTER_LABEL;
    return selfOwnerBaseLabel || DEFAULT_OWNER_FILTER_LABEL;
  }, [selectedLead?.owner, selectedLead?.visibility, selfOwnerBaseLabel]);

  const visibilityFieldDisplayLabel = useMemo(() => {
    return selectedLead?.visibility === 'personal'
      ? 'Personal (private)'
      : 'Global (visible for everyone)';
  }, [selectedLead?.visibility]);

  // Define all available fields
  const allFields = useMemo(() => [
    { id: 'addToSequence', label: 'Add to sequence', icon: FileText, added: addedFields.has('addToSequence') },
    { id: 'labels', label: 'Labels', icon: Tag, added: addedFields.has('labels'), isLabels: true },
    { id: 'value', label: 'Value', icon: CurrencyDollar, added: addedFields.has('value') },
    { id: 'expectedCloseDate', label: 'Expected close date', icon: Calendar, added: addedFields.has('expectedCloseDate') },
    { id: 'country', label: 'Country', icon: CheckSquare, added: addedFields.has('country') },
    { id: 'businessUnit', label: 'Business Unit:', icon: Briefcase, added: addedFields.has('businessUnit') },
    { id: 'businessType', label: 'Business Type:', icon: CheckSquare, added: addedFields.has('businessType') },
    { id: 'totalLocations', label: 'Total Locations:', icon: FileText, added: addedFields.has('totalLocations') },
    { id: 'rfpDate', label: 'RFP Date:', icon: Calendar, added: addedFields.has('rfpDate') },
    { id: 'rfpExpirationDate', label: 'RFP Expiration Date:', icon: Calendar, added: addedFields.has('rfpExpirationDate') },
    { id: 'securedLocations', label: 'Secured Locations:', icon: FileText, added: addedFields.has('securedLocations') },
    { id: 'industry', label: 'Industry:', icon: FileText, added: addedFields.has('industry') },
    { id: 'addedOnLinkedIn', label: 'Added on Linkedin:', icon: FileText, added: addedFields.has('addedOnLinkedIn') },
    { id: 'kkPlan', label: 'KK Plan:', icon: FileText, added: addedFields.has('kkPlan') },
    { id: 'goal', label: 'Goal:', icon: FileText, added: addedFields.has('goal') },
    { id: 'found', label: 'Found:', icon: FileText, added: addedFields.has('found') },
    { id: 'challenges', label: 'Challenges:', icon: FileText, added: addedFields.has('challenges') },
    { id: 'timing', label: 'Timing:', icon: FileText, added: addedFields.has('timing') },
    { id: 'dealScore', label: 'Deal Score:', icon: CheckSquare, added: addedFields.has('dealScore') },
    { id: 'dealSource', label: 'Deal Source:', icon: CheckSquare, added: addedFields.has('dealSource') },
    { id: 'eyeballs', label: 'Eyeballs', icon: FileText, added: addedFields.has('eyeballs') },
    { id: 'owner', label: ownerFieldDisplayLabel, icon: Person, added: addedFields.has('owner') },
    { id: 'visibility', label: visibilityFieldDisplayLabel, icon: PeopleFill, added: true },
    { id: 'sourceOrigin', label: 'Manually created • Source origin', icon: ArrowRight, added: addedFields.has('sourceOrigin'), hasInfo: true },
    { id: 'sourceChannel', label: 'Source channel', icon: ArrowRight, added: addedFields.has('sourceChannel') },
    { id: 'sourceChannelId', label: 'Source channel ID', icon: Dot, added: addedFields.has('sourceChannelId') },
  ], [addedFields, ownerFieldDisplayLabel, visibilityFieldDisplayLabel]);

  type DetailEditableFieldConfig = {
    leadKey: keyof Lead;
    inputType: 'text' | 'date' | 'checkbox';
    placeholder: string;
  };

  const detailEditableFieldConfig: Record<string, DetailEditableFieldConfig> = {
    title: { leadKey: 'title', inputType: 'text', placeholder: 'Title' },
    contactStage: { leadKey: 'contactStage', inputType: 'text', placeholder: 'Contact Stage' },
    email: { leadKey: 'email', inputType: 'text', placeholder: 'Email' },
    workPhone: { leadKey: 'workPhone', inputType: 'text', placeholder: 'Work Phone' },
    cellPhone: { leadKey: 'cellPhone', inputType: 'text', placeholder: 'Cell Phone' },
    assistantName: { leadKey: 'assistantName', inputType: 'text', placeholder: "Assistant's Name" },
    assistantPhone: { leadKey: 'assistantPhone', inputType: 'text', placeholder: "Assistant's Phone" },
    assistantEmail: { leadKey: 'assistantEmail', inputType: 'text', placeholder: "Assistant's Email" },
    organization: { leadKey: 'organization', inputType: 'text', placeholder: 'Organization' },
    contractRenewalDate: { leadKey: 'contractRenewalDate', inputType: 'date', placeholder: 'Contract Renewal' },
    accountStage: { leadKey: 'accountStage', inputType: 'text', placeholder: 'Account Stage' },
    address: { leadKey: 'address', inputType: 'text', placeholder: 'Address' },
    website: { leadKey: 'website', inputType: 'text', placeholder: 'Website' },
    linkedInProfile: { leadKey: 'linkedInProfile', inputType: 'text', placeholder: 'LinkedIn profile' },
    numberOfEmployees: { leadKey: 'numberOfEmployees', inputType: 'text', placeholder: 'Number of employees' },
    annualRevenue: { leadKey: 'annualRevenue', inputType: 'text', placeholder: 'Annual revenue' },
    country: { leadKey: 'country', inputType: 'text', placeholder: 'Country' },
    businessUnit: { leadKey: 'businessUnit', inputType: 'text', placeholder: 'Business Unit' },
    businessType: { leadKey: 'businessType', inputType: 'text', placeholder: 'Business Type' },
    totalLocations: { leadKey: 'totalLocations', inputType: 'text', placeholder: 'Total Locations' },
    rfpDate: { leadKey: 'rfpDate', inputType: 'date', placeholder: 'RFP Date' },
    rfpExpirationDate: { leadKey: 'rfpExpirationDate', inputType: 'date', placeholder: 'RFP Expiration Date' },
    securedLocations: { leadKey: 'securedLocations', inputType: 'text', placeholder: 'Secured Locations' },
    industry: { leadKey: 'industry', inputType: 'text', placeholder: 'Industry' },
    addedOnLinkedIn: { leadKey: 'addedOnLinkedIn', inputType: 'text', placeholder: 'Added on Linkedin' },
    kkPlan: { leadKey: 'kkPlan', inputType: 'text', placeholder: 'KK Plan' },
    goal: { leadKey: 'goal', inputType: 'text', placeholder: 'Goal' },
    found: { leadKey: 'found', inputType: 'text', placeholder: 'Found' },
    challenges: { leadKey: 'challenges', inputType: 'text', placeholder: 'Challenges' },
    timing: { leadKey: 'timing', inputType: 'text', placeholder: 'Timing' },
    dealScore: { leadKey: 'dealScore', inputType: 'text', placeholder: 'Deal Score' },
    dealSource: { leadKey: 'dealSource', inputType: 'text', placeholder: 'Deal Source' },
    eyeballs: { leadKey: 'eyeballs', inputType: 'text', placeholder: 'Eyeballs' },
    owner: { leadKey: 'owner', inputType: 'text', placeholder: 'Owner' },
    visibility: { leadKey: 'visibility', inputType: 'text', placeholder: 'Visibility' },
    sourceOrigin: { leadKey: 'sourceOrigin', inputType: 'text', placeholder: 'Source origin' },
    sourceChannel: { leadKey: 'sourceChannel', inputType: 'text', placeholder: 'Source channel' },
    sourceChannelId: { leadKey: 'sourceChannelId', inputType: 'text', placeholder: 'Source channel ID' }
  };

  const startEditingDetailField = (fieldId: string) => {
    const config = detailEditableFieldConfig[fieldId];
    if (!config || !selectedLead) return;

    if (fieldId !== 'sourceChannel') {
      setShowSourceChannelDropdown(false);
      setSourceChannelSearchQuery('');
      setShowCreateSourceChannel(false);
      setNewSourceChannelOption('');
      setEditingSourceChannelOption(null);
      setEditingSourceChannelOptionDraft('');
      setHoveredSourceChannelOption(null);
    }
    if (fieldId !== 'businessUnit') {
      setShowBusinessUnitDropdown(false);
      setBusinessUnitSearchQuery('');
      setShowCreateBusinessUnit(false);
      setNewBusinessUnitOption('');
      setEditingBusinessUnitOption(null);
      setEditingBusinessUnitOptionDraft('');
      setHoveredBusinessUnitOption(null);
    }

    const currentValue = selectedLead[config.leadKey];
    if (config.inputType === 'checkbox') {
      setDetailFieldDraftChecked(Boolean(currentValue));
      setDetailFieldDraftValue('');
    } else if (config.inputType === 'date') {
      setDetailFieldDraftValue(toDateInputValue(String(currentValue ?? '')));
    } else if (fieldId === 'workPhone') {
      setDetailFieldDraftValue(getWorkPhoneFromLead(selectedLead));
    } else if (fieldId === 'cellPhone') {
      setDetailFieldDraftValue(getCellPhoneFromLead(selectedLead));
    } else if (fieldId === 'businessUnit') {
      const businessUnitValue = typeof currentValue === 'string' ? currentValue.trim() : '';
      const fallbackValue = businessUnitValue || (selectedLead.inStore ? 'In-Store' : '');
      setDetailFieldDraftValue(fallbackValue);
    } else if (fieldId === 'owner') {
      const ownerValue = typeof currentValue === 'string' ? currentValue.trim() : '';
      if (ownerValue) {
        setDetailFieldDraftValue(ownerValue);
      } else if (selectedLead.visibility === 'global') {
        setDetailFieldDraftValue(DEFAULT_OWNER_FILTER_LABEL);
      } else {
        setDetailFieldDraftValue(selfOwnerBaseLabel || DEFAULT_OWNER_FILTER_LABEL);
      }
    } else if (fieldId === 'visibility') {
      setDetailFieldDraftValue(selectedLead.visibility === 'personal' ? 'personal' : 'global');
    } else {
      setDetailFieldDraftValue(typeof currentValue === 'string' ? currentValue : '');
    }
    setEditingDetailFieldId(fieldId);
  };

  const handleSaveDetailField = async (fieldId: string) => {
    if (!selectedLead) return;
    const config = detailEditableFieldConfig[fieldId];
    if (!config) return;

    const currentValue = selectedLead[config.leadKey];
    let nextValue: string | boolean = '';

    if (config.inputType === 'checkbox') {
      nextValue = detailFieldDraftChecked;
      if (Boolean(currentValue) === nextValue) {
        setEditingDetailFieldId(null);
        return;
      }
    } else if (config.inputType === 'date') {
      nextValue = formatHtmlDateValueToUsStorage(detailFieldDraftValue);
      if (normalizeClientDateToUsStorage(String(currentValue ?? '')) === nextValue) {
        setEditingDetailFieldId(null);
        return;
      }
    } else {
      nextValue = detailFieldDraftValue.trim();
      const currentTextValue = typeof currentValue === 'string' ? currentValue : '';
      if (!['businessUnit', 'workPhone', 'cellPhone'].includes(fieldId) && currentTextValue === nextValue) {
        setEditingDetailFieldId(null);
        return;
      }
    }

    let updatePayload: Record<string, string | boolean> = { [config.leadKey]: nextValue };
    let nextLeadPatch: Partial<Lead> = { [config.leadKey]: nextValue } as Partial<Lead>;

    if (fieldId === 'organization') {
      const nextOrgName = String(nextValue || '').trim();
      const existingAccount = nextOrgName ? accountNameMap.get(nextOrgName.toLowerCase()) : null;
      let nextAccountId = '';

      if (nextOrgName) {
        if (existingAccount?.id) {
          nextAccountId = String(existingAccount.id);
        } else {
          const created = await createAccount({ name: nextOrgName }).unwrap();
          const createdAccount = (created?.data?.account || created?.account || null);
          nextAccountId = createdAccount?.id ? String(createdAccount.id) : '';
          refetchAccounts();
        }
      }

      updatePayload = { organization: nextOrgName, accountId: nextAccountId || '' };
      nextLeadPatch = { organization: nextOrgName, accountId: nextAccountId || '' };
      nextValue = nextOrgName;
    }

    if (fieldId === 'businessUnit') {
      const currentBusinessUnit = (typeof currentValue === 'string' ? currentValue.trim() : '') || (selectedLead.inStore ? 'In-Store' : '');
      const nextBusinessUnit = String(nextValue || '').trim();
      const nextInStore = nextBusinessUnit.toLowerCase() === 'in-store' || nextBusinessUnit.toLowerCase() === 'in store';
      const currentInStore = Boolean(selectedLead.inStore);

      if (currentBusinessUnit === nextBusinessUnit && currentInStore === nextInStore) {
        setEditingDetailFieldId(null);
        return;
      }

      updatePayload = { businessUnit: nextBusinessUnit, inStore: nextInStore };
      nextLeadPatch = { businessUnit: nextBusinessUnit, inStore: nextInStore };
      nextValue = nextBusinessUnit;
    }

    if (fieldId === 'workPhone') {
      const currentWorkPhone = getWorkPhoneFromLead(selectedLead);
      const nextWorkPhone = String(nextValue || '').trim();

      if (currentWorkPhone === nextWorkPhone) {
        setEditingDetailFieldId(null);
        return;
      }

      updatePayload = { workPhone: nextWorkPhone };
      nextLeadPatch = { workPhone: nextWorkPhone };

      const legacyPhoneType = String(selectedLead.phoneType || '').trim().toLowerCase();
      if (!legacyPhoneType || legacyPhoneType === 'work') {
        updatePayload.phone = nextWorkPhone;
        updatePayload.phoneType = nextWorkPhone ? 'Work' : '';
        nextLeadPatch.phone = nextWorkPhone;
        nextLeadPatch.phoneType = nextWorkPhone ? 'Work' : '';
      }
    }

    if (fieldId === 'cellPhone') {
      const currentCellPhone = getCellPhoneFromLead(selectedLead);
      const nextCellPhone = String(nextValue || '').trim();

      if (currentCellPhone === nextCellPhone) {
        setEditingDetailFieldId(null);
        return;
      }

      updatePayload = { cellPhone: nextCellPhone };
      nextLeadPatch = { cellPhone: nextCellPhone };
    }

    if (fieldId === 'owner') {
      const currentOwner = String(currentValue || '').trim();
      const currentVisibility = selectedLead.visibility === 'personal' ? 'personal' : 'global';
      const selectedOwnerRaw = String(nextValue || '').trim() || DEFAULT_OWNER_FILTER_LABEL;
      const selectedOwnerIsEveryone = normalizeOwnerKey(selectedOwnerRaw) === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL);

      const normalizedCurrentOwnerForComparison = normalizeOwnerKey(
        currentOwner || (currentVisibility === 'global' ? DEFAULT_OWNER_FILTER_LABEL : '')
      );
      const nextOwner = selectedOwnerIsEveryone
        ? ''
        : selectedOwnerRaw.replace(/\(\s*you\s*\)/i, '').trim();
      const nextVisibility = selectedOwnerIsEveryone ? 'global' : currentVisibility;
      const normalizedNextOwnerForComparison = normalizeOwnerKey(nextOwner || DEFAULT_OWNER_FILTER_LABEL);

      if (
        normalizedCurrentOwnerForComparison === normalizedNextOwnerForComparison
        && currentVisibility === nextVisibility
      ) {
        setEditingDetailFieldId(null);
        return;
      }

      updatePayload = { owner: nextOwner, visibility: nextVisibility };
      nextLeadPatch = { owner: nextOwner, visibility: nextVisibility };
      nextValue = nextOwner;
    }

    if (fieldId === 'visibility') {
      const currentVisibility = selectedLead.visibility === 'personal' ? 'personal' : 'global';
      const nextVisibility = String(nextValue || '').trim().toLowerCase() === 'personal' ? 'personal' : 'global';

      if (currentVisibility === nextVisibility) {
        setEditingDetailFieldId(null);
        return;
      }

      updatePayload = { visibility: nextVisibility };
      nextLeadPatch = { visibility: nextVisibility };
      nextValue = nextVisibility;
    }

    try {
      const updateResult = await updateLead({
        leadId: selectedLead.id,
        ...updatePayload
      }).unwrap();

      const pr = updateResult as { data?: { lead?: Lead }; lead?: Lead };
      const serverLead = pr?.data?.lead ?? pr?.lead;
      const mergedBase: Lead =
        serverLead && serverLead.id === selectedLead.id
          ? { ...selectedLead, ...serverLead }
          : { ...selectedLead };
      setSelectedLead({ ...mergedBase, ...nextLeadPatch });
      setEditingDetailFieldId(null);
      setHoveredField(null);
      refetchLeads();
    } catch (error: any) {
      console.error(`Error updating ${String(config.leadKey)}:`, error);
      alert(`Failed to update ${config.placeholder}: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleAddField = (fieldId: string) => {
    const newAddedFields = new Set(addedFields);
    newAddedFields.add(fieldId);
    setAddedFields(newAddedFields);
    if (fieldId === 'expectedCloseDate') {
      setExpectedCloseDateDraft(toDateInputValue(selectedLead?.expectedCloseDate));
      setIsEditingExpectedCloseDate(true);
    }
  };

  const hasTextValue = (value: unknown): boolean => {
    if (typeof value === 'string') return value.trim().length > 0;
    return Boolean(value);
  };

  const detailsFieldHasData = (fieldId: string): boolean => {
    if (!selectedLead) return false;

    if (fieldId === 'addToSequence') return false;
    if (fieldId === 'labels') return Array.isArray(selectedLead.labels) && selectedLead.labels.length > 0;
    if (fieldId === 'value') return hasTextValue(selectedLead.value);
    if (fieldId === 'expectedCloseDate') return hasTextValue(selectedLead.expectedCloseDate);
    if (fieldId === 'businessUnit') return hasTextValue(selectedLead.businessUnit) || Boolean(selectedLead.inStore);
    if (fieldId === 'sourceChannel') return hasTextValue(selectedLead.sourceChannel);
    if (fieldId === 'sourceChannelId') return hasTextValue(selectedLead.sourceChannelId);
    if (fieldId === 'sourceOrigin') return hasTextValue(selectedLead.sourceOrigin);
    if (fieldId === 'owner') return hasTextValue(selectedLead.owner) || selectedLead.visibility === 'global';
    if (fieldId === 'visibility') return true;

    const editableConfig = detailEditableFieldConfig[fieldId];
    if (!editableConfig) return false;

    const rawValue = selectedLead[editableConfig.leadKey];
    if (editableConfig.inputType === 'checkbox') return Boolean(rawValue);
    return hasTextValue(rawValue);
  };

  const renderField = (field: typeof allFields[0]) => {
    // Skip rendering the "value" field - we have a custom implementation for it
    if (field.id === 'value') return null;
    
    const Icon = field.icon;
    const hasFieldData = detailsFieldHasData(field.id);
    const isFieldCurrentlyEditing = editingDetailFieldId === field.id;
    const shouldShowInCollapsed =
      hasFieldData
      || isFieldCurrentlyEditing
      || (field.id === 'expectedCloseDate' && isEditingExpectedCloseDate)
      || (field.id === 'value' && isEditingValueDetails)
      || (field.id === 'labels' && showLabelsModal)
      || (field.id === 'owner' && field.added)
      || (field.id === 'visibility' && field.added);
    const isVisible = showAllFields || shouldShowInCollapsed;
    if (!isVisible) return null;

    const isHovered = hoveredField === field.id;
    const showAddButton = !field.added && isHovered;

    // Special handling for labels field
    if ((field as any).isLabels && field.added && selectedLead) {
      return (
        <Dropdown 
          key={field.id}
          show={showLabelsModal} 
          onToggle={(isOpen) => {
            if (!isOpen && (showCreateLabelModal || showEditLabelModal)) {
              return;
            }
            setShowLabelsModal(isOpen);
            if (!isOpen) {
              setShowCreateLabelModal(false);
              setShowEditLabelModal(false);
            }
          }}
          className="mb-2"
          style={{ marginBottom: '0' }}
        >
          <Dropdown.Toggle
            as="div"
            variant="link"
            className="text-decoration-none labels-dropdown-toggle"
            style={{ 
              width: '100%',
              padding: '0.5rem',
              borderRadius: '4px',
              backgroundColor: isHoveringLabels ? '#f8f9fa' : 'transparent',
              transition: 'background-color 0.2s',
              cursor: isHoveringLabels ? 'pointer' : 'default',
              minHeight: '32px',
              border: 'none',
              textAlign: 'left'
            }}
            onMouseEnter={() => setIsHoveringLabels(true)}
            onMouseLeave={() => setIsHoveringLabels(false)}
          >
            <div className="d-flex align-items-center gap-2">
              <span style={detailFieldIconStyle}>
                <Icon size={14} />
              </span>
              {selectedLead?.labels && selectedLead.labels.length > 0 ? (
                <div className="d-flex flex-wrap gap-1 align-items-center">
                  {selectedLead.labels.map(label => (
                    <Badge key={label} bg={getLabelVariant(label)} style={{ fontSize: '0.55rem', padding: '0.15rem 0.35rem' }}>
                      {label}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span 
                  style={{ 
                    fontSize: '0.75rem', 
                    color: isHoveringLabels ? '#0d6efd' : '#6c757d',
                    fontWeight: isHoveringLabels ? '500' : 'normal'
                  }}
                >
                  {isHoveringLabels ? '+ Add Labels' : field.label}
                </span>
              )}
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu 
            show={showLabelsModal}
            style={{ 
              minWidth: '250px', 
              maxHeight: '400px', 
              overflowY: 'auto', 
              padding: '0.5rem',
              marginTop: '0.25rem'
            }}
          >
            {!showCreateLabelModal && !showEditLabelModal ? (
              <>
                <div className="mb-2">
                  <InputGroup size="sm">
                    <InputGroup.Text>
                      <Search size={14} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search label"
                      value={labelSearchQuery}
                      onChange={(e) => setLabelSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: '0.75rem' }}
                    />
                  </InputGroup>
                </div>
                {allLabels
                  .filter(label => label.toLowerCase().includes(labelSearchQuery.toLowerCase()))
                  .map(label => {
                    const isAssigned = includesLabelKey(selectedLead?.labels, label);
                    return (
                      <Dropdown.Item
                        key={label}
                        onMouseEnter={() => setHoveredLabel(label)}
                        onMouseLeave={() => setHoveredLabel(null)}
                        onClick={async (e) => {
                          if ((e.target as HTMLElement).closest('.label-edit-btn')) {
                            return;
                          }
                          if (!selectedLead) return;
                          
                          e.preventDefault();
                          e.stopPropagation();
                          
                          // Toggle label assignment
                          const currentLabels = selectedLead.labels || [];
                          let updatedLabels: string[];
                          const targetLabelKey = normalizeLabelKey(label);
                          
                          if (isAssigned) {
                            // Remove label
                            updatedLabels = currentLabels.filter((existingLabel) => normalizeLabelKey(existingLabel) !== targetLabelKey);
                          } else {
                            // Add label (case-insensitive unique)
                            updatedLabels = [
                              ...currentLabels.filter((existingLabel) => normalizeLabelKey(existingLabel) !== targetLabelKey),
                              label
                            ];
                          }
                          
                          try {
                            await updateLead({
                              leadId: selectedLead.id,
                              labels: updatedLabels
                            }).unwrap();
                            
                            // Update local state
                            setSelectedLead({ ...selectedLead, labels: updatedLabels });
                            
                            // Refetch leads to get updated list
                            refetchLeads();
                            
                            // Don't close the modal - allow multiple label selection
                            // setShowLabelsModal(false);
                          } catch (error: any) {
                            console.error('Error updating lead labels:', error);
                            alert(`Failed to update labels: ${error?.data?.message || error?.message || 'Unknown error'}`);
                          }
                        }}
                        style={{ fontSize: '0.75rem' }}
                      >
                        <div className="d-flex align-items-center justify-content-between w-100">
                          <div className="d-flex align-items-center">
                            {isAssigned && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                            <Badge bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}>{label}</Badge>
                          </div>
                          {hoveredLabel === label && (
                            <Button
                              variant="link"
                              className="label-edit-btn p-0 ms-2"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const currentColors = getLabelColors();
                                setEditingLabelOriginalName(label);
                                setEditingLabelName(label);
                                setEditingLabelColor(currentColors[label.toUpperCase()] || 'secondary');
                                setShowEditLabelModal(true);
                                setLabelSearchQuery('');
                              }}
                              style={{ color: 'inherit', textDecoration: 'none', padding: '0.25rem' }}
                            >
                              <Pencil size={14} />
                            </Button>
                          )}
                        </div>
                      </Dropdown.Item>
                    );
                  })}
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowCreateLabelModal(true);
                    setLabelSearchQuery('');
                  }}
                  style={{ fontSize: '0.75rem', color: '#0d6efd' }}
                >
                  <div className="d-flex align-items-center">
                    <Plus className="me-2" size={14} />
                    <span>Add new label</span>
                  </div>
                </Dropdown.Item>
              </>
            ) : showEditLabelModal ? (
              <div onClick={(e) => e.stopPropagation()}>
                <div className="d-flex align-items-center mb-3">
                  <Button
                    variant="link"
                    className="p-0 me-2"
                    onClick={() => {
                      setShowEditLabelModal(false);
                      setEditingLabelName('');
                      setEditingLabelOriginalName('');
                      setEditingLabelColor('secondary');
                    }}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    <ArrowLeft size={18} />
                  </Button>
                  <h6 style={{ fontSize: '0.875rem', fontWeight: 'bold', margin: 0, flex: 1 }}>Edit label</h6>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.5rem' }}>Label name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Label name"
                    value={editingLabelName}
                    onChange={(e) => setEditingLabelName(e.target.value)}
                    style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem' }}
                    autoFocus
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.5rem' }}>Label color</Form.Label>
                  <div className="d-flex gap-2 flex-wrap">
                    {[
                      { name: 'primary', color: '#0d6efd' },
                      { name: 'secondary', color: '#6c757d' },
                      { name: 'success', color: '#198754' },
                      { name: 'danger', color: '#dc3545' },
                      { name: 'warning', color: '#ffc107' },
                      { name: 'info', color: '#0dcaf0' },
                      { name: 'light', color: '#f8f9fa' },
                      { name: 'dark', color: '#212529' }
                    ].map((colorOption) => (
                      <button
                        key={colorOption.name}
                        type="button"
                        onClick={() => setEditingLabelColor(colorOption.name)}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          border: editingLabelColor === colorOption.name ? '3px solid #212529' : '2px solid #dee2e6',
                          backgroundColor: colorOption.color,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0
                        }}
                      >
                        {editingLabelColor === colorOption.name && (
                          <Check size={14} style={{ color: colorOption.name === 'light' ? '#212529' : '#fff' }} />
                        )}
                      </button>
                    ))}
                  </div>
                </Form.Group>
                <div className="d-flex justify-content-between align-items-center">
                  <Button
                    variant="link"
                    className="p-0"
                    onClick={() => {
                      void handleDeleteLabel(editingLabelOriginalName, { closeLabelsModal: true });
                    }}
                    style={{ color: '#dc3545', textDecoration: 'none', padding: '0.25rem' }}
                  >
                    <Trash size={16} />
                  </Button>
                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowEditLabelModal(false);
                        setEditingLabelName('');
                        setEditingLabelOriginalName('');
                        setEditingLabelColor('secondary');
                      }}
                      size="sm"
                      style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        if (editingLabelName.trim() && editingLabelOriginalName) {
                          const updatedLabelName = editingLabelName.trim();
                          const updatedLabelKey = normalizeLabelKey(updatedLabelName);
                          if (customLabels.includes(editingLabelOriginalName)) {
                            const updatedLabels = customLabels.map(l => l === editingLabelOriginalName ? updatedLabelName : l);
                            setCustomLabels(updatedLabels);
                            localStorage.setItem('customLabels', JSON.stringify(updatedLabels));
                          }
                          const storedColors = JSON.parse(localStorage.getItem('labelColors') || '{}');
                          delete storedColors[editingLabelOriginalName.toUpperCase()];
                          storedColors[updatedLabelKey] = editingLabelColor;
                          localStorage.setItem('labelColors', JSON.stringify(storedColors));
                          unhideLabelFromCatalog(updatedLabelName);
                          setShowEditLabelModal(false);
                          setEditingLabelName('');
                          setEditingLabelOriginalName('');
                          setEditingLabelColor('secondary');
                        }
                      }}
                      size="sm"
                      disabled={!editingLabelName.trim()}
                      style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div onClick={(e) => e.stopPropagation()}>
                <div className="d-flex align-items-center mb-3">
                  <Button
                    variant="link"
                    className="p-0 me-2"
                    onClick={() => {
                      setShowCreateLabelModal(false);
                      setNewLabelName('');
                      setNewLabelColor('secondary');
                    }}
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    <ArrowLeft size={18} />
                  </Button>
                  <h6 style={{ fontSize: '0.875rem', fontWeight: 'bold', margin: 0, flex: 1 }}>Create label</h6>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.5rem' }}>Label name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Label name"
                    value={newLabelName}
                    onChange={(e) => setNewLabelName(e.target.value)}
                    style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem' }}
                    autoFocus
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.5rem' }}>Label color</Form.Label>
                  <div className="d-flex gap-2 flex-wrap">
                    {[
                      { name: 'primary', color: '#0d6efd' },
                      { name: 'secondary', color: '#6c757d' },
                      { name: 'success', color: '#198754' },
                      { name: 'danger', color: '#dc3545' },
                      { name: 'warning', color: '#ffc107' },
                      { name: 'info', color: '#0dcaf0' },
                      { name: 'light', color: '#f8f9fa' },
                      { name: 'dark', color: '#212529' }
                    ].map((colorOption) => (
                      <button
                        key={colorOption.name}
                        type="button"
                        onClick={() => setNewLabelColor(colorOption.name)}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          border: newLabelColor === colorOption.name ? '3px solid #212529' : '2px solid #dee2e6',
                          backgroundColor: colorOption.color,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0
                        }}
                      >
                        {newLabelColor === colorOption.name && (
                          <Check size={14} style={{ color: colorOption.name === 'light' ? '#212529' : '#fff' }} />
                        )}
                      </button>
                    ))}
                  </div>
                </Form.Group>
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowCreateLabelModal(false);
                      setNewLabelName('');
                      setNewLabelColor('secondary');
                    }}
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => {
                      if (newLabelName.trim()) {
                        const newLabel = newLabelName.trim();
                        const newLabelKey = normalizeLabelKey(newLabel);
                        unhideLabelFromCatalog(newLabel);
                        if (!allLabels.some((label) => normalizeLabelKey(label) === newLabelKey)) {
                          const updatedLabels = [...customLabels, newLabel];
                          setCustomLabels(updatedLabels);
                          localStorage.setItem('customLabels', JSON.stringify(updatedLabels));
                          const storedColors = JSON.parse(localStorage.getItem('labelColors') || '{}');
                          storedColors[newLabelKey] = newLabelColor;
                          localStorage.setItem('labelColors', JSON.stringify(storedColors));
                        }
                        setShowCreateLabelModal(false);
                        setNewLabelName('');
                        setNewLabelColor('secondary');
                      }
                    }}
                    size="sm"
                    disabled={!newLabelName.trim()}
                    style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </Dropdown.Menu>
        </Dropdown>
      );
    }

    if (field.id === 'expectedCloseDate' && field.added && selectedLead) {
      const hasExpectedCloseDate = !!selectedLead.expectedCloseDate;
      const showExpectedCloseDateAdd = !hasExpectedCloseDate && isHoveringExpectedCloseDate && !isEditingExpectedCloseDate;

      return (
        <div
          key={field.id}
          className="mb-2"
          onMouseEnter={() => {
            if (!isEditingExpectedCloseDate) {
              setIsHoveringExpectedCloseDate(true);
            }
          }}
          onMouseLeave={() => {
            if (!isEditingExpectedCloseDate) {
              setIsHoveringExpectedCloseDate(false);
            }
          }}
          onClick={() => {
            if (!isEditingExpectedCloseDate) {
              setExpectedCloseDateDraft(toDateInputValue(selectedLead.expectedCloseDate));
              setIsEditingExpectedCloseDate(true);
            }
          }}
          style={{
            padding: '0.5rem',
            borderRadius: '4px',
            backgroundColor: isHoveringExpectedCloseDate && !isEditingExpectedCloseDate ? '#f8f9fa' : 'transparent',
            transition: 'background-color 0.2s',
            cursor: isHoveringExpectedCloseDate && !isEditingExpectedCloseDate ? 'pointer' : 'default',
            minHeight: '32px'
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={detailFieldIconStyle}>
              <Icon size={14} />
            </span>
            <span
              style={{
                fontSize: '0.75rem',
                color: showExpectedCloseDateAdd ? '#0d6efd' : (hasExpectedCloseDate ? 'inherit' : '#6c757d'),
                fontWeight: showExpectedCloseDateAdd ? 500 : 'normal'
              }}
            >
              {hasExpectedCloseDate ? selectedLead.expectedCloseDate : (showExpectedCloseDateAdd ? '+ Add value' : field.label)}
            </span>
          </div>
          {isEditingExpectedCloseDate && (
            <div className="d-flex align-items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
              <Form.Control
                type="date"
                value={expectedCloseDateDraft}
                onChange={(e) => setExpectedCloseDateDraft(e.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              />
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpectedCloseDateDraft('');
                  handleSaveExpectedCloseDate('');
                }}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
              >
                Clear
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpectedCloseDateDraft(toDateInputValue(selectedLead.expectedCloseDate));
                  setIsEditingExpectedCloseDate(false);
                }}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveExpectedCloseDate();
                }}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      );
    }

    if (field.id === 'businessUnit' && selectedLead) {
      const rawValue = (selectedLead.businessUnit || '').trim() || (selectedLead.inStore ? 'In-Store' : '');
      const hasValue = Boolean(rawValue);
      const isEditingThisField = editingDetailFieldId === field.id;
      const showAddPrompt = isHovered && !hasValue && !isEditingThisField;
      const displayValue = rawValue;

      return (
        <div
          key={field.id}
          className="mb-2"
          style={{
            cursor: 'pointer',
            opacity: field.added ? 1 : 0.5,
            padding: '0.5rem',
            borderRadius: '4px',
            backgroundColor: isHovered && !isEditingThisField ? '#f8f9fa' : 'transparent',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={() => setHoveredField(field.id)}
          onMouseLeave={() => setHoveredField(null)}
          onClick={() => {
            if (!field.added) {
              handleAddField(field.id);
            }
            startEditingDetailField(field.id);
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={detailFieldIconStyle}>
              <Icon size={14} />
            </span>
            <span
              style={{
                fontSize: '0.75rem',
                color: showAddPrompt ? '#0d6efd' : (hasValue ? 'inherit' : '#6c757d'),
                fontWeight: showAddPrompt ? 500 : 'normal'
              }}
            >
              {hasValue ? displayValue : (showAddPrompt ? '+ Add value' : field.label)}
            </span>
          </div>

          {isEditingThisField && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <Dropdown
                show={showBusinessUnitDropdown}
                onToggle={(isOpen) => {
                  setShowBusinessUnitDropdown(isOpen);
                  if (!isOpen) {
                    setBusinessUnitSearchQuery('');
                    resetBusinessUnitEditor();
                  }
                }}
              >
                <Dropdown.Toggle
                  as="div"
                  className="w-100"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowBusinessUnitDropdown((prev) => !prev);
                  }}
                >
                  <Form.Control
                    type="text"
                    readOnly
                    value={detailFieldDraftValue}
                    placeholder="Select business unit"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  show={showBusinessUnitDropdown}
                  style={{
                    minWidth: '320px',
                    maxHeight: '360px',
                    overflowY: 'auto',
                    padding: '0.5rem',
                    marginTop: '0.25rem'
                  }}
                >
                  {!showCreateBusinessUnit && !editingBusinessUnitOption ? (
                    <>
                      <div className="mb-2" onClick={(e) => e.stopPropagation()}>
                        <InputGroup size="sm">
                          <InputGroup.Text>
                            <Search size={14} />
                          </InputGroup.Text>
                          <Form.Control
                            placeholder="Search business unit"
                            value={businessUnitSearchQuery}
                            onChange={(e) => setBusinessUnitSearchQuery(e.target.value)}
                            style={{ fontSize: '0.75rem' }}
                          />
                        </InputGroup>
                      </div>

                      {filteredBusinessUnitOptions.length === 0 ? (
                        <div className="px-2 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                          No business units found
                        </div>
                      ) : (
                        filteredBusinessUnitOptions.map((option) => {
                          const isSelected = detailFieldDraftValue === option;
                          const showEditButton = hoveredBusinessUnitOption === option;
                          return (
                            <Dropdown.Item
                              key={option}
                              active={isSelected}
                              onMouseEnter={() => setHoveredBusinessUnitOption(option)}
                              onMouseLeave={() => setHoveredBusinessUnitOption(null)}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDetailFieldDraftValue(option);
                                setShowBusinessUnitDropdown(false);
                                setBusinessUnitSearchQuery('');
                              }}
                              style={{ fontSize: '0.75rem' }}
                            >
                              <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="d-flex align-items-center">
                                  {isSelected && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                                  <span>{option}</span>
                                </div>
                                {showEditButton && (
                                  <Button
                                    variant="link"
                                    className="p-0 ms-2"
                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setEditingBusinessUnitOption(option);
                                      setEditingBusinessUnitOptionDraft(option);
                                      setShowCreateBusinessUnit(false);
                                    }}
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                )}
                              </div>
                            </Dropdown.Item>
                          );
                        })
                      )}

                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowCreateBusinessUnit(true);
                          setNewBusinessUnitOption('');
                          setEditingBusinessUnitOption(null);
                          setEditingBusinessUnitOptionDraft('');
                        }}
                        style={{ fontSize: '0.75rem', color: '#0d6efd' }}
                      >
                        <div className="d-flex align-items-center">
                          <Plus className="me-2" size={14} />
                          <span>Add business unit</span>
                        </div>
                      </Dropdown.Item>
                    </>
                  ) : editingBusinessUnitOption ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <div className="d-flex align-items-center mb-2">
                        <Button
                          variant="link"
                          className="p-0 me-2"
                          onClick={() => {
                            setEditingBusinessUnitOption(null);
                            setEditingBusinessUnitOptionDraft('');
                          }}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          <ArrowLeft size={16} />
                        </Button>
                        <h6 style={{ fontSize: '0.85rem', fontWeight: 'bold', margin: 0 }}>Edit business unit</h6>
                      </div>
                      <Form.Control
                        type="text"
                        value={editingBusinessUnitOptionDraft}
                        onChange={(e) => setEditingBusinessUnitOptionDraft(e.target.value)}
                        placeholder="Business unit"
                        style={{ fontSize: '0.75rem', padding: '0.4rem 0.55rem' }}
                        autoFocus
                      />
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <Button
                          variant="link"
                          className="p-0"
                          style={{ color: '#dc3545', textDecoration: 'none' }}
                          onClick={() => {
                            if (!editingBusinessUnitOption) return;
                            handleDeleteBusinessUnitOption(editingBusinessUnitOption);
                            resetBusinessUnitEditor();
                          }}
                        >
                          <Trash size={16} />
                        </Button>
                        <div className="d-flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                            onClick={() => {
                              setEditingBusinessUnitOption(null);
                              setEditingBusinessUnitOptionDraft('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                            onClick={handleSaveEditedBusinessUnitOption}
                            disabled={!normalizeBusinessUnitOption(editingBusinessUnitOptionDraft)}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div onClick={(e) => e.stopPropagation()}>
                      <div className="d-flex align-items-center mb-2">
                        <Button
                          variant="link"
                          className="p-0 me-2"
                          onClick={() => {
                            setShowCreateBusinessUnit(false);
                            setNewBusinessUnitOption('');
                          }}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          <ArrowLeft size={16} />
                        </Button>
                        <h6 style={{ fontSize: '0.85rem', fontWeight: 'bold', margin: 0 }}>Add business unit</h6>
                      </div>
                      <Form.Control
                        type="text"
                        value={newBusinessUnitOption}
                        onChange={(e) => setNewBusinessUnitOption(e.target.value)}
                        placeholder="Business unit"
                        style={{ fontSize: '0.75rem', padding: '0.4rem 0.55rem' }}
                        autoFocus
                      />
                      <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                          onClick={() => {
                            setShowCreateBusinessUnit(false);
                            setNewBusinessUnitOption('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                          onClick={handleCreateBusinessUnitOption}
                          disabled={!normalizeBusinessUnitOption(newBusinessUnitOption)}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>

              <div className="d-flex align-items-center gap-2 mt-2">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setDetailFieldDraftValue('')}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                >
                  Clear
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setDetailFieldDraftValue((selectedLead.businessUnit || '').trim() || (selectedLead.inStore ? 'In-Store' : ''));
                    setEditingDetailFieldId(null);
                    setShowBusinessUnitDropdown(false);
                    setBusinessUnitSearchQuery('');
                    resetBusinessUnitEditor();
                  }}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    handleSaveDetailField(field.id);
                    setShowBusinessUnitDropdown(false);
                    setBusinessUnitSearchQuery('');
                    resetBusinessUnitEditor();
                  }}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (field.id === 'sourceChannel' && selectedLead) {
      const rawValue = selectedLead.sourceChannel;
      const hasValue = Boolean(typeof rawValue === 'string' ? rawValue.trim() : rawValue);
      const isEditingThisField = editingDetailFieldId === field.id;
      const showAddPrompt = isHovered && !hasValue && !isEditingThisField;
      const displayValue = typeof rawValue === 'string' ? rawValue : '';

      return (
        <div
          key={field.id}
          className="mb-2"
          style={{
            cursor: 'pointer',
            opacity: field.added ? 1 : 0.5,
            padding: '0.5rem',
            borderRadius: '4px',
            backgroundColor: isHovered && !isEditingThisField ? '#f8f9fa' : 'transparent',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={() => setHoveredField(field.id)}
          onMouseLeave={() => setHoveredField(null)}
          onClick={() => {
            if (!field.added) {
              handleAddField(field.id);
            }
            startEditingDetailField(field.id);
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={detailFieldIconStyle}>
              <Icon size={14} />
            </span>
            <span
              style={{
                fontSize: '0.75rem',
                color: showAddPrompt ? '#0d6efd' : (hasValue ? 'inherit' : '#6c757d'),
                fontWeight: showAddPrompt ? 500 : 'normal'
              }}
            >
              {hasValue ? displayValue : (showAddPrompt ? '+ Add value' : field.label)}
            </span>
          </div>

          {isEditingThisField && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <Dropdown
                show={showSourceChannelDropdown}
                onToggle={(isOpen) => {
                  setShowSourceChannelDropdown(isOpen);
                  if (!isOpen) {
                    setSourceChannelSearchQuery('');
                    resetSourceChannelEditor();
                  }
                }}
              >
                <Dropdown.Toggle
                  as="div"
                  className="w-100"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSourceChannelDropdown((prev) => !prev);
                  }}
                >
                  <Form.Control
                    type="text"
                    readOnly
                    value={detailFieldDraftValue}
                    placeholder="Select source channel"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  show={showSourceChannelDropdown}
                  style={{
                    minWidth: '320px',
                    maxHeight: '360px',
                    overflowY: 'auto',
                    padding: '0.5rem',
                    marginTop: '0.25rem'
                  }}
                >
                  {!showCreateSourceChannel && !editingSourceChannelOption ? (
                    <>
                      <div className="mb-2" onClick={(e) => e.stopPropagation()}>
                        <InputGroup size="sm">
                          <InputGroup.Text>
                            <Search size={14} />
                          </InputGroup.Text>
                          <Form.Control
                            placeholder="Search source channel"
                            value={sourceChannelSearchQuery}
                            onChange={(e) => setSourceChannelSearchQuery(e.target.value)}
                            style={{ fontSize: '0.75rem' }}
                          />
                        </InputGroup>
                      </div>

                      {filteredSourceChannelOptions.length === 0 ? (
                        <div className="px-2 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                          No source channels found
                        </div>
                      ) : (
                        filteredSourceChannelOptions.map((option) => {
                          const isSelected = detailFieldDraftValue === option;
                          const showEditButton = hoveredSourceChannelOption === option;
                          return (
                            <Dropdown.Item
                              key={option}
                              active={isSelected}
                              onMouseEnter={() => setHoveredSourceChannelOption(option)}
                              onMouseLeave={() => setHoveredSourceChannelOption(null)}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDetailFieldDraftValue(option);
                                setShowSourceChannelDropdown(false);
                                setSourceChannelSearchQuery('');
                              }}
                              style={{ fontSize: '0.75rem' }}
                            >
                              <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="d-flex align-items-center">
                                  {isSelected && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                                  <span>{option}</span>
                                </div>
                                {showEditButton && (
                                  <Button
                                    variant="link"
                                    className="p-0 ms-2"
                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setEditingSourceChannelOption(option);
                                      setEditingSourceChannelOptionDraft(option);
                                      setShowCreateSourceChannel(false);
                                    }}
                                  >
                                    <Pencil size={14} />
                                  </Button>
                                )}
                              </div>
                            </Dropdown.Item>
                          );
                        })
                      )}

                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowCreateSourceChannel(true);
                          setNewSourceChannelOption('');
                          setEditingSourceChannelOption(null);
                          setEditingSourceChannelOptionDraft('');
                        }}
                        style={{ fontSize: '0.75rem', color: '#0d6efd' }}
                      >
                        <div className="d-flex align-items-center">
                          <Plus className="me-2" size={14} />
                          <span>Add source channel</span>
                        </div>
                      </Dropdown.Item>
                    </>
                  ) : editingSourceChannelOption ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <div className="d-flex align-items-center mb-2">
                        <Button
                          variant="link"
                          className="p-0 me-2"
                          onClick={() => {
                            setEditingSourceChannelOption(null);
                            setEditingSourceChannelOptionDraft('');
                          }}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          <ArrowLeft size={16} />
                        </Button>
                        <h6 style={{ fontSize: '0.85rem', fontWeight: 'bold', margin: 0 }}>Edit source channel</h6>
                      </div>
                      <Form.Control
                        type="text"
                        value={editingSourceChannelOptionDraft}
                        onChange={(e) => setEditingSourceChannelOptionDraft(e.target.value)}
                        placeholder="Source channel"
                        style={{ fontSize: '0.75rem', padding: '0.4rem 0.55rem' }}
                        autoFocus
                      />
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <Button
                          variant="link"
                          className="p-0"
                          style={{ color: '#dc3545', textDecoration: 'none' }}
                          onClick={() => {
                            if (!editingSourceChannelOption) return;
                            handleDeleteSourceChannelOption(editingSourceChannelOption);
                            resetSourceChannelEditor();
                          }}
                        >
                          <Trash size={16} />
                        </Button>
                        <div className="d-flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                            onClick={() => {
                              setEditingSourceChannelOption(null);
                              setEditingSourceChannelOptionDraft('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                            onClick={handleSaveEditedSourceChannelOption}
                            disabled={!normalizeSourceChannelOption(editingSourceChannelOptionDraft)}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div onClick={(e) => e.stopPropagation()}>
                      <div className="d-flex align-items-center mb-2">
                        <Button
                          variant="link"
                          className="p-0 me-2"
                          onClick={() => {
                            setShowCreateSourceChannel(false);
                            setNewSourceChannelOption('');
                          }}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          <ArrowLeft size={16} />
                        </Button>
                        <h6 style={{ fontSize: '0.85rem', fontWeight: 'bold', margin: 0 }}>Add source channel</h6>
                      </div>
                      <Form.Control
                        type="text"
                        value={newSourceChannelOption}
                        onChange={(e) => setNewSourceChannelOption(e.target.value)}
                        placeholder="Source channel"
                        style={{ fontSize: '0.75rem', padding: '0.4rem 0.55rem' }}
                        autoFocus
                      />
                      <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                          onClick={() => {
                            setShowCreateSourceChannel(false);
                            setNewSourceChannelOption('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                          onClick={handleCreateSourceChannelOption}
                          disabled={!normalizeSourceChannelOption(newSourceChannelOption)}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>

              <div className="d-flex align-items-center gap-2 mt-2">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setDetailFieldDraftValue('')}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                >
                  Clear
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setDetailFieldDraftValue(selectedLead.sourceChannel || '');
                    setEditingDetailFieldId(null);
                    setShowSourceChannelDropdown(false);
                    setSourceChannelSearchQuery('');
                    resetSourceChannelEditor();
                  }}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    handleSaveDetailField(field.id);
                    setShowSourceChannelDropdown(false);
                    setSourceChannelSearchQuery('');
                    resetSourceChannelEditor();
                  }}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    }

    const editableConfig = detailEditableFieldConfig[field.id];
    if (editableConfig && selectedLead) {
      const rawValue = selectedLead[editableConfig.leadKey];
      const ownerRawValue = typeof rawValue === 'string' ? rawValue.trim() : '';
      const ownerDefaultValue =
        selectedLead.visibility === 'global'
          ? DEFAULT_OWNER_FILTER_LABEL
          : (selfOwnerBaseLabel || DEFAULT_OWNER_FILTER_LABEL);
      const ownerDisplayValue = ownerRawValue || ownerDefaultValue;
      const visibilityDisplayValue = selectedLead.visibility === 'personal'
        ? 'Personal (private)'
        : 'Global (visible for everyone)';
      const hasValue = field.id === 'owner'
        ? ownerDisplayValue.length > 0
        : field.id === 'visibility'
        ? true
        : editableConfig.inputType === 'checkbox'
        ? Boolean(rawValue)
        : Boolean(typeof rawValue === 'string' ? rawValue.trim() : rawValue);
      const isEditingThisField = editingDetailFieldId === field.id;
      const showAddPrompt = isHovered && !hasValue && !isEditingThisField;
      const displayValue = editableConfig.inputType === 'checkbox'
        ? (Boolean(rawValue) ? field.label : '')
        : field.id === 'owner'
        ? ownerDisplayValue
        : field.id === 'visibility'
        ? visibilityDisplayValue
        : (typeof rawValue === 'string' ? rawValue : '');
      const ownerDetailEditorOptions = field.id === 'owner'
        ? (() => {
            const options = [...ownerOptions];
            const normalizedCurrentOwner = normalizeOwnerKey(ownerRawValue);
            if (ownerRawValue && !options.some((option) => option.label === ownerRawValue)) {
              const fallbackKey = normalizedCurrentOwner || normalizeOwnerKey(`owner-current-${ownerRawValue}`);
              options.push({ key: fallbackKey, label: ownerRawValue });
            }
            return options;
          })()
        : [];

      return (
        <div
          key={field.id}
          className="mb-2"
          style={{
            cursor: 'pointer',
            opacity: field.added ? 1 : 0.5,
            padding: '0.5rem',
            borderRadius: '4px',
            backgroundColor: isHovered && !isEditingThisField ? '#f8f9fa' : 'transparent',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={() => setHoveredField(field.id)}
          onMouseLeave={() => setHoveredField(null)}
          onClick={() => {
            if (!field.added) {
              handleAddField(field.id);
            }
            startEditingDetailField(field.id);
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={detailFieldIconStyle}>
              <Icon size={14} />
            </span>
            <span
              style={{
                fontSize: '0.75rem',
                color: showAddPrompt ? '#0d6efd' : (hasValue ? 'inherit' : '#6c757d'),
                fontWeight: showAddPrompt ? 500 : 'normal'
              }}
            >
              {hasValue ? displayValue : (showAddPrompt ? '+ Add value' : field.label)}
              {field.hasInfo && <InfoCircle className="ms-1" size={9} />}
            </span>
          </div>
          {isEditingThisField && (
            <div className="d-flex align-items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
              {field.id === 'owner' ? (
                <Form.Select
                  value={detailFieldDraftValue || DEFAULT_OWNER_FILTER_LABEL}
                  onChange={(e) => setDetailFieldDraftValue(e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                  autoFocus
                >
                  {ownerDetailEditorOptions.map((option) => (
                    <option key={`lead-detail-owner-option-${option.key}`} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              ) : field.id === 'visibility' ? (
                <Form.Select
                  value={detailFieldDraftValue || (selectedLead.visibility === 'personal' ? 'personal' : 'global')}
                  onChange={(e) => setDetailFieldDraftValue(e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                  autoFocus
                >
                  <option value="global">Global (visible for everyone)</option>
                  <option value="personal">Personal (private)</option>
                </Form.Select>
              ) : editableConfig.inputType === 'checkbox' ? (
                <Form.Check
                  type="checkbox"
                  checked={detailFieldDraftChecked}
                  onChange={(e) => setDetailFieldDraftChecked(e.target.checked)}
                  label={<span style={{ fontSize: '0.75rem' }}>{field.label}</span>}
                />
              ) : (
                <Form.Control
                  type={editableConfig.inputType === 'date' ? 'date' : 'text'}
                  value={detailFieldDraftValue}
                  placeholder={editableConfig.placeholder}
                  onChange={(e) => setDetailFieldDraftValue(e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                  autoFocus
                />
              )}
              {(editableConfig.inputType === 'text' || editableConfig.inputType === 'date') && field.id !== 'owner' && field.id !== 'visibility' && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (editableConfig.inputType === 'date') {
                      setDetailFieldDraftValue('');
                    } else {
                      setDetailFieldDraftValue('');
                    }
                  }}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                >
                  Clear
                </Button>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingDetailFieldId(null);
                }}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveDetailField(field.id);
                }}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        key={field.id}
        className="mb-2 d-flex align-items-center gap-2"
        style={{
          cursor: !field.added ? 'pointer' : 'default',
          opacity: field.added ? 1 : 0.5,
          padding: '0.5rem',
          borderRadius: '4px'
        }}
        onMouseEnter={() => !field.added && setHoveredField(field.id)}
        onMouseLeave={() => setHoveredField(null)}
        onClick={() => !field.added && handleAddField(field.id)}
      >
        <span style={detailFieldIconStyle}>
          <Icon size={14} />
        </span>
        {showAddButton ? (
          <span style={{ fontSize: '0.75rem', color: '#0d6efd' }}>+ Add value</span>
        ) : (
          <span style={{ fontSize: '0.75rem', color: field.added ? 'inherit' : '#6c757d' }}>
            {field.label}
            {field.hasInfo && <InfoCircle className="ms-1" size={9} />}
          </span>
        )}
      </div>
    );
  };

  const ownerOptions = useMemo(() => {
    const ownerMap = new Map<string, string>();
    ownerMap.set(normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL), DEFAULT_OWNER_FILTER_LABEL);

    (leads as Lead[]).forEach((lead) => {
      const ownerLabel = String(lead.owner || '').trim();
      if (!ownerLabel) return;
      ownerMap.set(normalizeOwnerKey(ownerLabel), ownerLabel);
    });

    // Always keep the signed-in user visible with "(you)" label.
    ownerMap.set(normalizeOwnerKey(selfOwnerFilterLabel), selfOwnerFilterLabel);

    const everyoneKey = normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL);
    const options = Array.from(ownerMap.entries()).map(([key, label]) => ({ key, label }));
    const everyone = options.find((option) => option.key === everyoneKey);
    const rest = options
      .filter((option) => option.key !== everyoneKey)
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return everyone ? [everyone, ...rest] : rest;
  }, [leads, selfOwnerFilterLabel]);

  const selectedOwnerButtonLabel = useMemo(() => {
    const normalizedSelected = normalizeOwnerKey(selectedOwner);
    if (!selectedOwner || normalizedSelected === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) {
      return DEFAULT_OWNER_FILTER_LABEL;
    }
    const cleanedOwnerLabel = String(selectedOwner)
      .replace(/\(\s*you\s*\)/i, '')
      .trim();
    return cleanedOwnerLabel || DEFAULT_OWNER_FILTER_LABEL;
  }, [selectedOwner]);

  const normalizedOwnerFilterSearchQuery = ownerFilterSearchQuery.trim().toLowerCase();

  const allVisibilityFilterOptions = useMemo<LeadVisibilityFilterOption[]>(() => {
    const normalizedSaved = [...savedVisibilityFilters]
      .sort((a, b) => {
        const aTs = new Date(a.createdAt).getTime();
        const bTs = new Date(b.createdAt).getTime();
        if (Number.isNaN(aTs) && Number.isNaN(bTs)) return 0;
        if (Number.isNaN(aTs)) return 1;
        if (Number.isNaN(bTs)) return -1;
        return bTs - aTs;
      })
      .map((filter) => ({
        id: filter.id,
        label: filter.label,
        description: filter.description,
      }));

    return [...LEAD_VISIBILITY_FILTER_OPTIONS, ...normalizedSaved];
  }, [savedVisibilityFilters]);

  const visibleOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return ownerOptions;
    return ownerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [ownerOptions, normalizedOwnerFilterSearchQuery]);

  const visibleVisibilityFilterOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return allVisibilityFilterOptions;
    return allVisibilityFilterOptions.filter((option) => {
      const labelText = option.label.toLowerCase();
      const descriptionText = option.description.toLowerCase();
      return labelText.includes(normalizedOwnerFilterSearchQuery) || descriptionText.includes(normalizedOwnerFilterSearchQuery);
    });
  }, [allVisibilityFilterOptions, normalizedOwnerFilterSearchQuery]);

  const favoriteOwnerOptions = useMemo(() => {
    const favoriteKeySet = new Set(favoriteOwnerKeys.map((entry) => normalizeOwnerKey(entry)));
    return ownerOptions.filter((option) => favoriteKeySet.has(option.key));
  }, [ownerOptions, favoriteOwnerKeys]);

  const favoriteVisibilityFilterOptions = useMemo(() => {
    const favoriteKeySet = new Set(favoriteVisibilityFilterIds.map((entry) => String(entry || '').trim()));
    return allVisibilityFilterOptions.filter((option) => favoriteKeySet.has(option.id));
  }, [allVisibilityFilterOptions, favoriteVisibilityFilterIds]);

  const visibleFavoriteOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return favoriteOwnerOptions;
    return favoriteOwnerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [favoriteOwnerOptions, normalizedOwnerFilterSearchQuery]);

  const visibleFavoriteVisibilityFilterOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return favoriteVisibilityFilterOptions;
    return favoriteVisibilityFilterOptions.filter((option) => {
      const labelText = option.label.toLowerCase();
      const descriptionText = option.description.toLowerCase();
      return labelText.includes(normalizedOwnerFilterSearchQuery) || descriptionText.includes(normalizedOwnerFilterSearchQuery);
    });
  }, [favoriteVisibilityFilterOptions, normalizedOwnerFilterSearchQuery]);

  useEffect(() => {
    if (selectedOwner === DEFAULT_OWNER_FILTER_LABEL) return;
    const selectedKey = normalizeOwnerKey(selectedOwner);
    if (!ownerOptions.some((option) => option.key === selectedKey)) {
      setSelectedOwner(DEFAULT_OWNER_FILTER_LABEL);
    }
  }, [ownerOptions, selectedOwner]);

  useEffect(() => {
    if (selectedVisibilityFilterId === 'all') return;
    if (!allVisibilityFilterOptions.some((option) => option.id === selectedVisibilityFilterId)) {
      setSelectedVisibilityFilterId('all');
    }
  }, [allVisibilityFilterOptions, selectedVisibilityFilterId]);

  useEffect(() => {
    const validFilterIds = new Set(allVisibilityFilterOptions.map((option) => option.id));
    setFavoriteVisibilityFilterIds((prev) => {
      const normalizedPrev = Array.from(new Set(prev.map((entry) => String(entry || '').trim()).filter(Boolean)));
      const pruned = normalizedPrev.filter((entry) => entry !== 'all' && validFilterIds.has(entry));
      if (pruned.length === normalizedPrev.length && pruned.every((entry, idx) => entry === normalizedPrev[idx])) {
        return prev;
      }
      return pruned;
    });
  }, [allVisibilityFilterOptions]);

  const toggleFavoriteOwner = (ownerKey: string) => {
    const normalizedKey = normalizeOwnerKey(ownerKey);
    if (!normalizedKey || normalizedKey === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return;
    setFavoriteOwnerKeys((prev) => {
      const normalizedPrev = Array.from(new Set(prev.map((entry) => normalizeOwnerKey(entry)).filter(Boolean)));
      if (normalizedPrev.includes(normalizedKey)) {
        return normalizedPrev.filter((entry) => entry !== normalizedKey);
      }
      return [...normalizedPrev, normalizedKey];
    });
  };

  const toggleFavoriteVisibilityFilter = (filterId: string) => {
    const normalizedId = String(filterId || '').trim();
    if (!normalizedId || normalizedId === 'all') return;
    if (!allVisibilityFilterOptions.some((option) => option.id === normalizedId)) return;
    setFavoriteVisibilityFilterIds((prev) => {
      const normalizedPrev = Array.from(new Set(prev.map((entry) => String(entry || '').trim()).filter(Boolean)));
      if (normalizedPrev.includes(normalizedId)) {
        return normalizedPrev.filter((entry) => entry !== normalizedId);
      }
      return [...normalizedPrev, normalizedId];
    });
  };

  const getCreateFilterFieldOptions = (scope: LeadFilterConditionScope) => {
    return LEAD_FILTER_FIELD_OPTIONS_BY_SCOPE[scope] || [];
  };

  const buildCreateFilterConditionRowsFromSaved = (
    conditions: LeadSavedFilterCondition[],
    options: { ensureAtLeastOne?: boolean } = {}
  ): LeadFilterConditionRow[] => {
    const rows = conditions.map((condition) => ({
      ...createLeadFilterConditionRow(condition.scope),
      field: condition.field,
    }));
    if (rows.length > 0) return rows;
    if (options.ensureAtLeastOne) return [createLeadFilterConditionRow('activity')];
    return [];
  };

  const resetCreateFilterModal = () => {
    setCreateFilterEditingId(null);
    setShowCreateFilterArchiveHint(true);
    setCreateFilterAllConditions([createLeadFilterConditionRow('activity')]);
    setCreateFilterAnyConditions([]);
    setCreateFilterName('Activity');
    setCreateFilterVisibility('private');
    setCreateFilterSaveColumns(false);
  };

  const handleOpenCreateFilterModal = () => {
    resetCreateFilterModal();
    setShowOwnerFilter(false);
    setShowCreateFilterModal(true);
  };

  const handleOpenEditSavedVisibilityFilterModal = (filterId: string) => {
    const targetFilter = savedVisibilityFilters.find((filter) => filter.id === filterId);
    if (!targetFilter) return;

    setCreateFilterEditingId(targetFilter.id);
    setShowCreateFilterArchiveHint(false);
    setCreateFilterAllConditions(
      buildCreateFilterConditionRowsFromSaved(targetFilter.allConditions, { ensureAtLeastOne: true })
    );
    setCreateFilterAnyConditions(buildCreateFilterConditionRowsFromSaved(targetFilter.anyConditions));
    setCreateFilterName(targetFilter.label);
    setCreateFilterVisibility(targetFilter.visibility);
    setCreateFilterSaveColumns(targetFilter.saveColumns);
    setShowOwnerFilter(false);
    setShowCreateFilterModal(true);
  };

  const handleCloseCreateFilterModal = () => {
    setShowCreateFilterModal(false);
    setCreateFilterEditingId(null);
  };

  const handleAddCreateFilterCondition = (group: 'all' | 'any') => {
    if (group === 'all') {
      setCreateFilterAllConditions((prev) => [...prev, createLeadFilterConditionRow('activity')]);
      return;
    }
    setCreateFilterAnyConditions((prev) => [...prev, createLeadFilterConditionRow('activity')]);
  };

  const handleRemoveCreateFilterCondition = (group: 'all' | 'any', conditionId: string) => {
    if (group === 'all') {
      setCreateFilterAllConditions((prev) => prev.filter((condition) => condition.id !== conditionId));
      return;
    }
    setCreateFilterAnyConditions((prev) => prev.filter((condition) => condition.id !== conditionId));
  };

  const handleUpdateCreateFilterConditionScope = (
    group: 'all' | 'any',
    conditionId: string,
    scope: LeadFilterConditionScope
  ) => {
    const updateFn = (conditions: LeadFilterConditionRow[]) =>
      conditions.map((condition) =>
        condition.id === conditionId
          ? {
              ...condition,
              scope,
              field: '',
            }
          : condition
      );
    if (group === 'all') {
      setCreateFilterAllConditions((prev) => updateFn(prev));
      return;
    }
    setCreateFilterAnyConditions((prev) => updateFn(prev));
  };

  const handleUpdateCreateFilterConditionField = (
    group: 'all' | 'any',
    conditionId: string,
    field: string
  ) => {
    const updateFn = (conditions: LeadFilterConditionRow[]) =>
      conditions.map((condition) =>
        condition.id === conditionId
          ? {
              ...condition,
              field,
            }
          : condition
      );
    if (group === 'all') {
      setCreateFilterAllConditions((prev) => updateFn(prev));
      return;
    }
    setCreateFilterAnyConditions((prev) => updateFn(prev));
  };

  const createFilterCanSave = useMemo(() => {
    if (!createFilterName.trim()) return false;
    if (createFilterAllConditions.length === 0) return false;
    const allConditionsValid = createFilterAllConditions.every((condition) => String(condition.field || '').trim().length > 0);
    if (!allConditionsValid) return false;
    return createFilterAnyConditions.every((condition) => String(condition.field || '').trim().length > 0);
  }, [createFilterAllConditions, createFilterAnyConditions, createFilterName]);

  const handleDeleteSavedVisibilityFilter = (
    filterId: string,
    options: { skipConfirm?: boolean } = {}
  ): boolean => {
    if (isSystemVisibilityFilterId(filterId)) return false;
    const targetFilter = savedVisibilityFilters.find((filter) => filter.id === filterId);
    if (!targetFilter) return false;

    if (!options.skipConfirm) {
      const confirmed = window.confirm(`Delete filter "${targetFilter.label}"?`);
      if (!confirmed) return false;
    }

    setSavedVisibilityFilters((prev) => prev.filter((filter) => filter.id !== filterId));
    setFavoriteVisibilityFilterIds((prev) => prev.filter((entry) => String(entry || '').trim() !== filterId));
    if (selectedVisibilityFilterId === filterId) {
      setSelectedVisibilityFilterId('all');
    }
    if (createFilterEditingId === filterId) {
      setCreateFilterEditingId(null);
      setShowCreateFilterModal(false);
    }
    return true;
  };

  const isEditingCreateFilter = Boolean(createFilterEditingId);

  const handleSaveCreateFilter = () => {
    if (!createFilterCanSave) return;
    const normalizedName = createFilterName.trim();
    if (!normalizedName) return;

    const normalizedAllConditions: LeadSavedFilterCondition[] = createFilterAllConditions
      .map((condition) => normalizeLeadSavedFilterCondition(condition))
      .filter((condition: LeadSavedFilterCondition | null): condition is LeadSavedFilterCondition => Boolean(condition));
    const normalizedAnyConditions: LeadSavedFilterCondition[] = createFilterAnyConditions
      .map((condition) => normalizeLeadSavedFilterCondition(condition))
      .filter((condition: LeadSavedFilterCondition | null): condition is LeadSavedFilterCondition => Boolean(condition));

    if (normalizedAllConditions.length === 0) return;

    const nextDescription = buildLeadSavedFilterDescription(normalizedAllConditions, normalizedAnyConditions);
    let nextSelectedFilterId = '';

    if (createFilterEditingId) {
      const targetFilter = savedVisibilityFilters.find((filter) => filter.id === createFilterEditingId);
      if (!targetFilter) return;
      nextSelectedFilterId = targetFilter.id;
      const updatedFilter: LeadSavedVisibilityFilter = {
        ...targetFilter,
        label: normalizedName,
        description: nextDescription,
        visibility: createFilterVisibility,
        saveColumns: createFilterSaveColumns,
        allConditions: normalizedAllConditions,
        anyConditions: normalizedAnyConditions,
      };
      setSavedVisibilityFilters((prev) =>
        prev.map((filter) => (filter.id === createFilterEditingId ? updatedFilter : filter))
      );
    } else {
      const nextFilterId = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const nextFilter: LeadSavedVisibilityFilter = {
        id: nextFilterId,
        label: normalizedName,
        description: nextDescription,
        visibility: createFilterVisibility,
        saveColumns: createFilterSaveColumns,
        allConditions: normalizedAllConditions,
        anyConditions: normalizedAnyConditions,
        createdAt: new Date().toISOString(),
      };
      nextSelectedFilterId = nextFilterId;
      setSavedVisibilityFilters((prev) => [nextFilter, ...prev]);
    }

    setSelectedVisibilityFilterId(nextSelectedFilterId);
    setOwnerFilterTab('filters');
    setOwnerFilterSearchQuery('');
    setCreateFilterEditingId(null);
    setShowCreateFilterModal(false);
    setShowOwnerFilter(true);
  };

  const filteredLeads = useMemo(() => {
    let filtered = leads;

    if (leadMailboxView === 'archive') {
      filtered = filtered.filter((lead: Lead) => isLeadArchived(lead));
    } else {
      filtered = filtered.filter((lead: Lead) => !isLeadArchived(lead));
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((lead: Lead) =>
        lead.name.toLowerCase().includes(query) ||
        lead.title.toLowerCase().includes(query) ||
        lead.organization.toLowerCase().includes(query)
      );
    }

    if (selectedLabel !== 'All labels') {
      filtered = filtered.filter((lead: Lead) => includesLabelKey(lead.labels, selectedLabel));
    }

    const selectedOwnerKey = normalizeOwnerKey(selectedOwner);
    if (selectedOwnerKey !== normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) {
      filtered = filtered.filter((lead: Lead) => {
        const leadOwner = String(lead.owner || '').trim();
        if (!leadOwner) return false;
        return normalizeOwnerKey(leadOwner) === selectedOwnerKey;
      });
    }

    if (selectedVisibilityFilterId !== 'all') {
      const selectedSavedFilter = savedVisibilityFilters.find((filter) => filter.id === selectedVisibilityFilterId);
      if (selectedSavedFilter) {
        filtered = filtered.filter((lead: Lead) => (
          doesLeadMatchSavedVisibilityFilter(lead, selectedSavedFilter, selfOwnerBaseLabel)
        ));
      } else {
        filtered = filtered.filter((lead: Lead) => (
          doesLeadMatchVisibilityFilter(lead, selectedVisibilityFilterId, selfOwnerBaseLabel)
        ));
      }
    }

    if (contractRenewalFilter !== 'all') {
      filtered = filtered.filter((lead: Lead) => {
        const days = getDaysUntilContractRenewalDate(lead.contractRenewalDate);
        if (days === null) return false;
        if (contractRenewalFilter === 'overdue') return days < 0;
        if (contractRenewalFilter === 'next30') return days >= 0 && days <= 30;
        if (contractRenewalFilter === 'next60') return days >= 0 && days <= 60;
        if (contractRenewalFilter === 'next90') return days >= 0 && days <= 90;
        return true;
      });
    }

    if (contractRenewalFilter !== 'all') {
      filtered = [...filtered].sort((a: Lead, b: Lead) => {
        const aDays = getDaysUntilContractRenewalDate(a.contractRenewalDate);
        const bDays = getDaysUntilContractRenewalDate(b.contractRenewalDate);
        if (aDays === null && bDays === null) return 0;
        if (aDays === null) return 1;
        if (bDays === null) return -1;
        return aDays - bDays;
      });
    }

    return filtered;
  }, [
    leads,
    leadMailboxView,
    searchQuery,
    selectedLabel,
    selectedOwner,
    selectedVisibilityFilterId,
    savedVisibilityFilters,
    selfOwnerBaseLabel,
    contractRenewalFilter,
  ]);

  const upcomingFocusActivities = useMemo(() => {
    if (!selectedLead) return [] as ScheduledMeetingActivity[];

    const now = Date.now();
    return (meetings as ScheduledMeetingActivity[])
      .filter((meeting) => meeting.leadId === selectedLead.id)
      .filter((meeting) => typeof meeting.startTime === 'string' && meeting.startTime.length > 0)
      .filter((meeting) => {
        const startMs = new Date(meeting.startTime).getTime();
        return !Number.isNaN(startMs) && startMs >= (now - (5 * 60 * 1000));
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [meetings, selectedLead]);

  const leadActivityTimeline = useMemo(() => {
    if (!selectedLead) return [] as ScheduledMeetingActivity[];

    return (meetings as ScheduledMeetingActivity[])
      .filter((meeting) => meeting.leadId === selectedLead.id)
      .filter((meeting) => typeof meeting.startTime === 'string' && meeting.startTime.length > 0)
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
  }, [meetings, selectedLead]);

  const leadEmailThreads = useMemo(
    () => buildEmailThreadsForEntity(leadEmailThreadsData, leadEmailAddress),
    [leadEmailThreadsData, leadEmailAddress]
  );

  const pinnedFocusNotes = useMemo(() => {
    return [...notes]
      .filter((note) => note.pinned)
      .sort((a, b) => {
        const aPinnedAt = new Date(a.pinnedAt || a.timestamp).getTime();
        const bPinnedAt = new Date(b.pinnedAt || b.timestamp).getTime();
        return bPinnedAt - aPinnedAt;
      });
  }, [notes]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedLeads(new Set(filteredLeads.map((lead: Lead) => lead.id)));
    } else {
      setSelectedLeads(new Set());
    }
  };

  const handleSelectLead = (leadId: string) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };

  const handleSaveName = async () => {
    if (!selectedLead || !editedName.trim()) {
      setIsEditingName(false);
      setEditedName(selectedLead?.name || '');
      return;
    }

    if (editedName.trim() === selectedLead.name) {
      setIsEditingName(false);
      return;
    }

    try {
      await updateLead({
        leadId: selectedLead.id,
        name: editedName.trim()
      }).unwrap();

      // Update local state
      setSelectedLead({ ...selectedLead, name: editedName.trim() });
      setIsEditingName(false);
      
      // Refetch leads to get updated list
      refetchLeads();
    } catch (error: any) {
      console.error('Error updating lead name:', error);
      alert(`Failed to update lead name: ${error?.data?.message || error?.message || 'Unknown error'}`);
      // Revert on error
      setEditedName(selectedLead.name);
      setIsEditingName(false);
    }
  };

  const handleSaveValue = async (value: string, currency: string) => {
    if (!selectedLead) {
      return;
    }

    try {
      await updateLead({
        leadId: selectedLead.id,
        value: value || undefined,
        currency: currency || undefined
      }).unwrap();

      // Update local state
      setSelectedLead({ ...selectedLead, value: value || undefined, currency: currency || undefined });
      
      // Close details value editing mode
      setIsEditingValueDetails(false);
      
      // Refetch leads to get updated list
      refetchLeads();
    } catch (error: any) {
      console.error('Error updating lead value:', error);
      alert(`Failed to update value: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleSaveExpectedCloseDate = async (draftOverride?: string) => {
    if (!selectedLead) return;

    const dateInput = draftOverride !== undefined ? draftOverride : expectedCloseDateDraft;
    const formattedDate = formatHtmlDateValueToUsStorage(dateInput);

    try {
      const updateResult = await updateLead({
        leadId: selectedLead.id,
        expectedCloseDate: formattedDate || ''
      }).unwrap();

      const pr = updateResult as { data?: { lead?: Lead }; lead?: Lead };
      const serverLead = pr?.data?.lead ?? pr?.lead;
      const mergedBase: Lead =
        serverLead && serverLead.id === selectedLead.id
          ? { ...selectedLead, ...serverLead }
          : { ...selectedLead };
      setSelectedLead({ ...mergedBase, expectedCloseDate: formattedDate || '' });
      setIsEditingExpectedCloseDate(false);
      setIsHoveringExpectedCloseDate(false);
      refetchLeads();
    } catch (error: any) {
      console.error('Error updating expected close date:', error);
      alert(`Failed to update expected close date: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const pickMergedTextValue = (preferred?: string, fallback?: string): string => {
    const preferredValue = String(preferred || '').trim();
    if (preferredValue.length > 0) return preferredValue;
    return String(fallback || '').trim();
  };

  const handleArchiveSelectedLead = () => {
    if (!selectedLead) return;

    const sourceLead = selectedLead;
    const currentlyArchived = isLeadArchived(sourceLead);
    const nextStatus = currentlyArchived ? 'Lead' : 'Archived';
    const confirmationMessage = currentlyArchived
      ? `Move "${sourceLead.name}" back to Inbox?`
      : `Archive "${sourceLead.name}"?`;

    const shouldProceed = window.confirm(confirmationMessage);
    if (!shouldProceed) return;

    updateLead({
      leadId: sourceLead.id,
      status: nextStatus,
    })
      .unwrap()
      .then(() => {
        const nextLead = { ...sourceLead, status: nextStatus };
        setSelectedLead(nextLead);
        if (
          (leadMailboxView === 'inbox' && nextStatus.toLowerCase() === 'archived')
          || (leadMailboxView === 'archive' && nextStatus.toLowerCase() !== 'archived')
        ) {
          setShowSidebar(false);
          setSelectedLead(null);
        }
        refetchLeads();
      })
      .catch((error: any) => {
        console.error('Error updating lead status:', error);
        alert(`Failed to update lead status: ${error?.data?.message || error?.message || 'Unknown error'}`);
      });
  };

  const handleDeleteSelectedFromList = async () => {
    const ids = Array.from(selectedLeads);
    if (ids.length === 0) return;
    const count = ids.length;
    const shouldDelete = window.confirm(
      count === 1 ? 'Delete the selected lead? This cannot be undone.' : `Delete ${count} selected leads? This cannot be undone.`
    );
    if (!shouldDelete) return;

    try {
      for (const id of ids) {
        await deleteLead(id).unwrap();
      }
      setSelectedLeads(new Set());
      if (selectedLead && ids.includes(selectedLead.id)) {
        setShowSidebar(false);
        setSelectedLead(null);
      }
      refetchLeads();
      alert(count === 1 ? 'Lead deleted.' : `${count} leads deleted.`);
    } catch (error: any) {
      alert(`Failed to delete: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteSelectedLead = async () => {
    if (!selectedLead) return;

    const shouldDelete = window.confirm(`Delete lead "${selectedLead.name}"? This cannot be undone.`);
    if (!shouldDelete) return;

    try {
      await deleteLead(selectedLead.id).unwrap();
      setSelectedLeads((prev) => {
        const next = new Set(prev);
        next.delete(selectedLead.id);
        return next;
      });
      setShowSidebar(false);
      setSelectedLead(null);
      refetchLeads();
    } catch (error: any) {
      console.error('Error deleting lead:', error);
      alert(`Failed to delete lead: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleMergeSelectedLead = async () => {
    if (!selectedLead) return;

    const mergeCandidates = leads.filter((lead: Lead) => lead.id !== selectedLead.id);
    if (mergeCandidates.length === 0) {
      alert('No other lead is available to merge with.');
      return;
    }

    const previewList = mergeCandidates
      .slice(0, 15)
      .map((lead: Lead, index: number) => `${index + 1}. ${lead.name}${lead.organization ? ` (${lead.organization})` : ''}`)
      .join('\n');
    const selectionRaw = window.prompt(
      `Merge "${selectedLead.name}" into which lead?\nEnter a number from this list (or paste a lead ID / exact name):\n\n${previewList}`
    );
    if (!selectionRaw) return;

    const selection = selectionRaw.trim();
    let targetLead: Lead | undefined;

    if (/^\d+$/.test(selection)) {
      const index = Number(selection) - 1;
      if (index >= 0 && index < mergeCandidates.length) {
        targetLead = mergeCandidates[index];
      }
    }

    if (!targetLead) {
      const normalizedSelection = selection.toLowerCase();
      targetLead = mergeCandidates.find(
        (lead: Lead) =>
          lead.id === selection
          || lead.name.toLowerCase() === normalizedSelection
      );
    }

    if (!targetLead) {
      alert('Could not find the target lead. Try entering a number from the list.');
      return;
    }

    const shouldMerge = window.confirm(
      `Merge "${selectedLead.name}" into "${targetLead.name}"?\n\nThis will keep the target lead and delete the current lead.`
    );
    if (!shouldMerge) return;

    const sourceLead = selectedLead;
    const targetLabels = Array.isArray(targetLead.labels) ? targetLead.labels : [];
    const sourceLabels = Array.isArray(sourceLead.labels) ? sourceLead.labels : [];
    const mergedLabels = Array.from(
      new Set(
        [...targetLabels, ...sourceLabels]
          .map((label) => String(label || '').trim())
          .filter(Boolean)
      )
    );
    const targetNotes = parseLeadNotes(targetLead.notes);
    const sourceNotes = parseLeadNotes(sourceLead.notes);
    const mergedNotes = targetNotes.length > 0 ? targetNotes : sourceNotes;

    try {
      await updateLead({
        leadId: targetLead.id,
        name: pickMergedTextValue(targetLead.name, sourceLead.name),
        title: pickMergedTextValue(targetLead.title, sourceLead.title),
        organization: pickMergedTextValue(targetLead.organization, sourceLead.organization),
        accountStage: pickMergedTextValue(targetLead.accountStage, sourceLead.accountStage),
        contactStage: pickMergedTextValue(targetLead.contactStage, sourceLead.contactStage),
        labels: mergedLabels,
        notes: mergedNotes,
        value: pickMergedTextValue(targetLead.value, sourceLead.value),
        currency: pickMergedTextValue(targetLead.currency, sourceLead.currency),
        expectedCloseDate: pickMergedTextValue(targetLead.expectedCloseDate, sourceLead.expectedCloseDate),
        businessUnit: pickMergedTextValue(targetLead.businessUnit, sourceLead.businessUnit),
        owner: pickMergedTextValue(targetLead.owner, sourceLead.owner),
        sourceOrigin: pickMergedTextValue(targetLead.sourceOrigin, sourceLead.sourceOrigin),
        sourceChannel: pickMergedTextValue(targetLead.sourceChannel, sourceLead.sourceChannel),
        sourceChannelId: pickMergedTextValue(targetLead.sourceChannelId, sourceLead.sourceChannelId),
        email: pickMergedTextValue(targetLead.email, sourceLead.email),
        phone: pickMergedTextValue(targetLead.phone, sourceLead.phone),
        phoneType: pickMergedTextValue(targetLead.phoneType, sourceLead.phoneType),
        emailType: pickMergedTextValue(targetLead.emailType, sourceLead.emailType),
        workPhone: pickMergedTextValue(targetLead.workPhone, sourceLead.workPhone),
        cellPhone: pickMergedTextValue(targetLead.cellPhone, sourceLead.cellPhone),
        assistantName: pickMergedTextValue(targetLead.assistantName, sourceLead.assistantName),
        assistantPhone: pickMergedTextValue(targetLead.assistantPhone, sourceLead.assistantPhone),
        assistantEmail: pickMergedTextValue(targetLead.assistantEmail, sourceLead.assistantEmail),
        address: pickMergedTextValue(targetLead.address, sourceLead.address),
        website: pickMergedTextValue(targetLead.website, sourceLead.website),
        linkedInProfile: pickMergedTextValue(targetLead.linkedInProfile, sourceLead.linkedInProfile),
        numberOfEmployees: pickMergedTextValue(targetLead.numberOfEmployees, sourceLead.numberOfEmployees),
        annualRevenue: pickMergedTextValue(targetLead.annualRevenue, sourceLead.annualRevenue),
        industry: pickMergedTextValue(targetLead.industry, sourceLead.industry),
        country: pickMergedTextValue(targetLead.country, sourceLead.country),
        businessType: pickMergedTextValue(targetLead.businessType, sourceLead.businessType),
        totalLocations: pickMergedTextValue(targetLead.totalLocations, sourceLead.totalLocations),
        rfpDate: pickMergedTextValue(targetLead.rfpDate, sourceLead.rfpDate),
        rfpExpirationDate: pickMergedTextValue(targetLead.rfpExpirationDate, sourceLead.rfpExpirationDate),
        securedLocations: pickMergedTextValue(targetLead.securedLocations, sourceLead.securedLocations),
        addedOnLinkedIn: pickMergedTextValue(targetLead.addedOnLinkedIn, sourceLead.addedOnLinkedIn),
        kkPlan: pickMergedTextValue(targetLead.kkPlan, sourceLead.kkPlan),
        goal: pickMergedTextValue(targetLead.goal, sourceLead.goal),
        found: pickMergedTextValue(targetLead.found, sourceLead.found),
        challenges: pickMergedTextValue(targetLead.challenges, sourceLead.challenges),
        timing: pickMergedTextValue(targetLead.timing, sourceLead.timing),
        dealScore: pickMergedTextValue(targetLead.dealScore, sourceLead.dealScore),
        dealSource: pickMergedTextValue(targetLead.dealSource, sourceLead.dealSource),
        eyeballs: pickMergedTextValue(targetLead.eyeballs, sourceLead.eyeballs),
        inStore: Boolean(targetLead.inStore || sourceLead.inStore),
      }).unwrap();

      await deleteLead(sourceLead.id).unwrap();
      setSelectedLeads((prev) => {
        const next = new Set(prev);
        next.delete(sourceLead.id);
        return next;
      });
      setSelectedLead(targetLead);
      refetchLeads();
      alert(`Merged "${sourceLead.name}" into "${targetLead.name}".`);
    } catch (error: any) {
      console.error('Error merging lead:', error);
      alert(`Failed to merge lead: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  // Rich text editor formatting functions
  const executeCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (noteEditorRef) {
      noteEditorRef.focus();
    }
  };

  const handleSaveNote = async () => {
    if (!selectedLead) {
      return;
    }

    // Get content from editor
    const content = noteEditorRef?.innerHTML || currentNoteContent;
    const textContent = noteEditorRef?.textContent || currentNoteContent.replace(/<[^>]*>/g, '');
    
    if (!textContent.trim()) {
      return;
    }

    const newNote: LeadNote = {
      id: Date.now().toString(),
      content: content,
      timestamp: new Date().toISOString(),
      author: 'PhiLL Kross',
      pinned: false
    };

    const nextNotes = [newNote, ...notes];

    try {
      await updateLead({
        leadId: selectedLead.id,
        notes: nextNotes,
      }).unwrap();

      setNotes(nextNotes);
      setSelectedLead((prev) => (prev ? { ...prev, notes: nextNotes } : prev));
      setCurrentNoteContent('');
      if (noteEditorRef) {
        noteEditorRef.innerHTML = '';
        noteEditorRef.setAttribute('data-placeholder', 'Take a note, @name...');
      }
      setIsNoteEditorFocused(false);
      refetchLeads();
    } catch (error: any) {
      console.error('Error saving note:', error);
      alert(`Failed to save note: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleTogglePinNote = async (noteId: string) => {
    if (!selectedLead) return;

    const targetNote = notes.find((note) => note.id === noteId);
    if (!targetNote) return;

    const shouldPin = !targetNote.pinned;
    const nowIso = new Date().toISOString();
    const nextNotes = notes.map((note) =>
      note.id === noteId
        ? {
            ...note,
            pinned: shouldPin,
            pinnedAt: shouldPin ? (note.pinnedAt || nowIso) : undefined,
          }
        : note
    );

    try {
      await updateLead({
        leadId: selectedLead.id,
        notes: nextNotes,
      }).unwrap();
      setNotes(nextNotes);
      setSelectedLead((prev) => (prev ? { ...prev, notes: nextNotes } : prev));
      refetchLeads();
    } catch (error: any) {
      console.error('Error updating pinned note state:', error);
      alert(`Failed to update note pin: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!selectedLead) return;

    const targetNote = notes.find((note) => note.id === noteId);
    if (!targetNote) return;

    const confirmed = window.confirm('Delete this note?');
    if (!confirmed) return;

    const nextNotes = notes.filter((note) => note.id !== noteId);

    try {
      await updateLead({
        leadId: selectedLead.id,
        notes: nextNotes,
      }).unwrap();
      setNotes(nextNotes);
      setSelectedLead((prev) => (prev ? { ...prev, notes: nextNotes } : prev));
      refetchLeads();
    } catch (error: any) {
      console.error('Error deleting note:', error);
      alert(`Failed to delete note: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleCancelNote = () => {
    setCurrentNoteContent('');
    if (noteEditorRef) {
      noteEditorRef.innerHTML = '';
      noteEditorRef.setAttribute('data-placeholder', 'Take a note, @name...');
    }
    setIsNoteEditorFocused(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const relativeDayDiff = getRelativeDayDifferenceInTimezone(date, new Date(), tenantTimezone);
    
    if (relativeDayDiff === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...timezoneOption })}`;
    } else if (relativeDayDiff === -1) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...timezoneOption })}`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', ...timezoneOption }) + 
             ' at ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...timezoneOption });
    }
  };

  const splitLeadName = (rawName: string): { firstName: string; lastName: string } => {
    const normalized = String(rawName || '').trim().replace(/\s+/g, ' ');
    if (!normalized) return { firstName: 'Converted', lastName: 'Lead' };
    const parts = normalized.split(' ');
    if (parts.length === 1) return { firstName: parts[0], lastName: 'Lead' };
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ') || 'Lead',
    };
  };

  const handleConvertSelectedLead = async (target: 'Contact' | 'Client' | 'Deal') => {
    if (!selectedLead || isConvertingLead) return;

    const leadId = selectedLead.id;
    const visibility = selectedLead.visibility === 'personal' ? 'personal' : 'global';
    const fallbackName = String(selectedLead.name || '').trim()
      || String(selectedLead.organization || '').trim()
      || String(selectedLead.email || '').trim()
      || 'Converted Lead';
    const phoneValue = String(selectedLead.phone || selectedLead.workPhone || selectedLead.cellPhone || '').trim();
    const parsedNotes = parseLeadNotes(selectedLead.notes);
    const notesText = parsedNotes
      .map((note) => String(note.content || '').trim())
      .filter(Boolean)
      .join('\n\n');

    setIsConvertingLead(true);
    try {
      if (target === 'Contact') {
        const { firstName, lastName } = splitLeadName(selectedLead.name || fallbackName);
        await createContact({
          firstName,
          lastName,
          title: selectedLead.title || undefined,
          organization: selectedLead.organization || undefined,
          email: selectedLead.email || undefined,
          phone: phoneValue || undefined,
          address: selectedLead.address || undefined,
          country: selectedLead.country || undefined,
          notes: parsedNotes,
          accountStage: selectedLead.accountStage || undefined,
          contactStage: selectedLead.contactStage || undefined,
          value: selectedLead.value || undefined,
          expectedCloseDate: selectedLead.expectedCloseDate || undefined,
          businessUnit: selectedLead.businessUnit || undefined,
          inStore: Boolean(selectedLead.inStore),
          businessType: selectedLead.businessType || undefined,
          totalLocations: selectedLead.totalLocations || undefined,
          rfpDate: selectedLead.rfpDate || undefined,
          rfpExpirationDate: selectedLead.rfpExpirationDate || undefined,
          securedLocations: selectedLead.securedLocations || undefined,
          industry: selectedLead.industry || undefined,
          addedOnLinkedIn: selectedLead.addedOnLinkedIn || undefined,
          kkPlan: selectedLead.kkPlan || undefined,
          goal: selectedLead.goal || undefined,
          found: selectedLead.found || undefined,
          challenges: selectedLead.challenges || undefined,
          timing: selectedLead.timing || undefined,
          dealScore: selectedLead.dealScore || undefined,
          dealSource: selectedLead.dealSource || undefined,
          eyeballs: selectedLead.eyeballs || undefined,
          sourceOrigin: selectedLead.sourceOrigin || undefined,
          sourceChannel: selectedLead.sourceChannel || undefined,
          sourceChannelId: selectedLead.sourceChannelId || undefined,
          linkedInProfile: selectedLead.linkedInProfile || undefined,
          numberOfEmployees: selectedLead.numberOfEmployees || undefined,
          annualRevenue: selectedLead.annualRevenue || undefined,
          visibility,
        }).unwrap();
      } else if (target === 'Client') {
        await createClient({
          name: fallbackName,
          organization: selectedLead.organization || undefined,
          accountStage: selectedLead.accountStage || undefined,
          contactStage: selectedLead.contactStage || undefined,
          industry: selectedLead.industry || undefined,
          website: selectedLead.website || undefined,
          address: selectedLead.address || undefined,
          country: selectedLead.country || undefined,
          phone: phoneValue || undefined,
          email: selectedLead.email || undefined,
          notes: notesText || undefined,
          status: 'current',
          value: selectedLead.value || undefined,
          expectedCloseDate: selectedLead.expectedCloseDate || undefined,
          businessUnit: selectedLead.businessUnit || undefined,
          inStore: Boolean(selectedLead.inStore),
          sourceOrigin: selectedLead.sourceOrigin || undefined,
          sourceChannel: selectedLead.sourceChannel || undefined,
          sourceChannelId: selectedLead.sourceChannelId || undefined,
          linkedInProfile: selectedLead.linkedInProfile || undefined,
          numberOfEmployees: selectedLead.numberOfEmployees || undefined,
          annualRevenue: selectedLead.annualRevenue || undefined,
          visibility,
        }).unwrap();
      } else {
        const dealName = String(selectedLead.name || '').trim()
          ? `${selectedLead.name}${selectedLead.organization ? ` - ${selectedLead.organization}` : ''}`
          : (selectedLead.organization || 'Converted Lead Deal');
        await createDeal({
          name: dealName,
          value: selectedLead.value || '0',
          currency: selectedLead.currency || 'US Dollar (USD)',
          expectedCloseDate: selectedLead.expectedCloseDate || undefined,
          leadId: selectedLead.id,
          notes: notesText || undefined,
          visibility,
        }).unwrap();
      }

      await deleteLead(leadId).unwrap();
      setSelectedLeads((prev) => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
      setShowSidebar(false);
      setSelectedLead(null);
      refetchLeads();
      alert(`Converted lead to ${target.toLowerCase()}.`);
    } catch (error: any) {
      alert(`Failed to convert lead to ${target.toLowerCase()}: ${error?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setIsConvertingLead(false);
    }
  };

  const handleSaveLead = async () => {
    try {
      const leadData = {
        name: formData.contactPerson || 'Unnamed',
        title: formData.title || '',
        organization: formData.organization || '',
        accountStage: formData.accountStage || '',
        contactStage: formData.contactStage || '',
        labels: formData.labels ? [formData.labels] : [],
        value: formData.value,
        currency: formData.currency,
        expectedCloseDate: formData.expectedCloseDate,
        sourceChannel: formData.sourceChannel,
        sourceChannelId: formData.sourceChannelId,
        businessUnit: formData.businessUnit,
        inStore: formData.businessUnit.trim().toLowerCase() === 'in-store' || formData.businessUnit.trim().toLowerCase() === 'in store',
        email: formData.email,
        phone: formData.phone,
        phoneType: formData.phoneType,
        emailType: formData.emailType,
        status: 'Lead',
        visibility: formData.visibility === 'personal' ? 'personal' : 'global',
        decisionMaker: Boolean(formData.decisionMaker)
      };

      await createLead(leadData).unwrap();
      
      // Reset form
      setFormData({
        contactPerson: '',
        organization: '',
        accountStage: '',
        contactStage: '',
        visibility: 'global',
        title: '',
        value: '',
        currency: 'US Dollar (USD)',
        labels: '',
        expectedCloseDate: '',
        sourceChannel: '',
        sourceChannelId: '',
        businessUnit: '',
        phone: '',
        phoneType: 'Work',
        email: '',
        emailType: 'Work',
        decisionMaker: false
      });

      // Close modal
      setShowAddLeadModal(false);
      
      // Refetch leads to get updated list
      refetchLeads();
    } catch (error: any) {
      console.error('Error creating lead:', error);
      alert(`Failed to create lead: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const persistSourceChannelOptions = async (nextOptions: string[]) => {
    const normalized = dedupeSourceChannelOptions(nextOptions);
    const optionsToPersist = normalized.length > 0 ? normalized : [...DEFAULT_SOURCE_CHANNEL_OPTIONS];
    const previousOptions = sourceChannelOptions;

    setSourceChannelOptions(optionsToPersist);

    try {
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: { crmSourceChannelOptions: optionsToPersist },
      }).unwrap();
    } catch (error: any) {
      console.error('Error saving source channel options:', error);
      setSourceChannelOptions(previousOptions);
      alert(`Failed to save source channel options: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const allSourceChannelOptions = useMemo(() => {
    const fromLeads = leads
      .map((lead: Lead) => lead.sourceChannel || '')
      .filter((value: string) => value.trim().length > 0);
    return dedupeSourceChannelOptions([...sourceChannelOptions, ...fromLeads]);
  }, [sourceChannelOptions, leads]);

  const filteredSourceChannelOptions = useMemo(() => {
    const query = sourceChannelSearchQuery.trim().toLowerCase();
    if (!query) return allSourceChannelOptions;
    return allSourceChannelOptions.filter((option) => option.toLowerCase().includes(query));
  }, [allSourceChannelOptions, sourceChannelSearchQuery]);

  const resetSourceChannelEditor = () => {
    setShowCreateSourceChannel(false);
    setNewSourceChannelOption('');
    setEditingSourceChannelOption(null);
    setEditingSourceChannelOptionDraft('');
    setHoveredSourceChannelOption(null);
  };

  const handleCreateSourceChannelOption = async () => {
    const normalized = normalizeSourceChannelOption(newSourceChannelOption);
    if (!normalized) return;

    const existingOption = allSourceChannelOptions.find(
      (option) => option.toLowerCase() === normalized.toLowerCase()
    );

    if (existingOption) {
      setDetailFieldDraftValue(existingOption);
      setShowCreateSourceChannel(false);
      setNewSourceChannelOption('');
      return;
    }

    await persistSourceChannelOptions([...allSourceChannelOptions, normalized]);
    setDetailFieldDraftValue(normalized);
    setShowCreateSourceChannel(false);
    setNewSourceChannelOption('');
  };

  const handleSaveEditedSourceChannelOption = async () => {
    if (!editingSourceChannelOption) return;

    const normalized = normalizeSourceChannelOption(editingSourceChannelOptionDraft);
    if (!normalized) return;

    const duplicateOption = allSourceChannelOptions.find(
      (option) =>
        option.toLowerCase() === normalized.toLowerCase()
        && option.toLowerCase() !== editingSourceChannelOption.toLowerCase()
    );

    if (duplicateOption) {
      setEditingSourceChannelOptionDraft(duplicateOption);
      return;
    }

    const updatedOptions = allSourceChannelOptions.map((option) =>
      option === editingSourceChannelOption ? normalized : option
    );
    await persistSourceChannelOptions(updatedOptions);

    if (detailFieldDraftValue === editingSourceChannelOption) {
      setDetailFieldDraftValue(normalized);
    }
    if (formData.sourceChannel === editingSourceChannelOption) {
      setFormData((prev) => ({ ...prev, sourceChannel: normalized }));
    }

    setEditingSourceChannelOption(null);
    setEditingSourceChannelOptionDraft('');
  };

  const handleDeleteSourceChannelOption = async (optionToDelete: string) => {
    const updatedOptions = allSourceChannelOptions.filter((option) => option !== optionToDelete);
    await persistSourceChannelOptions(updatedOptions);

    if (detailFieldDraftValue === optionToDelete) {
      setDetailFieldDraftValue('');
    }
    if (formData.sourceChannel === optionToDelete) {
      setFormData((prev) => ({ ...prev, sourceChannel: '' }));
    }

    if (editingSourceChannelOption === optionToDelete) {
      setEditingSourceChannelOption(null);
      setEditingSourceChannelOptionDraft('');
    }
  };

  const persistBusinessUnitOptions = async (nextOptions: string[]) => {
    const normalized = dedupeBusinessUnitOptions(nextOptions);
    const optionsToPersist = normalized.length > 0 ? normalized : [...DEFAULT_BUSINESS_UNIT_OPTIONS];
    const previousOptions = businessUnitOptions;

    setBusinessUnitOptions(optionsToPersist);

    try {
      await updateUserPreferences({
        ...preferencesTenantWriteSpread(tenantIdForPrefs),
        preferences: { crmBusinessUnitOptions: optionsToPersist },
      }).unwrap();
    } catch (error: any) {
      console.error('Error saving business unit options:', error);
      setBusinessUnitOptions(previousOptions);
      alert(`Failed to save business unit options: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const allBusinessUnitOptions = useMemo(() => {
    const fromLeads = leads.flatMap((lead: Lead) => {
      const values: string[] = [];
      if (lead.businessUnit && lead.businessUnit.trim().length > 0) {
        values.push(lead.businessUnit);
      }
      if (lead.inStore) {
        values.push('In-Store');
      }
      return values;
    });
    return dedupeBusinessUnitOptions([...businessUnitOptions, ...fromLeads]);
  }, [businessUnitOptions, leads]);

  const filteredBusinessUnitOptions = useMemo(() => {
    const query = businessUnitSearchQuery.trim().toLowerCase();
    if (!query) return allBusinessUnitOptions;
    return allBusinessUnitOptions.filter((option) => option.toLowerCase().includes(query));
  }, [allBusinessUnitOptions, businessUnitSearchQuery]);

  const resetBusinessUnitEditor = () => {
    setShowCreateBusinessUnit(false);
    setNewBusinessUnitOption('');
    setEditingBusinessUnitOption(null);
    setEditingBusinessUnitOptionDraft('');
    setHoveredBusinessUnitOption(null);
  };

  const handleCreateBusinessUnitOption = async () => {
    const normalized = normalizeBusinessUnitOption(newBusinessUnitOption);
    if (!normalized) return;

    const existingOption = allBusinessUnitOptions.find(
      (option) => option.toLowerCase() === normalized.toLowerCase()
    );

    if (existingOption) {
      setDetailFieldDraftValue(existingOption);
      setShowCreateBusinessUnit(false);
      setNewBusinessUnitOption('');
      return;
    }

    await persistBusinessUnitOptions([...allBusinessUnitOptions, normalized]);
    setDetailFieldDraftValue(normalized);
    setShowCreateBusinessUnit(false);
    setNewBusinessUnitOption('');
  };

  const handleSaveEditedBusinessUnitOption = async () => {
    if (!editingBusinessUnitOption) return;

    const normalized = normalizeBusinessUnitOption(editingBusinessUnitOptionDraft);
    if (!normalized) return;

    const duplicateOption = allBusinessUnitOptions.find(
      (option) =>
        option.toLowerCase() === normalized.toLowerCase()
        && option.toLowerCase() !== editingBusinessUnitOption.toLowerCase()
    );

    if (duplicateOption) {
      setEditingBusinessUnitOptionDraft(duplicateOption);
      return;
    }

    const updatedOptions = allBusinessUnitOptions.map((option) =>
      option === editingBusinessUnitOption ? normalized : option
    );
    await persistBusinessUnitOptions(updatedOptions);

    if (detailFieldDraftValue === editingBusinessUnitOption) {
      setDetailFieldDraftValue(normalized);
    }
    if (formData.businessUnit === editingBusinessUnitOption) {
      setFormData((prev) => ({ ...prev, businessUnit: normalized }));
    }

    setEditingBusinessUnitOption(null);
    setEditingBusinessUnitOptionDraft('');
  };

  const handleDeleteBusinessUnitOption = async (optionToDelete: string) => {
    const updatedOptions = allBusinessUnitOptions.filter((option) => option !== optionToDelete);
    await persistBusinessUnitOptions(updatedOptions);

    if (detailFieldDraftValue === optionToDelete) {
      setDetailFieldDraftValue('');
    }
    if (formData.businessUnit === optionToDelete) {
      setFormData((prev) => ({ ...prev, businessUnit: '' }));
    }

    if (editingBusinessUnitOption === optionToDelete) {
      setEditingBusinessUnitOption(null);
      setEditingBusinessUnitOptionDraft('');
    }
  };

  type PersonTextFieldId =
    | 'title'
    | 'contactStage'
    | 'email'
    | 'workPhone'
    | 'cellPhone'
    | 'assistantName'
    | 'assistantPhone'
    | 'assistantEmail';

  const getPersonTextFieldValue = (lead: Lead, fieldId: PersonTextFieldId): string => {
    switch (fieldId) {
      case 'title':
        return String(lead.title || '').trim();
      case 'contactStage':
        return String(lead.contactStage || '').trim();
      case 'email':
        return String(lead.email || '').trim();
      case 'workPhone':
        return getWorkPhoneFromLead(lead);
      case 'cellPhone':
        return getCellPhoneFromLead(lead);
      case 'assistantName':
        return String(lead.assistantName || '').trim();
      case 'assistantPhone':
        return String(lead.assistantPhone || '').trim();
      case 'assistantEmail':
        return String(lead.assistantEmail || '').trim();
      default:
        return '';
    }
  };

  const renderPersonTextField = (
    fieldId: PersonTextFieldId,
    label: string,
    Icon: React.ComponentType<{ size?: number; className?: string }>,
  ) => {
    if (!selectedLead) return null;

    const value = getPersonTextFieldValue(selectedLead, fieldId);
    const hasValue = value.length > 0;
    const isEditing = editingDetailFieldId === fieldId;
    const isHovered = hoveredField === fieldId;
    const showAddPrompt = isHovered && !hasValue && !isEditing;
    const shouldRender = fieldId === 'contactStage' || showAllPersonFields || hasValue || isEditing;
    if (!shouldRender) return null;

    const inputType = fieldId.toLowerCase().includes('email')
      ? 'email'
      : fieldId.toLowerCase().includes('phone')
        ? 'tel'
        : 'text';

    return (
      <div
        key={fieldId}
        className="mb-2"
        style={{
          cursor: 'pointer',
          opacity: 1,
          padding: '0.5rem',
          borderRadius: '4px',
          backgroundColor: isHovered && !isEditing ? '#f8f9fa' : 'transparent',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={() => setHoveredField(fieldId)}
        onMouseLeave={() => setHoveredField(null)}
        onClick={() => startEditingDetailField(fieldId)}
      >
        <div className="d-flex align-items-center gap-2">
          <span style={detailFieldIconStyle}>
            <Icon size={14} />
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              color: showAddPrompt ? '#0d6efd' : (hasValue ? 'inherit' : '#6c757d'),
              fontWeight: showAddPrompt ? 500 : 'normal'
            }}
          >
            {hasValue ? value : (showAddPrompt ? '+ Add value' : label)}
          </span>
        </div>

        {isEditing && (
          <div className="d-flex align-items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
            {fieldId === 'contactStage' ? (
              <Form.Select
                value={detailFieldDraftValue}
                onChange={(e) => setDetailFieldDraftValue(e.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="">Select</option>
                {contactStageOptions.map((stage) => (
                  <option key={`lead-contact-stage-${stage}`} value={stage}>
                    {stage}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                type={inputType}
                value={detailFieldDraftValue}
                placeholder={label}
                onChange={(e) => setDetailFieldDraftValue(e.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              />
            )}
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setDetailFieldDraftValue('');
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Clear
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setEditingDetailFieldId(null);
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleSaveDetailField(fieldId);
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    );
  };

  type OrganizationTextFieldId =
    | 'organization'
    | 'contractRenewalDate'
    | 'accountStage'
    | 'address'
    | 'website'
    | 'linkedInProfile'
    | 'numberOfEmployees'
    | 'annualRevenue'
    | 'industry';

  const getOrganizationTextFieldValue = (lead: Lead, fieldId: OrganizationTextFieldId): string => {
    switch (fieldId) {
      case 'organization':
        return String(lead.organization || '').trim();
      case 'contractRenewalDate':
        return String(lead.contractRenewalDate || '').trim();
      case 'accountStage':
        return String(lead.accountStage || '').trim();
      case 'address':
        return String(lead.address || '').trim();
      case 'website':
        return String(lead.website || '').trim();
      case 'linkedInProfile':
        return String(lead.linkedInProfile || '').trim();
      case 'numberOfEmployees':
        return String(lead.numberOfEmployees || '').trim();
      case 'annualRevenue':
        return String(lead.annualRevenue || '').trim();
      case 'industry':
        return String(lead.industry || '').trim();
      default:
        return '';
    }
  };

  const renderOrganizationTextField = (
    fieldId: OrganizationTextFieldId,
    label: string,
    Icon: React.ComponentType<{ size?: number; className?: string }>,
  ) => {
    if (!selectedLead) return null;

    const value = getOrganizationTextFieldValue(selectedLead, fieldId);
    const hasValue = value.length > 0;
    const isEditing = editingDetailFieldId === fieldId;
    const isHovered = hoveredField === fieldId;
    const showAddPrompt = isHovered && !hasValue && !isEditing;
    const shouldRender = fieldId === 'accountStage' || fieldId === 'contractRenewalDate' || showAllOrganizationFields || hasValue || isEditing;
    if (!shouldRender) return null;

    return (
      <div
        key={fieldId}
        className="mb-2"
        style={{
          cursor: 'pointer',
          opacity: 1,
          padding: '0.5rem',
          borderRadius: '4px',
          backgroundColor: isHovered && !isEditing ? '#f8f9fa' : 'transparent',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={() => setHoveredField(fieldId)}
        onMouseLeave={() => setHoveredField(null)}
        onClick={() => startEditingDetailField(fieldId)}
      >
        <div className="d-flex align-items-center gap-2">
          <span style={detailFieldIconStyle}>
            <Icon size={14} />
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              color: showAddPrompt ? '#0d6efd' : (hasValue ? 'inherit' : '#6c757d'),
              fontWeight: showAddPrompt ? 500 : 'normal'
            }}
          >
            {hasValue ? value : (showAddPrompt ? '+ Add value' : label)}
          </span>
        </div>

        {isEditing && (
          <div className="d-flex align-items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
            {fieldId === 'accountStage' ? (
              <Form.Select
                value={detailFieldDraftValue}
                onChange={(e) => setDetailFieldDraftValue(e.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="">Select</option>
                {accountStageOptions.map((stage) => (
                  <option key={`lead-account-stage-${stage}`} value={stage}>
                    {stage}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                type="text"
                value={detailFieldDraftValue}
                placeholder={label}
                onChange={(e) => setDetailFieldDraftValue(e.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              />
            )}
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setDetailFieldDraftValue('');
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Clear
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setEditingDetailFieldId(null);
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleSaveDetailField(fieldId);
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    );
  };

  const allLabels = useMemo(() => {
    const labelsByKey = new Map<string, string>();
    const hiddenLabelKeys = new Set(hiddenLabels.map((label) => normalizeLabelKey(label)));
    const addLabel = (candidate: string) => {
      const normalized = String(candidate || '').trim();
      if (!normalized) return;
      const key = normalizeLabelKey(normalized);
      if (!key || hiddenLabelKeys.has(key) || labelsByKey.has(key)) return;
      labelsByKey.set(key, normalized);
    };

    // Keep the default/common label catalog visible (unless explicitly deleted/hidden).
    Object.keys(getLabelColors()).forEach((label) => {
      addLabel(label);
    });

    leads.forEach((lead: Lead) => {
      const leadLabels = Array.isArray(lead.labels) ? lead.labels : [];
      leadLabels.forEach((label: string) => {
        addLabel(label);
      });
    });

    // Add custom labels that were created (unless explicitly deleted/hidden).
    customLabels.forEach((label: string) => {
      addLabel(label);
    });

    return Array.from(labelsByKey.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [leads, customLabels, hiddenLabels]);

  const getLabelVariant = (label: string): string => {
    // Get current label colors (may have been updated)
    const currentColors = getLabelColors();
    return currentColors[label] || currentColors[label.toUpperCase()] || 'secondary';
  };

  const renderNextActivity = (activity?: Lead['nextActivity']) => {
    if (!activity || activity.type === 'none') {
      return (
        <span className="text-muted">
          <ExclamationTriangle className="me-1" style={{ color: '#ffc107' }} />
          No activity
        </span>
      );
    }

    if (activity.type === 'calendar' && activity.date) {
      return (
        <span style={{ color: '#dc3545' }}>
          <Calendar className="me-1" />
          {activity.date}
        </span>
      );
    }

    if (activity.type === 'phone' && activity.date) {
      return (
        <span style={{ color: '#dc3545' }}>
          <Phone className="me-1" />
          {activity.date}
        </span>
      );
    }

    return null;
  };

  const renderScheduledActivityIcon = (activityType: ScheduleActivityType) => {
    switch (activityType) {
      case 'call':
        return <Phone size={14} />;
      case 'text':
        return <Dot size={14} />;
      case 'meeting':
        return <PeopleFill size={14} />;
      case 'task':
        return <CheckSquare size={14} />;
      case 'deadline':
        return <Clock size={14} />;
      case 'email':
        return <Envelope size={14} />;
      case 'lunch':
        return <Briefcase size={14} />;
      default:
        return <Phone size={14} />;
    }
  };

  const handleSelectScheduledActivityType = (activityType: ScheduleActivityType) => {
    setScheduledActivityType(activityType);
    setScheduledActivityText(SCHEDULE_ACTIVITY_LABELS[activityType]);
    setShowScheduledActivityTypeDropdown(false);
  };

  const getScheduledActivityTypeFromMeeting = (meeting: ScheduledMeetingActivity): ScheduleActivityType => {
    const tagType = (meeting.tags || [])
      .find((tag) => typeof tag === 'string' && tag.startsWith('activity:'))
      ?.replace('activity:', '')
      ?.trim()
      ?.toLowerCase();

    if (tagType && SCHEDULE_ACTIVITY_OPTIONS.some((option) => option.type === tagType)) {
      return tagType as ScheduleActivityType;
    }

    if (meeting.meetingType === 'phone') return 'call';
    return 'meeting';
  };

  const formatFocusDateLabel = (dateValue: string): string => {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const dayDiff = getRelativeDayDifferenceInTimezone(date, new Date(), tenantTimezone);
    if (dayDiff === null) {
      return toLocaleDateStringInTimezone(date, 'en-US', { month: 'short', day: 'numeric' }, tenantTimezone);
    }

    if (dayDiff === 0) return 'Today';
    if (dayDiff === 1) return 'Tomorrow';
    if (dayDiff > 1 && dayDiff < 7) {
      return toLocaleDateStringInTimezone(date, 'en-US', { weekday: 'long' }, tenantTimezone);
    }

    return toLocaleDateStringInTimezone(date, 'en-US', { month: 'short', day: 'numeric' }, tenantTimezone);
  };

  const toLocalDateTimeInputValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getQuickScheduleStart = (preset: QuickSchedulePreset): Date | null => {
    const now = new Date();

    if (preset === 'in1h') {
      return new Date(now.getTime() + (60 * 60 * 1000));
    }
    if (preset === 'in3h') {
      return new Date(now.getTime() + (3 * 60 * 60 * 1000));
    }
    if (preset === 'tomorrow') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow;
    }
    if (preset === 'nextWeek') {
      const nextWeek = new Date(now);
      nextWeek.setDate(nextWeek.getDate() + 7);
      nextWeek.setHours(9, 0, 0, 0);
      return nextWeek;
    }

    const proposedDate = new Date(now.getTime() + (60 * 60 * 1000));
    const userInput = window.prompt(
      'Schedule date and time (YYYY-MM-DDTHH:mm)',
      toLocalDateTimeInputValue(proposedDate)
    );
    if (!userInput) return null;

    const parsed = new Date(userInput);
    if (Number.isNaN(parsed.getTime())) {
      alert('Invalid date/time format. Please use YYYY-MM-DDTHH:mm');
      return null;
    }
    return parsed;
  };

  const handleQuickScheduleActivity = async (preset: QuickSchedulePreset) => {
    if (!selectedLead) return;

    const startDate = getQuickScheduleStart(preset);
    if (!startDate) return;

    const currentLead = selectedLead;
    const title = scheduledActivityText.trim() || SCHEDULE_ACTIVITY_LABELS[scheduledActivityType];
    const endDate = new Date(startDate.getTime() + (30 * 60 * 1000));
    const isPhoneActivity = scheduledActivityType === 'call' || scheduledActivityType === 'text';
    const nextActivityDate = toLocaleDateStringInTimezone(
      startDate,
      'en-US',
      { month: 'long', day: 'numeric' },
      tenantTimezone
    );

    try {
      await createMeeting({
        title,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
        meetingType: isPhoneActivity ? 'phone' : 'in-person',
        leadId: currentLead.id,
        location: currentLead.organization || undefined,
        tags: [`activity:${scheduledActivityType}`],
        visibility: 'personal',
      }).unwrap();

      try {
        await updateLead({
          leadId: currentLead.id,
          nextActivity: {
            type: isPhoneActivity ? 'phone' : 'calendar',
            date: nextActivityDate,
          },
        }).unwrap();
        setSelectedLead({
          ...currentLead,
          nextActivity: {
            type: isPhoneActivity ? 'phone' : 'calendar',
            date: nextActivityDate,
          },
        });
      } catch (leadUpdateError: any) {
        console.error('Failed to sync next activity to lead:', leadUpdateError);
      }

      refetchMeetings();
      refetchLeads();
    } catch (error: any) {
      console.error('Error scheduling activity:', error);
      alert(`Failed to schedule activity: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const leadImportMappedRows = useMemo<LeadImportRow[]>(() => {
    return buildLeadImportRowsFromMapping(leadImportSourceHeaders, leadImportSourceRows, leadImportColumnMapping);
  }, [leadImportColumnMapping, leadImportSourceHeaders, leadImportSourceRows]);

  const leadImportDuplicateInsights = useMemo<Record<number, LeadImportDuplicateInsight>>(() => {
    const existingEmailSet = new Set<string>();
    const existingPhoneSet = new Set<string>();
    const existingNameOrganizationSet = new Set<string>();

    leads.forEach((lead: Lead) => {
      const normalizedEmail = normalizeLeadImportText(String(lead.email || ''));
      if (normalizedEmail) existingEmailSet.add(normalizedEmail);

      const normalizedPhoneValues = [
        normalizeLeadImportPhone(String(lead.phone || '')),
        normalizeLeadImportPhone(String(lead.workPhone || '')),
        normalizeLeadImportPhone(String(lead.cellPhone || '')),
      ].filter(Boolean);
      normalizedPhoneValues.forEach((phoneValue) => existingPhoneSet.add(phoneValue));

      const normalizedName = normalizeLeadImportText(String(lead.name || ''));
      const normalizedOrganization = normalizeLeadImportText(String(lead.organization || ''));
      if (normalizedName && normalizedOrganization) {
        existingNameOrganizationSet.add(`${normalizedName}|${normalizedOrganization}`);
      }
    });

    const importEmailCounts = new Map<string, number>();
    const importPhoneCounts = new Map<string, number>();
    const importNameOrganizationCounts = new Map<string, number>();
    const incrementCount = (targetMap: Map<string, number>, key: string): void => {
      if (!key) return;
      targetMap.set(key, (targetMap.get(key) || 0) + 1);
    };

    leadImportMappedRows.forEach((row) => {
      const normalizedEmail = normalizeLeadImportText(String(row.values.email || ''));
      const normalizedPhone = normalizeLeadImportPhone(String(row.values.phone || ''));
      const normalizedName = normalizeLeadImportText(String(row.values.name || ''));
      const normalizedOrganization = normalizeLeadImportText(String(row.values.organization || ''));
      const normalizedNameOrganization =
        normalizedName && normalizedOrganization ? `${normalizedName}|${normalizedOrganization}` : '';

      incrementCount(importEmailCounts, normalizedEmail);
      incrementCount(importPhoneCounts, normalizedPhone);
      incrementCount(importNameOrganizationCounts, normalizedNameOrganization);
    });

    return leadImportMappedRows.reduce<Record<number, LeadImportDuplicateInsight>>((accumulator, row) => {
      const reasons: string[] = [];
      const normalizedEmail = normalizeLeadImportText(String(row.values.email || ''));
      const normalizedPhone = normalizeLeadImportPhone(String(row.values.phone || ''));
      const normalizedName = normalizeLeadImportText(String(row.values.name || ''));
      const normalizedOrganization = normalizeLeadImportText(String(row.values.organization || ''));
      const normalizedNameOrganization =
        normalizedName && normalizedOrganization ? `${normalizedName}|${normalizedOrganization}` : '';

      if (normalizedEmail && existingEmailSet.has(normalizedEmail)) {
        reasons.push('Matches existing lead email');
      }
      if (normalizedPhone && existingPhoneSet.has(normalizedPhone)) {
        reasons.push('Matches existing lead phone');
      }
      if (normalizedNameOrganization && existingNameOrganizationSet.has(normalizedNameOrganization)) {
        reasons.push('Matches existing lead name + organization');
      }

      if (normalizedEmail && (importEmailCounts.get(normalizedEmail) || 0) > 1) {
        reasons.push('Duplicate email in uploaded file');
      }
      if (normalizedPhone && (importPhoneCounts.get(normalizedPhone) || 0) > 1) {
        reasons.push('Duplicate phone in uploaded file');
      }
      if (normalizedNameOrganization && (importNameOrganizationCounts.get(normalizedNameOrganization) || 0) > 1) {
        reasons.push('Duplicate name + organization in uploaded file');
      }

      accumulator[row.rowNumber] = {
        isDuplicate: reasons.length > 0,
        reasons,
      };
      return accumulator;
    }, {});
  }, [leadImportMappedRows, leads]);

  const leadImportDuplicateRows = useMemo<LeadImportRow[]>(() => {
    return leadImportMappedRows.filter(
      (row) => leadImportDuplicateInsights[row.rowNumber]?.isDuplicate
    );
  }, [leadImportDuplicateInsights, leadImportMappedRows]);

  const leadImportRowsReadyForImport = useMemo<LeadImportRow[]>(() => {
    if (leadImportDuplicatePolicy === 'import-all') return leadImportMappedRows;
    return leadImportMappedRows.filter(
      (row) => !leadImportDuplicateInsights[row.rowNumber]?.isDuplicate
    );
  }, [leadImportDuplicateInsights, leadImportDuplicatePolicy, leadImportMappedRows]);

  const leadImportMappedFieldKeys = useMemo<string[]>(() => {
    return Array.from(
      new Set(
        Object.values(leadImportColumnMapping).filter(
          (value) => value && value !== 'ignore'
        )
      )
    );
  }, [leadImportColumnMapping]);

  const leadImportCanAdvanceFromMapping = useMemo<boolean>(() => {
    if (leadImportMappedRows.length === 0) return false;
    const hasIdentityField = leadImportMappedFieldKeys.some((field) =>
      ['name', 'organization', 'email'].includes(field)
    );
    return hasIdentityField;
  }, [leadImportMappedFieldKeys, leadImportMappedRows.length]);

  const handleLeadMenuPlaceholderAction = (actionLabel: string) => {
    alert(`${actionLabel} is coming soon.`);
  };

  const handleDownloadLeadImportTemplate = () => {
    const templateCsv = `${LEAD_IMPORT_TEMPLATE_HEADERS.map((header) => escapeCsvValue(header)).join(',')}\r\n`;
    const blob = new Blob([templateCsv], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = 'kross-leads-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const resetLeadImportWizard = () => {
    setLeadImportStep(1);
    setLeadImportSourceHeaders([]);
    setLeadImportSourceRows([]);
    setLeadImportColumnMapping({});
    setLeadImportDuplicatePolicy('skip');
    setLeadImportFileName('');
    setLeadImportParseErrors([]);
    setLeadImportRowErrors([]);
    setLeadImportFailedRows([]);
    setLeadImportSummary(null);
    setIsLeadImportDragOver(false);
    if (leadImportFileInputRef.current) {
      leadImportFileInputRef.current.value = '';
    }
  };

  const handleExportFilteredLeads = () => {
    if (filteredLeads.length === 0) {
      alert('No leads match your current filters.');
      return;
    }

    const csvRows = [
      LEAD_EXPORT_COLUMNS.map((column) => escapeCsvValue(column.header)).join(','),
      ...filteredLeads.map((lead: Lead) =>
        LEAD_EXPORT_COLUMNS.map((column) => escapeCsvValue(column.getValue(lead))).join(',')
      ),
    ];

    const csvContent = csvRows.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.href = objectUrl;
    link.download = `kross-leads-export-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const handleDownloadLeadImportFailedRows = () => {
    if (leadImportFailedRows.length === 0) {
      alert('No failed rows to download.');
      return;
    }

    const headerValues = [
      'rowNumber',
      'error',
      ...leadImportFailedRowFieldKeys.map((fieldKey) => getLeadImportTargetFieldLabel(fieldKey)),
    ];
    const csvRows = [
      headerValues.map((value) => escapeCsvValue(value)).join(','),
      ...leadImportFailedRows.map((failedRow) => {
        const rowValues = [
          String(failedRow.rowNumber),
          failedRow.reason,
          ...leadImportFailedRowFieldKeys.map((fieldKey) => String(failedRow.values[fieldKey] || '')),
        ];
        return rowValues.map((value) => escapeCsvValue(value)).join(',');
      }),
    ];

    const csvContent = csvRows.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.href = objectUrl;
    link.download = `kross-leads-import-failed-rows-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const handleDownloadLeadImportSkippedDuplicates = () => {
    const skippedRows =
      leadImportDuplicatePolicy === 'skip'
        ? leadImportDuplicateRows
        : [];
    if (skippedRows.length === 0) {
      alert('No skipped duplicate rows to download.');
      return;
    }

    const headerValues = [
      'rowNumber',
      'duplicateReasons',
      ...leadImportSkippedDuplicateFieldKeys.map((fieldKey) => getLeadImportTargetFieldLabel(fieldKey)),
    ];
    const csvRows = [
      headerValues.map((value) => escapeCsvValue(value)).join(','),
      ...skippedRows.map((skippedRow) => {
        const duplicateReasons =
          leadImportDuplicateInsights[skippedRow.rowNumber]?.reasons.join('; ') || 'Duplicate detected';
        const rowValues = [
          String(skippedRow.rowNumber),
          duplicateReasons,
          ...leadImportSkippedDuplicateFieldKeys.map((fieldKey) => String(skippedRow.values[fieldKey] || '')),
        ];
        return rowValues.map((value) => escapeCsvValue(value)).join(',');
      }),
    ];

    const csvContent = csvRows.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.href = objectUrl;
    link.download = `kross-leads-import-skipped-duplicates-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const handleDownloadLeadImportNonImportedRows = () => {
    const skippedRows =
      leadImportDuplicatePolicy === 'skip'
        ? leadImportDuplicateRows
        : [];
    const nonImportedCount = leadImportFailedRows.length + skippedRows.length;
    if (nonImportedCount === 0) {
      alert('No non-imported rows to download.');
      return;
    }

    const headerValues = [
      'rowNumber',
      'nonImportType',
      'reason',
      ...leadImportNonImportedFieldKeys.map((fieldKey) => getLeadImportTargetFieldLabel(fieldKey)),
    ];
    const csvRows = [
      headerValues.map((value) => escapeCsvValue(value)).join(','),
      ...leadImportFailedRows.map((failedRow) => {
        const rowValues = [
          String(failedRow.rowNumber),
          'failed',
          failedRow.reason,
          ...leadImportNonImportedFieldKeys.map((fieldKey) => String(failedRow.values[fieldKey] || '')),
        ];
        return rowValues.map((value) => escapeCsvValue(value)).join(',');
      }),
      ...skippedRows.map((skippedRow) => {
        const duplicateReasons =
          leadImportDuplicateInsights[skippedRow.rowNumber]?.reasons.join('; ') || 'Duplicate detected';
        const rowValues = [
          String(skippedRow.rowNumber),
          'skipped-duplicate',
          duplicateReasons,
          ...leadImportNonImportedFieldKeys.map((fieldKey) => String(skippedRow.values[fieldKey] || '')),
        ];
        return rowValues.map((value) => escapeCsvValue(value)).join(',');
      }),
    ];

    const csvContent = csvRows.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.href = objectUrl;
    link.download = `kross-leads-import-non-imported-rows-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const handleOpenLeadImportModal = () => {
    resetLeadImportWizard();
    setShowLeadImportModal(true);
  };

  const handleCloseLeadImportModal = () => {
    if (isImportingLeads) return;
    setShowLeadImportModal(false);
    resetLeadImportWizard();
  };

  const applyLeadImportFile = async (file: File) => {
    setLeadImportFileName(file.name);
    setLeadImportParseErrors([]);
    setLeadImportRowErrors([]);
    setLeadImportFailedRows([]);
    setLeadImportSummary(null);

    const lowerFileName = file.name.toLowerCase();
    try {
      const parsedSource = lowerFileName.endsWith('.xlsx') || lowerFileName.endsWith('.xls')
        ? await parseLeadImportSourceXlsx(file)
        : parseLeadImportSourceCsv(await file.text());
      const { headers, rows, errors } = parsedSource;
      const defaultMapping = headers.reduce<Record<string, string>>((accumulator, header) => {
        accumulator[header] = resolveLeadImportHeader(header) || 'ignore';
        return accumulator;
      }, {});
      setLeadImportSourceHeaders(headers);
      setLeadImportSourceRows(rows);
      setLeadImportColumnMapping(defaultMapping);
      setLeadImportParseErrors(errors);
    } catch (error: any) {
      setLeadImportSourceHeaders([]);
      setLeadImportSourceRows([]);
      setLeadImportColumnMapping({});
      setLeadImportParseErrors([
        `Could not parse spreadsheet file: ${error?.message || 'Unknown error'}`,
      ]);
    }
  };

  const handleLeadImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setLeadImportFileName('');
      setLeadImportSourceHeaders([]);
      setLeadImportSourceRows([]);
      setLeadImportColumnMapping({});
      setLeadImportParseErrors([]);
      setLeadImportFailedRows([]);
      return;
    }
    await applyLeadImportFile(file);
  };

  const handleLeadImportDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsLeadImportDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await applyLeadImportFile(file);
  };

  const handleChangeLeadImportMapping = (sourceHeader: string, targetField: string) => {
    setLeadImportColumnMapping((previous) => {
      const next: Record<string, string> = { ...previous };
      if (targetField !== 'ignore') {
        Object.keys(next).forEach((header) => {
          if (header !== sourceHeader && next[header] === targetField) {
            next[header] = 'ignore';
          }
        });
      }
      next[sourceHeader] = targetField;
      return next;
    });
  };

  const handleLeadImportBack = () => {
    if (isImportingLeads || leadImportStep <= 1) return;
    if (leadImportStep === 5) {
      setLeadImportStep(4);
      return;
    }
    setLeadImportStep((previous) => previous - 1);
  };

  const handleLeadImportNext = () => {
    if (leadImportStep === 1) {
      setLeadImportStep(2);
      return;
    }
    if (leadImportStep === 2) {
      if (!leadImportFileName) {
        alert('Upload a spreadsheet file first.');
        return;
      }
      if (leadImportParseErrors.length > 0 || leadImportSourceRows.length === 0) {
        alert('Fix file issues before continuing.');
        return;
      }
      setLeadImportStep(3);
      return;
    }
    if (leadImportStep === 3) {
      if (!leadImportCanAdvanceFromMapping) {
        alert('Map at least one identity field (name, organization, or email).');
        return;
      }
      setLeadImportStep(4);
    }
  };

  const handleImportLeads = async () => {
    if (leadImportMappedRows.length === 0) {
      setLeadImportFailedRows([]);
      setLeadImportRowErrors(['No mappable rows were found. Please check your field mapping.']);
      return;
    }

    const skippedDuplicates =
      leadImportDuplicatePolicy === 'skip'
        ? leadImportDuplicateRows.length
        : 0;
    const rowsToImport = leadImportRowsReadyForImport;
    if (rowsToImport.length === 0) {
      setLeadImportFailedRows([]);
      setLeadImportSummary({
        total: leadImportMappedRows.length,
        attempted: 0,
        imported: 0,
        failed: 0,
        skippedDuplicates,
      });
      setLeadImportRowErrors(
        skippedDuplicates > 0
          ? ['All mapped rows are currently flagged as duplicates. Switch duplicate handling to "Import all rows" to proceed anyway.']
          : ['No rows are available to import.']
      );
      setLeadImportStep(5);
      return;
    }

    setIsImportingLeads(true);
    const failures: string[] = [];
    const failedRows: LeadImportFailedRow[] = [];
    let importedCount = 0;

    for (const importRow of rowsToImport) {
      const values = importRow.values;
      const businessUnit = String(values.businessUnit || '').trim();
      const labels = String(values.labels || '')
        .split(/[|,;]/)
        .map((entry) => entry.trim())
        .filter(Boolean);
      const visibilityRaw = String(values.visibility || '').trim().toLowerCase();
      const fallbackName = String(values.email || values.organization || '').trim();
      const leadName = String(values.name || '').trim() || fallbackName || `Imported lead ${importRow.rowNumber}`;

      const payload = {
        name: leadName,
        title: String(values.title || '').trim(),
        organization: String(values.organization || '').trim(),
        accountStage: String(values.accountStage || '').trim(),
        contactStage: String(values.contactStage || '').trim(),
        labels,
        value: String(values.value || '').trim(),
        currency: String(values.currency || '').trim() || 'US Dollar (USD)',
        expectedCloseDate: String(values.expectedCloseDate || '').trim(),
        sourceChannel: String(values.sourceChannel || '').trim(),
        sourceChannelId: String(values.sourceChannelId || '').trim(),
        businessUnit,
        inStore: businessUnit.toLowerCase() === 'in-store' || businessUnit.toLowerCase() === 'in store',
        email: String(values.email || '').trim(),
        phone: String(values.phone || '').trim(),
        phoneType: String(values.phoneType || '').trim() || 'Work',
        emailType: String(values.emailType || '').trim() || 'Work',
        status: String(values.status || '').trim() || 'Lead',
        visibility: visibilityRaw === 'personal' ? 'personal' : 'global',
      };

      try {
        await createLead(payload).unwrap();
        importedCount += 1;
      } catch (error: any) {
        const reason = error?.data?.message || error?.message || 'Failed to import lead';
        failures.push(`Row ${importRow.rowNumber}: ${reason}`);
        failedRows.push({
          rowNumber: importRow.rowNumber,
          reason,
          values: { ...values },
        });
      }
    }

    setIsImportingLeads(false);
    refetchLeads();
    setLeadImportFailedRows(failedRows);
    setLeadImportSummary({
      total: leadImportMappedRows.length,
      attempted: rowsToImport.length,
      imported: importedCount,
      failed: failures.length,
      skippedDuplicates,
    });
    setLeadImportRowErrors(failures.slice(0, 12));
    setLeadImportStep(5);
  };

  const canProceedFromUploadStep =
    Boolean(leadImportFileName) && leadImportParseErrors.length === 0 && leadImportSourceRows.length > 0;
  const leadImportPreviewFieldKeys = leadImportMappedFieldKeys.slice(0, 8);
  const leadImportPreviewRows = leadImportMappedRows.slice(0, 8);
  const leadImportDuplicateCount = leadImportDuplicateRows.length;
  const leadImportSkippedRows = useMemo<LeadImportRow[]>(() => {
    return leadImportDuplicatePolicy === 'skip' ? leadImportDuplicateRows : [];
  }, [leadImportDuplicatePolicy, leadImportDuplicateRows]);
  const leadImportFailedRowFieldKeys = useMemo<string[]>(() => {
    const keys = new Set<string>(leadImportMappedFieldKeys);
    leadImportFailedRows.forEach((failedRow) => {
      Object.keys(failedRow.values).forEach((fieldKey) => keys.add(fieldKey));
    });
    return Array.from(keys);
  }, [leadImportFailedRows, leadImportMappedFieldKeys]);
  const leadImportSkippedDuplicateFieldKeys = useMemo<string[]>(() => {
    const keys = new Set<string>(leadImportMappedFieldKeys);
    leadImportDuplicateRows.forEach((duplicateRow) => {
      Object.keys(duplicateRow.values).forEach((fieldKey) => keys.add(fieldKey));
    });
    return Array.from(keys);
  }, [leadImportDuplicateRows, leadImportMappedFieldKeys]);
  const leadImportNonImportedFieldKeys = useMemo<string[]>(() => {
    const keys = new Set<string>(leadImportMappedFieldKeys);
    leadImportFailedRows.forEach((failedRow) => {
      Object.keys(failedRow.values).forEach((fieldKey) => keys.add(fieldKey));
    });
    leadImportSkippedRows.forEach((skippedRow) => {
      Object.keys(skippedRow.values).forEach((fieldKey) => keys.add(fieldKey));
    });
    return Array.from(keys);
  }, [leadImportFailedRows, leadImportMappedFieldKeys, leadImportSkippedRows]);
  const leadImportNonImportedCount = leadImportFailedRows.length + leadImportSkippedRows.length;

  // Show loading state
  if (isLoadingLeads) {
    return (
      <Container fluid className="py-3" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading leads...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  // Show error state
  if (leadsError) {
    return (
      <Container fluid className="py-3" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <Alert variant="danger">
          <Alert.Heading>Error loading leads</Alert.Heading>
          <p>{(leadsError as any)?.data?.message || (leadsError as any)?.message || 'Failed to load leads. Please try again.'}</p>
          <Button variant="outline-danger" onClick={() => refetchLeads()}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {/* Page Header - White section under nav bar */}
      <div style={{ backgroundColor: '#ffffff', paddingTop: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6' }}>
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
              <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 500, color: '#212529' }}>Leads</h5>
        </div>
        <div className="d-flex align-items-center gap-2">
              <InputGroup size="sm" style={{ width: '200px' }}>
                <InputGroup.Text style={{ backgroundColor: 'white', borderRight: 'none', fontSize: '0.75rem' }}>
                  <Search size={14} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search Kross Global"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                  size="sm"
            />
          </InputGroup>
        </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <div className="py-3">

      {/* Action Bar */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center">
            <OverlayTrigger placement="bottom" overlay={<Tooltip id="leads-inbox-view-tooltip">Inbox</Tooltip>}>
              <Button
                variant={leadMailboxView === 'inbox' ? 'outline-primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setLeadMailboxView('inbox')}
                style={{
                  width: '40px',
                  height: '34px',
                  padding: 0,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Inbox size={14} />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="bottom" overlay={<Tooltip id="leads-archive-view-tooltip">Archive</Tooltip>}>
              <Button
                variant={leadMailboxView === 'archive' ? 'outline-primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setLeadMailboxView('archive')}
                style={{
                  width: '40px',
                  height: '34px',
                  padding: 0,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  marginLeft: '-1px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Archive size={14} />
              </Button>
            </OverlayTrigger>
          </div>
          {selectedLeads.size > 0 && (
            <OverlayTrigger placement="bottom" overlay={<Tooltip id="leads-delete-selected-tooltip">Delete selected</Tooltip>}>
              <span className="d-inline-block">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="d-flex align-items-center"
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  disabled={isDeletingLead}
                  onClick={handleDeleteSelectedFromList}
                >
                  <Trash size={14} />
                </Button>
              </span>
            </OverlayTrigger>
          )}
          <Button 
            variant="primary" 
            size="sm" 
            className="d-flex align-items-center" 
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={() => setShowAddLeadModal(true)}
          >
            <Plus className="me-1" size={14} />
            Lead
            <ChevronDown className="ms-1" size={12} />
          </Button>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: '0.75rem' }}>{filteredLeads.length} leads</span>
          <Dropdown>
            <Dropdown.Toggle
              variant={contractRenewalFilter !== 'all' ? 'primary' : 'outline-secondary'}
              size="sm"
              id="leads-contract-renewal-dropdown"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              {contractRenewalFilter === 'overdue'
                ? 'Renewal: Overdue'
                : contractRenewalFilter === 'next30'
                  ? 'Renewal: Next 30'
                  : contractRenewalFilter === 'next60'
                    ? 'Renewal: Next 60'
                    : contractRenewalFilter === 'next90'
                      ? 'Renewal: Next 90'
                      : 'Renewal: All'}
              <ChevronDown size={12} className="ms-1" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setContractRenewalFilter('all')}>All</Dropdown.Item>
              <Dropdown.Item onClick={() => setContractRenewalFilter('overdue')}>Overdue / expired</Dropdown.Item>
              <Dropdown.Item onClick={() => setContractRenewalFilter('next30')}>Next 30 days</Dropdown.Item>
              <Dropdown.Item onClick={() => setContractRenewalFilter('next60')}>Next 60 days</Dropdown.Item>
              <Dropdown.Item onClick={() => setContractRenewalFilter('next90')}>Next 90 days</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown 
            show={showLabelsFilter || showCreateLabelModal || showEditLabelModal} 
            onToggle={(isOpen) => {
              // Don't close if we're showing the create or edit label modal
              if (!isOpen && (showCreateLabelModal || showEditLabelModal)) {
                return;
              }
              setShowLabelsFilter(isOpen);
              // If closing and modals are open, close them too
              if (!isOpen) {
                setShowCreateLabelModal(false);
                setShowEditLabelModal(false);
              }
            }}
          >
            <Dropdown.Toggle variant="outline-secondary" size="sm" id="labels-dropdown" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
              {selectedLabel}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ minWidth: '250px', maxHeight: '400px', overflowY: 'auto', padding: '0.5rem' }}>
              {!showCreateLabelModal && !showEditLabelModal ? (
                <>
                  <div className="mb-2">
                    <InputGroup size="sm">
                      <InputGroup.Text>
                        <Search size={14} />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Search label"
                        value={labelSearchQuery}
                        onChange={(e) => setLabelSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: '0.75rem' }}
                      />
                    </InputGroup>
                  </div>
                  <Dropdown.Item
                    onClick={() => {
                      setSelectedLabel('All labels');
                      setShowLabelsFilter(false);
                      setLabelSearchQuery('');
                    }}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <div className="d-flex align-items-center">
                      {selectedLabel === 'All labels' && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                      <span>All labels</span>
                    </div>
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  {allLabels
                    .filter(label => label.toLowerCase().includes(labelSearchQuery.toLowerCase()))
                    .map(label => (
                      <Dropdown.Item
                        key={label}
                        onMouseEnter={() => setHoveredLabel(label)}
                        onMouseLeave={() => setHoveredLabel(null)}
                        onClick={(e) => {
                          // Don't select label if clicking on pencil icon
                          if ((e.target as HTMLElement).closest('.label-edit-btn')) {
                            return;
                          }
                          setSelectedLabel(label);
                          setShowLabelsFilter(false);
                          setLabelSearchQuery('');
                        }}
                        style={{ fontSize: '0.75rem' }}
                      >
                        <div className="d-flex align-items-center justify-content-between w-100">
                          <div className="d-flex align-items-center">
                            {selectedLabel === label && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                            <Badge bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}>{label}</Badge>
                          </div>
                          {hoveredLabel === label && (
                            <Button
                              variant="link"
                              className="label-edit-btn p-0 ms-2"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const currentColors = getLabelColors();
                                setEditingLabelOriginalName(label);
                                setEditingLabelName(label);
                                setEditingLabelColor(currentColors[label.toUpperCase()] || 'secondary');
                                setShowEditLabelModal(true);
                                setLabelSearchQuery('');
                              }}
                              style={{ color: 'inherit', textDecoration: 'none', padding: '0.25rem' }}
                            >
                              <Pencil size={14} />
                            </Button>
                          )}
                        </div>
                      </Dropdown.Item>
                    ))}
                  <Dropdown.Divider />
                  <Dropdown.Item
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowCreateLabelModal(true);
                      setLabelSearchQuery('');
                    }}
                    style={{ fontSize: '0.75rem', color: '#0d6efd' }}
                  >
                    <div className="d-flex align-items-center">
                      <Plus className="me-2" size={14} />
                      <span>Add new label</span>
                    </div>
                  </Dropdown.Item>
                </>
              ) : showCreateLabelModal ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <div className="d-flex align-items-center mb-3">
                    <Button
                      variant="link"
                      className="p-0 me-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCreateLabelModal(false);
                        setNewLabelName('');
                        setNewLabelColor('secondary');
                      }}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      <ArrowLeft size={18} />
                    </Button>
                    <h6 style={{ fontSize: '0.875rem', fontWeight: 'bold', margin: 0, flex: 1 }}>Create label</h6>
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.5rem' }}>Label name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Label name"
                      value={newLabelName}
                      onChange={(e) => setNewLabelName(e.target.value)}
                      style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem' }}
                      autoFocus
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.5rem' }}>Label color</Form.Label>
                    <div className="d-flex gap-2 flex-wrap">
                      {[
                        { name: 'primary', color: '#0d6efd' },
                        { name: 'secondary', color: '#6c757d' },
                        { name: 'success', color: '#198754' },
                        { name: 'danger', color: '#dc3545' },
                        { name: 'warning', color: '#ffc107' },
                        { name: 'info', color: '#0dcaf0' },
                        { name: 'light', color: '#f8f9fa' },
                        { name: 'dark', color: '#212529' }
                      ].map((colorOption) => (
                        <button
                          key={colorOption.name}
                          type="button"
                          onClick={() => setNewLabelColor(colorOption.name)}
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            border: newLabelColor === colorOption.name ? '3px solid #212529' : '2px solid #dee2e6',
                            backgroundColor: colorOption.color,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                          }}
                        >
                          {newLabelColor === colorOption.name && (
                            <Check size={14} style={{ color: colorOption.name === 'light' ? '#212529' : '#fff' }} />
                          )}
                        </button>
                      ))}
                    </div>
                  </Form.Group>
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="secondary"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCreateLabelModal(false);
                        setNewLabelName('');
                        setNewLabelColor('secondary');
                      }}
                      size="sm"
                      style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (newLabelName.trim()) {
                          const newLabel = newLabelName.trim();
                          const newLabelKey = normalizeLabelKey(newLabel);
                          unhideLabelFromCatalog(newLabel);
                          // Add to custom labels if it doesn't already exist
                          if (!allLabels.some((label) => normalizeLabelKey(label) === newLabelKey)) {
                            const updatedLabels = [...customLabels, newLabel];
                            setCustomLabels(updatedLabels);
                            // Persist to localStorage
                            localStorage.setItem('customLabels', JSON.stringify(updatedLabels));
                            // Persist label colors to localStorage
                            const storedColors = JSON.parse(localStorage.getItem('labelColors') || '{}');
                            storedColors[newLabelKey] = newLabelColor;
                            localStorage.setItem('labelColors', JSON.stringify(storedColors));
                          }
                          setShowCreateLabelModal(false);
                          setNewLabelName('');
                          setNewLabelColor('secondary');
                          // Close the dropdown after saving
                          setShowLabelsFilter(false);
                          // TODO: Call API to create label and persist it
                          console.log('Creating label:', { name: newLabel, color: newLabelColor });
                        }
                      }}
                      size="sm"
                      disabled={!newLabelName.trim()}
                      style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : showEditLabelModal ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <div className="d-flex align-items-center mb-3">
                    <Button
                      variant="link"
                      className="p-0 me-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowEditLabelModal(false);
                        setEditingLabelName('');
                        setEditingLabelOriginalName('');
                        setEditingLabelColor('secondary');
                      }}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      <ArrowLeft size={18} />
                    </Button>
                    <h6 style={{ fontSize: '0.875rem', fontWeight: 'bold', margin: 0, flex: 1 }}>Edit label</h6>
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.5rem' }}>Label name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Label name"
                      value={editingLabelName}
                      onChange={(e) => setEditingLabelName(e.target.value)}
                      style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem' }}
                      autoFocus
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.5rem' }}>Label color</Form.Label>
                    <div className="d-flex gap-2 flex-wrap">
                      {[
                        { name: 'primary', color: '#0d6efd' },
                        { name: 'secondary', color: '#6c757d' },
                        { name: 'success', color: '#198754' },
                        { name: 'danger', color: '#dc3545' },
                        { name: 'warning', color: '#ffc107' },
                        { name: 'info', color: '#0dcaf0' },
                        { name: 'light', color: '#f8f9fa' },
                        { name: 'dark', color: '#212529' }
                      ].map((colorOption) => (
                        <button
                          key={colorOption.name}
                          type="button"
                          onClick={() => setEditingLabelColor(colorOption.name)}
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            border: editingLabelColor === colorOption.name ? '3px solid #212529' : '2px solid #dee2e6',
                            backgroundColor: colorOption.color,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0
                          }}
                        >
                          {editingLabelColor === colorOption.name && (
                            <Check size={14} style={{ color: colorOption.name === 'light' ? '#212529' : '#fff' }} />
                          )}
                        </button>
                      ))}
                    </div>
                  </Form.Group>
                  <div className="d-flex justify-content-between align-items-center">
                    <Button
                      variant="link"
                      className="p-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        // Delete label
                        void handleDeleteLabel(editingLabelOriginalName, { closeFilter: true });
                      }}
                      style={{ color: '#dc3545', textDecoration: 'none', padding: '0.25rem' }}
                    >
                      <Trash size={16} />
                    </Button>
                    <div className="d-flex gap-2">
                      <Button
                        variant="secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowEditLabelModal(false);
                          setEditingLabelName('');
                          setEditingLabelOriginalName('');
                          setEditingLabelColor('secondary');
                        }}
                        size="sm"
                        style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (editingLabelName.trim() && editingLabelOriginalName) {
                            const updatedLabelName = editingLabelName.trim();
                            const updatedLabelKey = normalizeLabelKey(updatedLabelName);
                            
                            // Update custom labels if it's a custom label
                            if (customLabels.includes(editingLabelOriginalName)) {
                              const updatedLabels = customLabels.map(l => l === editingLabelOriginalName ? updatedLabelName : l);
                              setCustomLabels(updatedLabels);
                              localStorage.setItem('customLabels', JSON.stringify(updatedLabels));
                            }
                            
                            // Update label colors in localStorage
                            const storedColors = JSON.parse(localStorage.getItem('labelColors') || '{}');
                            // Remove old color entry
                            delete storedColors[editingLabelOriginalName.toUpperCase()];
                            // Add new color entry
                            storedColors[updatedLabelKey] = editingLabelColor;
                            localStorage.setItem('labelColors', JSON.stringify(storedColors));
                            unhideLabelFromCatalog(updatedLabelName);
                            
                            setShowEditLabelModal(false);
                            setEditingLabelName('');
                            setEditingLabelOriginalName('');
                            setEditingLabelColor('secondary');
                            // Close the dropdown after saving
                            setShowLabelsFilter(false);
                            console.log('Label updated:', { oldName: editingLabelOriginalName, newName: updatedLabelName, color: editingLabelColor });
                          }
                        }}
                        size="sm"
                        disabled={!editingLabelName.trim()}
                        style={{ fontSize: '0.75rem', padding: '0.375rem 0.75rem' }}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown
            show={showOwnerFilter}
            autoClose="outside"
            onToggle={(isOpen) => {
              setShowOwnerFilter(isOpen);
              if (isOpen) {
                setOwnerFilterSearchQuery('');
                setOwnerFilterTab('owners');
                setHoveredOwnerRowKey(null);
                setIsHoveringAddFilterButton(false);
              } else {
                setHoveredOwnerRowKey(null);
                setIsHoveringAddFilterButton(false);
              }
            }}
          >
            <Dropdown.Toggle variant="outline-secondary" size="sm" id="owner-dropdown" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
              {selectedOwnerButtonLabel}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ width: '320px', padding: 0, borderRadius: '0.5rem', overflow: 'hidden' }}>
              <div className="p-2 border-bottom" onClick={(event) => event.stopPropagation()}>
                <InputGroup size="sm">
                  <InputGroup.Text style={{ backgroundColor: '#fff', borderRight: 'none' }}>
                    <Search size={14} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search owner or filter"
                    value={ownerFilterSearchQuery}
                    onChange={(event) => setOwnerFilterSearchQuery(event.target.value)}
                    style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                  />
                </InputGroup>
              </div>
              <div className="d-flex border-bottom" onClick={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => setOwnerFilterTab('favorites')}
                  style={{
                    flex: 1,
                    border: 'none',
                    borderBottom: ownerFilterTab === 'favorites' ? '2px solid #0d6efd' : '2px solid transparent',
                    backgroundColor: 'transparent',
                    color: ownerFilterTab === 'favorites' ? '#0d6efd' : '#6c757d',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '0.5rem 0.25rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.15rem'
                  }}
                >
                  <Star size={15} />
                  <span>Favorites</span>
                </button>
                <button
                  type="button"
                  onClick={() => setOwnerFilterTab('owners')}
                  style={{
                    flex: 1,
                    border: 'none',
                    borderBottom: ownerFilterTab === 'owners' ? '2px solid #0d6efd' : '2px solid transparent',
                    backgroundColor: 'transparent',
                    color: ownerFilterTab === 'owners' ? '#0d6efd' : '#6c757d',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '0.5rem 0.25rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.15rem'
                  }}
                >
                  <Person size={15} />
                  <span>Owners</span>
                </button>
                <button
                  type="button"
                  onClick={() => setOwnerFilterTab('filters')}
                  style={{
                    flex: 1,
                    border: 'none',
                    borderBottom: ownerFilterTab === 'filters' ? '2px solid #0d6efd' : '2px solid transparent',
                    backgroundColor: 'transparent',
                    color: ownerFilterTab === 'filters' ? '#0d6efd' : '#6c757d',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    padding: '0.5rem 0.25rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.15rem'
                  }}
                >
                  <Funnel size={15} />
                  <span>Filters</span>
                </button>
              </div>
              <div style={{ maxHeight: '280px', overflowY: 'auto', overflowX: 'hidden' }} onClick={(event) => event.stopPropagation()}>
                {ownerFilterTab === 'owners' && (
                  <div className="py-1">
                    {visibleOwnerOptions.length === 0 ? (
                      <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                        No owners found.
                      </div>
                    ) : (
                      visibleOwnerOptions.map((ownerOption) => {
                        const isEveryone = ownerOption.key === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL);
                        const isFavorite = favoriteOwnerKeys.some((entry) => normalizeOwnerKey(entry) === ownerOption.key);
                        const isSelected = normalizeOwnerKey(selectedOwner) === ownerOption.key;
                        const isRowHovered = hoveredOwnerRowKey === ownerOption.key;
                        const rowTextColor = isRowHovered ? '#ffffff' : '#212529';
                        return (
                          <button
                            key={ownerOption.key}
                            type="button"
                            onClick={() => {
                              setSelectedOwner(ownerOption.label);
                              setShowOwnerFilter(false);
                            }}
                            onMouseEnter={() => setHoveredOwnerRowKey(ownerOption.key)}
                            onMouseLeave={() => setHoveredOwnerRowKey((prev) => (prev === ownerOption.key ? null : prev))}
                            style={{
                              width: '100%',
                              border: 'none',
                              backgroundColor: isRowHovered ? '#0d6efd' : 'transparent',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.55rem 0.75rem',
                              fontSize: '0.8rem',
                              borderRadius: '0.35rem',
                              boxSizing: 'border-box'
                            }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <Person size={14} style={{ color: isRowHovered ? '#ffffff' : '#6c757d' }} />
                              <span style={{ fontWeight: isEveryone ? 600 : 500, color: rowTextColor }}>{ownerOption.label}</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {isEveryone ? (
                                <span style={{ color: isRowHovered ? '#ffffff' : '#adb5bd', lineHeight: 1, display: 'inline-flex' }}>
                                  <Star size={14} />
                                </span>
                              ) : (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleFavoriteOwner(ownerOption.key);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      toggleFavoriteOwner(ownerOption.key);
                                    }
                                  }}
                                  style={{
                                    color: isFavorite ? '#f4b400' : (isRowHovered ? '#ffffff' : '#adb5bd'),
                                    lineHeight: 1,
                                    display: 'inline-flex'
                                  }}
                                >
                                  {isFavorite ? <StarFill size={14} /> : <Star size={14} />}
                                </span>
                              )}
                              {isSelected ? <Check size={15} style={{ color: isRowHovered ? '#ffffff' : '#111827' }} /> : null}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
                {ownerFilterTab === 'filters' && (
                  <div className="py-1">
                    {visibleVisibilityFilterOptions.length === 0 ? (
                      <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                        No filters found.
                      </div>
                    ) : (
                      visibleVisibilityFilterOptions.map((filterOption) => {
                        const isSelected = selectedVisibilityFilterId === filterOption.id;
                        const isFavorite = favoriteVisibilityFilterIds.includes(filterOption.id);
                        const isCustomFilter = !isSystemVisibilityFilterId(filterOption.id);
                        const isSystemFilter = isSystemVisibilityFilterId(filterOption.id);
                        return (
                          <button
                            key={filterOption.id}
                            type="button"
                            onClick={() => setSelectedVisibilityFilterId(filterOption.id)}
                            style={{
                              width: '100%',
                              border: 'none',
                              backgroundColor: 'transparent',
                              textAlign: 'left',
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: '0.5rem',
                              padding: '0.6rem 0.75rem'
                            }}
                          >
                            <div>
                              <div className="d-flex align-items-center gap-1">
                                <div style={{ fontSize: '0.79rem', fontWeight: 500, color: '#212529' }}>{filterOption.label}</div>
                                {isSystemFilter ? (
                                  <span
                                    style={{ color: '#6c757d', lineHeight: 1, display: 'inline-flex', opacity: 0.75 }}
                                    title="System filter (read-only)"
                                  >
                                    <LockFill size={10} />
                                  </span>
                                ) : null}
                              </div>
                              <div style={{ fontSize: '0.69rem', color: '#6c757d', marginTop: '0.15rem' }}>
                                {filterOption.description}
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {isCustomFilter ? (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleOpenEditSavedVisibilityFilterModal(filterOption.id);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleOpenEditSavedVisibilityFilterModal(filterOption.id);
                                    }
                                  }}
                                  style={{ color: '#6c757d', lineHeight: 1, display: 'inline-flex' }}
                                  title="Edit filter"
                                >
                                  <Pencil size={13} />
                                </span>
                              ) : null}
                              {isCustomFilter ? (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleDeleteSavedVisibilityFilter(filterOption.id);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleDeleteSavedVisibilityFilter(filterOption.id);
                                    }
                                  }}
                                  style={{ color: '#dc3545', lineHeight: 1, display: 'inline-flex' }}
                                  title="Delete filter"
                                >
                                  <Trash size={13} />
                                </span>
                              ) : null}
                              {filterOption.id !== 'all' ? (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleFavoriteVisibilityFilter(filterOption.id);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      toggleFavoriteVisibilityFilter(filterOption.id);
                                    }
                                  }}
                                  style={{ color: isFavorite ? '#f4b400' : '#adb5bd', lineHeight: 1, display: 'inline-flex' }}
                                >
                                  {isFavorite ? <StarFill size={14} /> : <Star size={14} />}
                                </span>
                              ) : null}
                              {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
                {ownerFilterTab === 'favorites' && (
                  <div className="py-1">
                    {visibleFavoriteOwnerOptions.length === 0 && visibleFavoriteVisibilityFilterOptions.length === 0 ? (
                      <div className="mx-2 my-2 p-2" style={{ border: '1px solid #b6d4fe', borderRadius: '0.25rem', backgroundColor: '#f8fbff', color: '#0a58ca', fontSize: '0.75rem' }}>
                        Mark an owner or filter as favorite to make it appear here.
                      </div>
                    ) : (
                      <>
                        {visibleFavoriteOwnerOptions.length > 0 ? (
                          <div className="px-3 pt-1 pb-1 text-uppercase text-muted" style={{ fontSize: '0.62rem', fontWeight: 700 }}>
                            Owners
                          </div>
                        ) : null}
                        {visibleFavoriteOwnerOptions.map((ownerOption) => {
                          const isSelected = normalizeOwnerKey(selectedOwner) === ownerOption.key;
                          return (
                            <button
                              key={`favorite-owner-${ownerOption.key}`}
                              type="button"
                              onClick={() => {
                                setSelectedOwner(ownerOption.label);
                                setShowOwnerFilter(false);
                              }}
                              style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: 'transparent',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.55rem 0.75rem',
                                fontSize: '0.8rem'
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <Person size={14} style={{ color: '#6c757d' }} />
                                <span style={{ fontWeight: 500 }}>{ownerOption.label}</span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <StarFill size={14} style={{ color: '#f4b400' }} />
                                {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                              </div>
                            </button>
                          );
                        })}
                        {visibleFavoriteVisibilityFilterOptions.length > 0 ? (
                          <div className="px-3 pt-2 pb-1 text-uppercase text-muted" style={{ fontSize: '0.62rem', fontWeight: 700 }}>
                            Filters
                          </div>
                        ) : null}
                        {visibleFavoriteVisibilityFilterOptions.map((filterOption) => {
                          const isSelected = selectedVisibilityFilterId === filterOption.id;
                          const isSystemFilter = isSystemVisibilityFilterId(filterOption.id);
                          return (
                            <button
                              key={`favorite-filter-${filterOption.id}`}
                              type="button"
                              onClick={() => setSelectedVisibilityFilterId(filterOption.id)}
                              style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: 'transparent',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.55rem 0.75rem',
                                fontSize: '0.8rem'
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <Funnel size={13} style={{ color: '#6c757d' }} />
                                <span>{filterOption.label}</span>
                                {isSystemFilter ? (
                                  <span
                                    style={{ color: '#6c757d', lineHeight: 1, display: 'inline-flex', opacity: 0.75 }}
                                    title="System filter (read-only)"
                                  >
                                    <LockFill size={10} />
                                  </span>
                                ) : null}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <StarFill size={14} style={{ color: '#f4b400' }} />
                                {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                              </div>
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="border-top px-3 py-2" onClick={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  onClick={handleOpenCreateFilterModal}
                  onMouseEnter={() => setIsHoveringAddFilterButton(true)}
                  onMouseLeave={() => setIsHoveringAddFilterButton(false)}
                  style={{
                    border: 'none',
                    backgroundColor: isHoveringAddFilterButton ? '#0d6efd' : 'transparent',
                    color: isHoveringAddFilterButton ? '#fff' : '#0d6efd',
                    borderRadius: '0.35rem',
                    width: '100%',
                    padding: '0.42rem 0.6rem',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '0.35rem'
                  }}
                >
                  <Plus size={14} />
                  Add new filter
                </button>
              </div>
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown align="end">
            <Dropdown.Toggle
              as={IconOnlyDropdownToggle}
              id="lead-bulk-actions-dropdown"
            >
              <ThreeDotsVertical size={18} />
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ minWidth: '230px', padding: '0.35rem 0' }}>
              <Dropdown.Item onClick={handleExportFilteredLeads} style={{ fontSize: '0.95rem', padding: '0.45rem 0.8rem' }}>
                <span className="d-flex align-items-center gap-2">
                  <Upload size={14} />
                  <span>Export filter results</span>
                </span>
              </Dropdown.Item>
              <Dropdown.Item onClick={handleOpenLeadImportModal} style={{ fontSize: '0.95rem', padding: '0.45rem 0.8rem' }}>
                <span className="d-flex align-items-center gap-2">
                  <Download size={14} />
                  <span>Import data</span>
                </span>
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleLeadMenuPlaceholderAction('Open data cleanup')}
                style={{ fontSize: '0.95rem', padding: '0.45rem 0.8rem' }}
              >
                <span className="d-flex align-items-center gap-2">
                  <Funnel size={14} />
                  <span>Open data cleanup</span>
                </span>
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleLeadMenuPlaceholderAction('Restore data')}
                style={{ fontSize: '0.95rem', padding: '0.45rem 0.8rem' }}
              >
                <span className="d-flex align-items-center gap-2">
                  <ArrowClockwise size={14} />
                  <span>Restore data</span>
                </span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Leads Table */}
      <Card className="crm-table-card">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 crm-table">
            <thead className="table-light">
              <tr>
                <th style={{ width: '50px', fontSize: '0.75rem' }}>
                  <Form.Check
                    type="checkbox"
                    checked={selectedLeads.size === filteredLeads.length && filteredLeads.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th style={{ fontSize: '0.75rem' }}>Name</th>
                <th style={{ width: '48px', fontSize: '0.75rem', whiteSpace: 'nowrap', textAlign: 'center' }} title="Decision Maker">DM</th>
                <th style={{ fontSize: '0.75rem' }}>Title</th>
                <th style={{ fontSize: '0.75rem' }}>Organization</th>
                <th style={{ fontSize: '0.75rem' }}>Contact Stage</th>
                <th style={{ width: '48px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Renewal</th>
                <th style={{ fontSize: '0.75rem' }}>
                  Labels
                  <span className="ms-1">↑</span>
                </th>
                <th style={{ fontSize: '0.75rem' }}>
                  Next activity
                  <span className="ms-1">↑</span>
                </th>
                <th style={{ fontSize: '0.75rem' }}>Lead created</th>
                <th style={{ width: '50px', fontSize: '0.75rem' }}>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() => setShowCustomizeColumnsModal(true)}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <Gear size={16} />
                  </Button>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-5 text-muted">
                    No leads found
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead: Lead) => (
                  <tr 
                    key={lead.id}
                    onClick={() => {
                      setSelectedLead(lead);
                      setShowSidebar(true);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedLeads.has(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                      />
                    </td>
                    <td>
                      <div style={{ fontSize: '0.75rem' }}>{lead.name}</div>
                    </td>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      {lead.decisionMaker ? (
                        <span title="Decision Maker" style={{ color: '#0d6efd', display: 'inline-flex', alignItems: 'center' }}>
                          <DiamondFill size={14} />
                        </span>
                      ) : null}
                    </td>
                    <td style={{ fontSize: '0.75rem' }}>{lead.title}</td>
                    <td style={{ fontSize: '0.75rem' }}>{lead.organization}</td>
                    <td style={{ fontSize: '0.75rem' }}>{lead.contactStage || '—'}</td>
                    <td
                      className={isContractRenewalOverdue(lead.contractRenewalDate) ? 'text-danger' : undefined}
                      style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                    >
                      {formatCompactRenewalLabelFromRawDate(lead.contractRenewalDate)}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {(lead.labels || []).map(label => (
                          <Badge key={label} bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.35rem' }}>
                            {label}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td style={{ fontSize: '0.75rem' }}>{renderNextActivity(lead.nextActivity)}</td>
                    <td className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {lead.leadCreated}
                    </td>
                    <td>
                      <Button variant="link" size="sm" className="p-0">
                        <ThreeDotsVertical size={16} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showLeadImportModal} onHide={handleCloseLeadImportModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>Import data</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="px-4 pt-3 pb-2 border-bottom">
            <div className="d-flex align-items-center justify-content-between gap-2">
              {LEAD_IMPORT_STEP_LABELS.map((stepLabel, index) => {
                const stepNumber = index + 1;
                const isActive = leadImportStep === stepNumber;
                const isComplete = leadImportStep > stepNumber;
                return (
                  <div
                    key={`lead-import-step-${stepLabel}`}
                    className="d-flex align-items-center gap-2"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '999px',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: isActive || isComplete ? '#fff' : '#6c757d',
                        backgroundColor: isActive || isComplete ? '#198754' : '#e9ecef',
                        flexShrink: 0,
                      }}
                    >
                      {stepNumber}
                    </span>
                    <span
                      style={{
                        fontSize: '0.78rem',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? '#212529' : '#6c757d',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {stepLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-4">
            {leadImportStep === 1 && (
              <Row className="g-4">
                <Col lg={6}>
                  <h6 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.85rem' }}>
                    Select what you'd like to import
                  </h6>
                  <div className="d-flex flex-column gap-2">
                    {([
                      {
                        id: 'people',
                        label: 'People',
                        description: 'People are the individual contacts you communicate with.',
                        icon: Person,
                        selected: false,
                      },
                      {
                        id: 'organizations',
                        label: 'Organizations',
                        description: 'Organizations are company records and account profiles.',
                        icon: BuildingFill,
                        selected: false,
                      },
                      {
                        id: 'leads',
                        label: 'Leads',
                        description: "Leads are prospects you haven't qualified yet but want to track.",
                        icon: Lightning,
                        selected: true,
                      },
                      {
                        id: 'deals',
                        label: 'Deals',
                        description: 'Deals represent opportunities connected to people or organizations.',
                        icon: CurrencyDollar,
                        selected: false,
                      },
                      {
                        id: 'activities',
                        label: 'Activities',
                        description: 'Activities are calls, tasks, meetings, and follow-ups.',
                        icon: Calendar,
                        selected: false,
                      },
                      {
                        id: 'product-library',
                        label: 'Product library',
                        description: 'Products are reusable catalog entries for deals.',
                        icon: Grid3x3,
                        selected: false,
                      },
                    ] as const).map((option) => {
                      const IconComponent = option.icon;
                      return (
                        <div
                          key={`lead-import-option-${option.id}`}
                          style={{
                            border: option.selected ? '1px solid #0d6efd' : '1px solid #dee2e6',
                            borderRadius: '0.4rem',
                            padding: '0.75rem 0.85rem',
                            backgroundColor: option.selected ? '#f0f7ff' : '#fff',
                            opacity: option.selected ? 1 : 0.75,
                          }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-2">
                            <div className="d-flex align-items-start gap-2">
                              <div style={{ marginTop: '1px', color: option.selected ? '#0d6efd' : '#6c757d' }}>
                                <IconComponent size={16} />
                              </div>
                              <div>
                                <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{option.label}</div>
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                  {option.description}
                                </div>
                              </div>
                            </div>
                            {option.selected ? <Check size={14} style={{ color: '#0d6efd' }} /> : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Col>
                <Col lg={6}>
                  <Card style={{ border: '1px solid #e8eef8', backgroundColor: '#fbfdff' }}>
                    <Card.Body>
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <h6 style={{ fontSize: '0.9rem', fontWeight: 600, margin: 0 }}>Import guide</h6>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleDownloadLeadImportTemplate}
                          style={{ fontSize: '0.7rem', padding: '0.2rem 0.45rem' }}
                        >
                          Download template file (.csv)
                        </Button>
                      </div>
                      <p className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                        These fields are mandatory for your selected data import:
                      </p>
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                        <Badge bg="light" text="dark" style={{ border: '1px solid #dee2e6', fontSize: '0.73rem' }}>
                          Any lead field *
                        </Badge>
                      </div>
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                        <Badge bg="light" text="dark" style={{ border: '1px solid #dee2e6', fontSize: '0.73rem' }}>
                          Person - Name *
                        </Badge>
                        <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                          OR
                        </span>
                        <Badge bg="light" text="dark" style={{ border: '1px solid #dee2e6', fontSize: '0.73rem' }}>
                          Organization - Name *
                        </Badge>
                      </div>
                      <div className="text-muted mb-2" style={{ fontSize: '0.72rem', fontWeight: 600 }}>
                        Spreadsheet example
                      </div>
                      <Table size="sm" bordered className="mb-0">
                        <thead>
                          <tr>
                            <th style={{ fontSize: '0.68rem' }}>Lead - Title</th>
                            <th style={{ fontSize: '0.68rem' }}>Person - Name</th>
                            <th style={{ fontSize: '0.68rem' }}>Person - Email</th>
                            <th style={{ fontSize: '0.68rem' }}>Organization - Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{ fontSize: '0.68rem' }}>Mover Lead</td>
                            <td style={{ fontSize: '0.68rem' }}>Tony Turner</td>
                            <td style={{ fontSize: '0.68rem' }}>tony@example.com</td>
                            <td style={{ fontSize: '0.68rem' }}>Mover Limited</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {leadImportStep === 2 && (
              <>
                <input
                  ref={leadImportFileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv"
                  onChange={(event) => void handleLeadImportFileChange(event)}
                  style={{ display: 'none' }}
                />
                <h6 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                  Upload your spreadsheet
                </h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.78rem' }}>
                  Drag and drop your file here, or click upload. CSV and XLSX are supported.
                </p>
                <div
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsLeadImportDragOver(true);
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    setIsLeadImportDragOver(false);
                  }}
                  onDrop={(event) => void handleLeadImportDrop(event)}
                  onClick={() => leadImportFileInputRef.current?.click()}
                  style={{
                    border: isLeadImportDragOver ? '2px solid #198754' : '2px dashed #cdd4db',
                    borderRadius: '0.5rem',
                    padding: '2.2rem 1.5rem',
                    textAlign: 'center',
                    backgroundColor: isLeadImportDragOver ? '#f1fff5' : '#fbfcfd',
                    cursor: 'pointer',
                  }}
                >
                  <Grid3x3 size={26} style={{ color: '#6c757d', marginBottom: '0.65rem' }} />
                  <div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        leadImportFileInputRef.current?.click();
                      }}
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.85rem' }}
                    >
                      Upload spreadsheet (.XLSX, .CSV)
                    </Button>
                  </div>
                  <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                    Or drag and drop the file here
                  </div>
                </div>
                {leadImportFileName ? (
                  <div className="mt-3 p-2 rounded border" style={{ backgroundColor: '#f8f9fa', fontSize: '0.78rem' }}>
                    <div className="fw-semibold">{leadImportFileName}</div>
                    <div className="text-muted">{leadImportSourceRows.length} data rows detected</div>
                  </div>
                ) : null}
                {leadImportParseErrors.length > 0 ? (
                  <Alert variant="warning" className="mt-3 mb-0" style={{ fontSize: '0.78rem' }}>
                    <ul className="mb-0 ps-3">
                      {leadImportParseErrors.map((errorMessage, errorIndex) => (
                        <li key={`lead-import-upload-error-${errorIndex}`}>{errorMessage}</li>
                      ))}
                    </ul>
                  </Alert>
                ) : null}
              </>
            )}

            {leadImportStep === 3 && (
              <>
                <h6 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                  Map fields
                </h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.78rem' }}>
                  Confirm how each source column should map to Kross Global lead fields.
                </p>
                {leadImportSourceHeaders.length === 0 ? (
                  <Alert variant="warning" className="mb-0" style={{ fontSize: '0.78rem' }}>
                    Upload a valid file first.
                  </Alert>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span style={{ fontSize: '0.76rem', fontWeight: 600 }}>
                        {leadImportMappedFieldKeys.length} mapped field{leadImportMappedFieldKeys.length === 1 ? '' : 's'}
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted" style={{ fontSize: '0.74rem' }}>
                          {leadImportMappedRows.length} rows ready
                        </span>
                        {leadImportDuplicateCount > 0 ? (
                          <Badge bg="warning" text="dark" style={{ fontSize: '0.68rem' }}>
                            {leadImportDuplicateCount} possible duplicate{leadImportDuplicateCount === 1 ? '' : 's'}
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                    <Table bordered size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th style={{ fontSize: '0.72rem' }}>Source column</th>
                          <th style={{ fontSize: '0.72rem', width: '280px' }}>Kross Global field</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leadImportSourceHeaders.map((header) => (
                          <tr key={`lead-import-map-${header}`}>
                            <td style={{ fontSize: '0.76rem' }}>{header}</td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={leadImportColumnMapping[header] || 'ignore'}
                                onChange={(event) =>
                                  handleChangeLeadImportMapping(header, event.target.value)
                                }
                                style={{ fontSize: '0.76rem' }}
                              >
                                {LEAD_IMPORT_TARGET_FIELDS.map((targetField) => (
                                  <option key={`lead-import-target-${header}-${targetField.key}`} value={targetField.key}>
                                    {targetField.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <div className="mt-2 text-muted" style={{ fontSize: '0.72rem' }}>
                      To continue, map at least one identity field: name, organization, or email.
                    </div>
                  </>
                )}
              </>
            )}

            {leadImportStep === 4 && (
              <>
                <h6 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                  Preview
                </h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.78rem' }}>
                  Review mapped output before importing leads.
                </p>
                <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                  <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                    {leadImportMappedRows.length} mapped row{leadImportMappedRows.length === 1 ? '' : 's'} total
                  </div>
                  {leadImportDuplicateCount > 0 ? (
                    <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem' }}>
                      {leadImportDuplicateCount} possible duplicate{leadImportDuplicateCount === 1 ? '' : 's'}
                    </Badge>
                  ) : (
                    <Badge bg="success" style={{ fontSize: '0.7rem' }}>
                      No duplicates detected
                    </Badge>
                  )}
                </div>
                {leadImportDuplicateCount > 0 ? (
                  <div className="border rounded p-2 mb-3" style={{ backgroundColor: '#fffdf2' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.45rem' }}>
                      Duplicate handling
                    </div>
                    <div className="d-flex flex-wrap gap-3">
                      <Form.Check
                        type="radio"
                        id="lead-import-duplicate-policy-skip"
                        name="lead-import-duplicate-policy"
                        label={`Skip duplicate rows (${leadImportDuplicateCount})`}
                        checked={leadImportDuplicatePolicy === 'skip'}
                        onChange={() => setLeadImportDuplicatePolicy('skip')}
                        style={{ fontSize: '0.74rem' }}
                      />
                      <Form.Check
                        type="radio"
                        id="lead-import-duplicate-policy-import-all"
                        name="lead-import-duplicate-policy"
                        label="Import all rows (including duplicates)"
                        checked={leadImportDuplicatePolicy === 'import-all'}
                        onChange={() => setLeadImportDuplicatePolicy('import-all')}
                        style={{ fontSize: '0.74rem' }}
                      />
                    </div>
                    <div className="text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                      Rows that will be imported now: {leadImportRowsReadyForImport.length}
                    </div>
                  </div>
                ) : null}
                {leadImportPreviewRows.length === 0 || leadImportPreviewFieldKeys.length === 0 ? (
                  <Alert variant="warning" className="mb-0" style={{ fontSize: '0.78rem' }}>
                    No mapped rows to preview. Check your mapping in the previous step.
                  </Alert>
                ) : (
                  <>
                    <Table bordered hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          {leadImportPreviewFieldKeys.map((fieldKey) => (
                            <th key={`lead-import-preview-head-${fieldKey}`} style={{ fontSize: '0.71rem' }}>
                              {getLeadImportTargetFieldLabel(fieldKey)}
                            </th>
                          ))}
                          <th style={{ fontSize: '0.71rem', width: '240px' }}>Duplicate check</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leadImportPreviewRows.map((row) => (
                          <tr
                            key={`lead-import-preview-row-${row.rowNumber}`}
                            style={{
                              backgroundColor: leadImportDuplicateInsights[row.rowNumber]?.isDuplicate
                                ? '#fffaf0'
                                : 'transparent',
                            }}
                          >
                            {leadImportPreviewFieldKeys.map((fieldKey) => (
                              <td key={`lead-import-preview-cell-${row.rowNumber}-${fieldKey}`} style={{ fontSize: '0.73rem' }}>
                                {row.values[fieldKey] || '—'}
                              </td>
                            ))}
                            <td style={{ fontSize: '0.71rem' }}>
                              {leadImportDuplicateInsights[row.rowNumber]?.isDuplicate ? (
                                <span className="text-warning-emphasis">
                                  {leadImportDuplicateInsights[row.rowNumber].reasons.join('; ')}
                                </span>
                              ) : (
                                <span className="text-success">No duplicate signal</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {leadImportMappedRows.length > leadImportPreviewRows.length ? (
                      <div className="text-muted mt-2" style={{ fontSize: '0.72rem' }}>
                        Showing first {leadImportPreviewRows.length} rows of {leadImportMappedRows.length}.
                      </div>
                    ) : null}
                  </>
                )}
              </>
            )}

            {leadImportStep === 5 && (
              <>
                <h6 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '0.35rem' }}>Finish</h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.78rem' }}>
                  Import finished. Review the summary below.
                </p>
                {leadImportSummary ? (
                  <Alert
                    variant={leadImportSummary.failed > 0 ? 'warning' : 'success'}
                    className="mb-3"
                    style={{ fontSize: '0.8rem' }}
                  >
                    <div className="fw-semibold mb-1">Import summary</div>
                    <div>Total rows: {leadImportSummary.total}</div>
                    <div>Attempted: {leadImportSummary.attempted}</div>
                    <div>Imported: {leadImportSummary.imported}</div>
                    <div>Failed: {leadImportSummary.failed}</div>
                    <div>Skipped duplicates: {leadImportSummary.skippedDuplicates}</div>
                  </Alert>
                ) : null}
                {leadImportSummary && leadImportNonImportedCount > 0 ? (
                  <div className="d-flex justify-content-end flex-wrap gap-2 mb-3">
                    {leadImportSummary.skippedDuplicates > 0 ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleDownloadLeadImportSkippedDuplicates}
                        style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }}
                      >
                        Download skipped duplicates (.csv)
                      </Button>
                    ) : null}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleDownloadLeadImportNonImportedRows}
                      style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }}
                    >
                      Download all non-imported rows (.csv)
                    </Button>
                  </div>
                ) : null}
                {leadImportRowErrors.length > 0 ? (
                  <Alert variant="warning" className="mb-0" style={{ fontSize: '0.78rem' }}>
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                      <div className="fw-semibold">Row errors</div>
                      {leadImportFailedRows.length > 0 ? (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleDownloadLeadImportFailedRows}
                          style={{ fontSize: '0.7rem', padding: '0.2rem 0.45rem' }}
                        >
                          Download failed rows (.csv)
                        </Button>
                      ) : null}
                    </div>
                    <ul className="mb-0 ps-3">
                      {leadImportRowErrors.map((errorMessage, errorIndex) => (
                        <li key={`lead-import-row-error-${errorIndex}`}>{errorMessage}</li>
                      ))}
                    </ul>
                  </Alert>
                ) : (
                  <div className="text-muted" style={{ fontSize: '0.76rem' }}>
                    No row errors reported.
                  </div>
                )}
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            {leadImportStep > 1 && leadImportStep < 5 ? (
              <Button variant="outline-secondary" onClick={handleLeadImportBack} disabled={isImportingLeads}>
                Back
              </Button>
            ) : (
              <span />
            )}
          </div>
          <div className="d-flex gap-2">
            {leadImportStep !== 5 ? (
              <Button variant="secondary" onClick={handleCloseLeadImportModal} disabled={isImportingLeads}>
                Cancel
              </Button>
            ) : null}

            {leadImportStep === 1 ? (
              <Button variant="primary" onClick={handleLeadImportNext}>
                Next
              </Button>
            ) : null}

            {leadImportStep === 2 ? (
              <Button
                variant="primary"
                onClick={handleLeadImportNext}
                disabled={!canProceedFromUploadStep}
              >
                Next
              </Button>
            ) : null}

            {leadImportStep === 3 ? (
              <Button
                variant="primary"
                onClick={handleLeadImportNext}
                disabled={!leadImportCanAdvanceFromMapping}
              >
                Next
              </Button>
            ) : null}

            {leadImportStep === 4 ? (
              <Button
                variant="primary"
                onClick={() => void handleImportLeads()}
                disabled={!leadImportCanAdvanceFromMapping || isImportingLeads}
              >
                {isImportingLeads ? 'Finishing...' : 'Finish'}
              </Button>
            ) : null}

            {leadImportStep === 5 ? (
              <>
                <Button variant="outline-secondary" onClick={resetLeadImportWizard}>
                  Import another file
                </Button>
                <Button variant="primary" onClick={handleCloseLeadImportModal}>
                  Done
                </Button>
              </>
            ) : null}
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreateFilterModal} onHide={handleCloseCreateFilterModal} size="xl">
        <Modal.Header className="border-bottom-0" style={{ padding: '0.9rem 1.25rem 0.8rem' }}>
          <Modal.Title style={{ fontSize: '1.15rem', fontWeight: 600 }}>
            {isEditingCreateFilter ? 'Edit filter' : 'Create new filter'}
          </Modal.Title>
          <Button
            variant="link"
            className="p-0"
            onClick={handleCloseCreateFilterModal}
            style={{ color: '#495057', textDecoration: 'none' }}
          >
            <X size={20} />
          </Button>
        </Modal.Header>
        <Modal.Body className="p-0">
          {showCreateFilterArchiveHint && (
            <div className="border-top border-bottom px-4 py-3" style={{ backgroundColor: '#f8f9fc' }}>
              <div className="d-flex justify-content-between align-items-start gap-3">
                <div>
                  <div style={{ fontSize: '1.2rem', lineHeight: 1, color: '#4f46e5', marginBottom: '0.45rem' }}>◈</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#212529', marginBottom: '0.5rem' }}>
                    To search from archived leads or deals, add the condition:
                  </div>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <Badge bg="light" text="dark" style={{ border: '1px solid #ced4da', fontSize: '0.82rem', fontWeight: 600, padding: '0.33rem 0.5rem' }}>
                      Deal archive status is archived
                    </Badge>
                    <Badge bg="light" text="dark" style={{ border: '1px solid #ced4da', fontSize: '0.82rem', fontWeight: 600, padding: '0.33rem 0.5rem' }}>
                      Lead archive status is archived
                    </Badge>
                  </div>
                  <Button variant="link" className="p-0" style={{ fontSize: '0.86rem', textDecoration: 'none', lineHeight: 1 }}>
                    Learn more about archiving <ArrowRight size={17} />
                  </Button>
                </div>
                <Button
                  variant="link"
                  className="p-0"
                  onClick={() => setShowCreateFilterArchiveHint(false)}
                  style={{ color: '#6c757d', textDecoration: 'none' }}
                >
                  <X size={20} />
                </Button>
              </div>
            </div>
          )}

          <div className="px-4 pt-3 pb-2 border-bottom">
            <div className="d-flex align-items-center gap-2 mb-3">
              <h6 style={{ margin: 0, fontSize: '1.02rem', fontWeight: 600 }}>Match ALL of these conditions</h6>
              <InfoCircle size={18} style={{ color: '#6c757d' }} />
            </div>
            <div style={{ backgroundColor: '#f8f9fa', borderRadius: '0.4rem', padding: '0.75rem' }}>
              {createFilterAllConditions.map((condition, index) => {
                const fieldOptions = getCreateFilterFieldOptions(condition.scope);
                return (
                  <div
                    key={condition.id}
                    className="d-flex align-items-center gap-2 mb-2"
                    style={{ padding: '0.15rem 0.1rem' }}
                  >
                    <Badge
                      bg="light"
                      text="dark"
                      style={{
                        border: '1px solid #ced4da',
                        fontSize: '0.72rem',
                        minWidth: '54px',
                        padding: '0.35rem 0.35rem',
                      }}
                    >
                      {index === 0 ? 'WHERE' : 'AND'}
                    </Badge>
                    <Form.Select
                      value={condition.scope}
                      onChange={(event) =>
                        handleUpdateCreateFilterConditionScope(
                          'all',
                          condition.id,
                          event.target.value as LeadFilterConditionScope
                        )
                      }
                      style={{ width: '170px', fontSize: '0.85rem' }}
                    >
                      {LEAD_FILTER_SCOPE_OPTIONS.map((option) => (
                        <option key={`create-filter-all-scope-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Select
                      value={condition.field}
                      onChange={(event) => handleUpdateCreateFilterConditionField('all', condition.id, event.target.value)}
                      style={{ maxWidth: '250px', fontSize: '0.85rem' }}
                    >
                      <option value="">Select field</option>
                      {fieldOptions.map((option) => (
                        <option key={`create-filter-all-field-${condition.id}-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      variant="link"
                      className="p-0 ms-auto"
                      onClick={() => handleRemoveCreateFilterCondition('all', condition.id)}
                      disabled={createFilterAllConditions.length <= 1}
                      style={{ color: '#6c757d', textDecoration: 'none' }}
                    >
                      <Trash size={15} />
                    </Button>
                  </div>
                );
              })}
              <Button
                variant="link"
                className="p-0 mt-1"
                onClick={() => handleAddCreateFilterCondition('all')}
                style={{ textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}
              >
                <Plus size={15} className="me-1" />
                Add condition
              </Button>
            </div>
          </div>

          <div className="px-4 pt-3 pb-2 border-bottom">
            <div className="d-flex align-items-center gap-2 mb-3">
              <h6 style={{ margin: 0, fontSize: '1.02rem', fontWeight: 600 }}>And match ANY of these conditions</h6>
              <InfoCircle size={18} style={{ color: '#6c757d' }} />
            </div>
            <div style={{ backgroundColor: '#f8f9fa', borderRadius: '0.4rem', padding: '0.75rem' }}>
              {createFilterAnyConditions.map((condition, index) => {
                const fieldOptions = getCreateFilterFieldOptions(condition.scope);
                return (
                  <div
                    key={condition.id}
                    className="d-flex align-items-center gap-2 mb-2"
                    style={{ padding: '0.15rem 0.1rem' }}
                  >
                    <Badge
                      bg="light"
                      text="dark"
                      style={{
                        border: '1px solid #ced4da',
                        fontSize: '0.72rem',
                        minWidth: '54px',
                        padding: '0.35rem 0.35rem',
                      }}
                    >
                      {index === 0 ? 'WHERE' : 'OR'}
                    </Badge>
                    <Form.Select
                      value={condition.scope}
                      onChange={(event) =>
                        handleUpdateCreateFilterConditionScope(
                          'any',
                          condition.id,
                          event.target.value as LeadFilterConditionScope
                        )
                      }
                      style={{ width: '170px', fontSize: '0.85rem' }}
                    >
                      {LEAD_FILTER_SCOPE_OPTIONS.map((option) => (
                        <option key={`create-filter-any-scope-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Select
                      value={condition.field}
                      onChange={(event) => handleUpdateCreateFilterConditionField('any', condition.id, event.target.value)}
                      style={{ maxWidth: '250px', fontSize: '0.85rem' }}
                    >
                      <option value="">Select field</option>
                      {fieldOptions.map((option) => (
                        <option key={`create-filter-any-field-${condition.id}-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      variant="link"
                      className="p-0 ms-auto"
                      onClick={() => handleRemoveCreateFilterCondition('any', condition.id)}
                      style={{ color: '#6c757d', textDecoration: 'none' }}
                    >
                      <Trash size={15} />
                    </Button>
                  </div>
                );
              })}
              <Button
                variant="link"
                className="p-0 mt-1"
                onClick={() => handleAddCreateFilterCondition('any')}
                style={{ textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}
              >
                <Plus size={15} className="me-1" />
                Add condition
              </Button>
            </div>
          </div>

          <div className="px-4 py-3">
            <Row className="g-3">
              <Col md={6}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Filter name:</Form.Label>
                <Form.Control
                  type="text"
                  value={createFilterName}
                  onChange={(event) => setCreateFilterName(event.target.value)}
                  style={{ fontSize: '0.88rem', padding: '0.42rem 0.65rem' }}
                />
              </Col>
              <Col md={6}>
                <Form.Label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Visibility:</Form.Label>
                <InputGroup>
                  <InputGroup.Text style={{ backgroundColor: '#fff' }}>
                    <LockFill size={12} />
                  </InputGroup.Text>
                  <Form.Select
                    value={createFilterVisibility}
                    onChange={(event) => setCreateFilterVisibility(event.target.value as 'private' | 'shared')}
                    style={{ fontSize: '0.88rem', padding: '0.42rem 0.65rem' }}
                  >
                    <option value="private">Private</option>
                    <option value="shared">Shared</option>
                  </Form.Select>
                </InputGroup>
              </Col>
            </Row>
            <Form.Check
              type="checkbox"
              className="mt-3"
              checked={createFilterSaveColumns}
              onChange={(event) => setCreateFilterSaveColumns(event.target.checked)}
              label={<span style={{ fontSize: '0.88rem' }}>Save selected columns with the filter</span>}
            />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '0.65rem 1rem' }}>
          {isEditingCreateFilter && createFilterEditingId ? (
            <Button
              variant="outline-danger"
              className="me-auto"
              onClick={() => {
                handleDeleteSavedVisibilityFilter(createFilterEditingId);
              }}
              style={{ fontSize: '0.85rem', padding: '0.35rem 0.95rem', fontWeight: 600 }}
            >
              Delete
            </Button>
          ) : null}
          <Button
            variant="outline-secondary"
            disabled
            style={{ fontSize: '0.85rem', padding: '0.35rem 0.95rem', fontWeight: 600 }}
          >
            Preview
          </Button>
          <Button
            variant="success"
            onClick={handleSaveCreateFilter}
            disabled={!createFilterCanSave}
            style={{ fontSize: '0.85rem', padding: '0.35rem 0.95rem', fontWeight: 600 }}
          >
            {isEditingCreateFilter ? 'Update' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Lead Modal */}
      <Modal show={showAddLeadModal} onHide={() => setShowAddLeadModal(false)} size="lg">
        <Modal.Header style={{ padding: '0.75rem 1rem' }}>
          <Modal.Title style={{ fontSize: '1rem' }}>Add lead</Modal.Title>
          <Button variant="link" className="p-0" onClick={() => setShowAddLeadModal(false)}>
            <X size={18} />
          </Button>
        </Modal.Header>
        <Modal.Body style={{ padding: '1rem' }}>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Contact person
                    <Person className="ms-1" size={12} />
                    <InfoCircle className="ms-1" size={10} style={{ color: '#6c757d' }} />
                  </Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Enter contact name" 
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.contactPerson}
                    onChange={(e) => handleFormChange('contactPerson', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Organization
                    <Briefcase className="ms-1" size={12} />
                    <InfoCircle className="ms-1" size={10} style={{ color: '#6c757d' }} />
                  </Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Enter organization" 
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.organization}
                    onChange={(e) => handleFormChange('organization', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Account Stage
                    <Briefcase className="ms-1" size={12} />
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.accountStage}
                    onChange={(e) => handleFormChange('accountStage', e.target.value)}
                  >
                    <option value="">Select account stage</option>
                    {accountStageOptions.map((stage) => (
                      <option key={`lead-account-stage-option-${stage}`} value={stage}>
                        {stage}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Visibility
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.visibility}
                    onChange={(e) => handleFormChange('visibility', e.target.value)}
                  >
                    <option value="global">Global (tenant-wide)</option>
                    <option value="personal">Personal (private)</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>Title</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Enter title" 
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Value
                    <InfoCircle className="ms-1" size={10} style={{ color: '#6c757d' }} />
                  </Form.Label>
                  <InputGroup size="sm">
                    <Form.Control 
                      type="text" 
                      placeholder="Enter value" 
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      value={formData.value}
                      onChange={(e) => handleFormChange('value', e.target.value)}
                    />
                    <Form.Select 
                      style={{ maxWidth: '130px', fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      value={formData.currency}
                      onChange={(e) => handleFormChange('currency', e.target.value)}
                    >
                      <option>US Dollar (USD)</option>
                    </Form.Select>
                  </InputGroup>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Labels
                    <InfoCircle className="ms-1" size={10} style={{ color: '#6c757d' }} />
                  </Form.Label>
                  <Form.Select 
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.labels}
                    onChange={(e) => handleFormChange('labels', e.target.value)}
                  >
                    <option value="">Select labels</option>
                    {allLabels.map(label => (
                      <option key={label} value={label}>{label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Expected close date
                    <InfoCircle className="ms-1" size={10} style={{ color: '#6c757d' }} />
                  </Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="MM/DD/YYYY" 
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.expectedCloseDate}
                    onChange={(e) => handleFormChange('expectedCloseDate', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>Source channel</Form.Label>
                  <Form.Select 
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.sourceChannel}
                    onChange={(e) => handleFormChange('sourceChannel', e.target.value)}
                  >
                    <option value="">Select source channel</option>
                    {allSourceChannelOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>Source channel ID</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Enter source channel ID" 
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.sourceChannelId}
                    onChange={(e) => handleFormChange('sourceChannelId', e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>Business Unit:</Form.Label>
                  <Form.Select 
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.businessUnit}
                    onChange={(e) => handleFormChange('businessUnit', e.target.value)}
                  >
                    <option value="">Select business unit</option>
                    {allBusinessUnitOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Button variant="link" className="p-0" style={{ fontSize: '0.75rem' }}>
                  <FileText className="me-1" size={12} />
                  Import
                </Button>
              </Col>
              <Col md={6}>
                <h6 className="text-uppercase text-muted mb-2" style={{ fontSize: '0.7rem' }}>PERSON</h6>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Contact Stage
                    <Person className="ms-1" size={12} />
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    value={formData.contactStage}
                    onChange={(e) => handleFormChange('contactStage', e.target.value)}
                  >
                    <option value="">Select contact stage</option>
                    {contactStageOptions.map((stage) => (
                      <option key={`lead-contact-stage-option-${stage}`} value={stage}>
                        {stage}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>Phone</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control 
                      type="text" 
                      placeholder="Enter phone number" 
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      value={formData.phone}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                    />
                    <Form.Select 
                      style={{ maxWidth: '90px', fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      value={formData.phoneType}
                      onChange={(e) => handleFormChange('phoneType', e.target.value)}
                    >
                      <option>Work</option>
                    </Form.Select>
                  </InputGroup>
                  <Button variant="link" className="p-0 mt-1" style={{ fontSize: '0.75rem' }}>
                    <Plus className="me-1" size={10} />
                    Add phone
                  </Button>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>Email</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control 
                      type="email" 
                      placeholder="Enter email address" 
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      value={formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                    />
                    <Form.Select 
                      style={{ maxWidth: '90px', fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      value={formData.emailType}
                      onChange={(e) => handleFormChange('emailType', e.target.value)}
                    >
                      <option>Work</option>
                    </Form.Select>
                  </InputGroup>
                  <Button variant="link" className="p-0 mt-1" style={{ fontSize: '0.75rem' }}>
                    <Plus className="me-1" size={10} />
                    Add email
                  </Button>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    Decision Maker
                    <Diamond className="ms-1" size={12} />
                  </Form.Label>
                  <div style={{ paddingTop: '0.15rem' }}>
                    <Form.Check
                      type="switch"
                      id="add-lead-decision-maker-switch"
                      label={formData.decisionMaker ? 'Yes' : 'No'}
                      checked={Boolean(formData.decisionMaker)}
                      onChange={(e) => handleFormChange('decisionMaker', e.target.checked)}
                      style={{ fontSize: '0.75rem' }}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center" style={{ padding: '0.75rem 1rem' }}>
          <div className="d-flex align-items-center">
            <span className="text-muted me-2" style={{ fontSize: '0.75rem' }}>{leads.length}/unlimited</span>
            <InfoCircle size={12} style={{ color: '#6c757d' }} />
          </div>
          <div>
            <Button 
              variant="secondary" 
              onClick={() => setShowAddLeadModal(false)} 
              className="me-2"
              size="sm"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveLead}
              size="sm"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              disabled={isCreatingLead}
            >
              {isCreatingLead ? <Spinner size="sm" animation="border" /> : 'Save'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>


      {/* Customize Columns Modal */}
      <Modal show={showCustomizeColumnsModal} onHide={() => setShowCustomizeColumnsModal(false)}>
        <Modal.Header>
          <Modal.Title>Customize columns</Modal.Title>
          <Button variant="link" className="p-0" onClick={() => setShowCustomizeColumnsModal(false)}>
            <X size={20} />
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="text"
            placeholder="Search"
            className="mb-3"
          />
          <div>
            <h6 className="mb-2">VISIBLE</h6>
            <Form.Check label="Name (Person)" defaultChecked />
            <Form.Check label="Title (Lead)" defaultChecked />
            <Form.Check label="Organization (Lead)" defaultChecked />
            <Form.Check label="Contact Stage (Lead)" defaultChecked />
            <Form.Check label="Labels (Lead)" defaultChecked />
            <Form.Check label="Next activity (Lead)" defaultChecked />
            <Form.Check label="Lead created (Lead)" defaultChecked />
          </div>
          <div className="mt-4">
            <h6 className="mb-2">AVAILABLE</h6>
            <Form.Check label="Owner" />
            <Form.Check label="Value" />
            <Form.Check label="Source channel" />
            <Form.Check label="Expected close date" />
            <Form.Check label="Visible to" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary">Default</Button>
          <Button variant="secondary" onClick={() => setShowCustomizeColumnsModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => setShowCustomizeColumnsModal(false)}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Right Sidebar for Lead Details */}
      <Offcanvas
        show={showSidebar}
        onHide={() => {
          setShowSidebar(false);
          setSelectedLead(null);
        }}
        placement="end"
        style={{
          width: 'calc(100vw - 510px)',
          maxWidth: '1200px',
          minWidth: '800px',
          top: '56px',
          height: 'calc(100vh - 56px)',
          zIndex: 1040
        }}
      >
        <Offcanvas.Header
          className="position-relative"
          style={{
            borderBottom: '1px solid #dee2e6',
            paddingTop: '0.95rem',
            paddingBottom: '1.05rem',
          }}
        >
          <Offcanvas.Title style={{ flex: 1 }}>
            {selectedLead && (
              <div>
                <div 
                  className="d-flex align-items-center gap-2"
                  onMouseEnter={() => setIsHoveringName(true)}
                  onMouseLeave={() => setIsHoveringName(false)}
                  style={{ 
                    position: 'relative',
                    padding: isHoveringName && !isEditingName ? '0.2rem 0.5rem' : '0.2rem 0.5rem',
                    borderRadius: '4px',
                    backgroundColor: isHoveringName && !isEditingName ? '#fff3cd' : 'transparent',
                    transition: 'background-color 0.2s',
                    cursor: isHoveringName && !isEditingName ? 'pointer' : 'default',
                    width: 'fit-content',
                    maxWidth: '900px'
                  }}
                >
                  {isEditingName ? (
                    <div className="d-flex flex-column gap-2" style={{ flex: 1, width: '100%' }}>
                      <Form.Control
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveName();
                          } else if (e.key === 'Escape') {
                            setIsEditingName(false);
                            setEditedName(selectedLead.name);
                          }
                        }}
                        autoFocus
                        style={{ 
                          fontSize: '0.75rem', 
                          fontWeight: 'bold', 
                          padding: '0.5rem 0.75rem',
                          width: '100%',
                          minWidth: '450px',
                          maxWidth: '900px'
                        }}
                      />
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            setIsEditingName(false);
                            setEditedName(selectedLead.name);
                          }}
                          style={{ 
                            fontSize: '0.75rem', 
                            padding: '0.375rem 0.75rem',
                            fontWeight: 'bold',
                            borderColor: '#dee2e6',
                            backgroundColor: 'white',
                            color: '#000'
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleSaveName}
                          style={{ 
                            fontSize: '0.75rem', 
                            padding: '0.375rem 0.75rem',
                            fontWeight: 'bold',
                            backgroundColor: '#0d6efd',
                            borderColor: '#0d6efd'
                          }}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="fw-bold" style={{ fontSize: '0.85rem' }}>{selectedLead.name}</div>
                      {isHoveringName && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="p-1"
                          onClick={() => {
                            setEditedName(selectedLead.name);
                            setIsEditingName(true);
                          }}
                          style={{ 
                            padding: '0.25rem 0.5rem',
                            borderColor: '#dee2e6',
                            backgroundColor: 'white',
                            minWidth: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Pencil size={14} />
                        </Button>
                      )}
                    </>
                  )}
                </div>
                {String(selectedLead.organization || '').trim().length > 0 && (
                  <div className="text-muted small" style={{ fontSize: '0.7rem', paddingLeft: '0.5rem', marginTop: '0.05rem' }}>
                    {selectedLead.organization}
                  </div>
                )}
                {String(selectedLead.title || '').trim().length > 0 && (
                  <div className="text-muted small" style={{ fontSize: '0.7rem', paddingLeft: '0.5rem', marginTop: '0.05rem' }}>
                    {selectedLead.title}
                  </div>
                )}
              </div>
            )}
          </Offcanvas.Title>
          <Button
            variant="link"
            className="p-0 position-absolute"
            onClick={() => {
              setShowSidebar(false);
              setSelectedLead(null);
            }}
            style={{
              top: '1.4rem',
              right: '1rem',
              zIndex: 10
            }}
          >
            <X size={20} />
          </Button>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          {selectedLead && (
            <div className="d-flex h-100" style={{ overflowX: 'hidden' }}>
              {/* Left Column - Details */}
              <div style={{ width: '320px', padding: '1rem', borderRight: '1px solid #dee2e6', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <div className="mb-4" style={{ flex: 1 }}>
                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>DETAILS</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setShowAllFields(!showAllFields)}
                      >
                        {showAllFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>
                    {allFields.map((field) => {
                      const rendered = renderField(field);
                      // If this is the labels field, render Value field right after it
                      if ((field as any).isLabels && field.added) {
                        const detailsValueHasData = Boolean(String(selectedLead?.value || '').trim());
                        const shouldShowDetailsValueRow = showAllFields || detailsValueHasData || isEditingValueDetails;
                        return (
                          <React.Fragment key={field.id}>
                            {rendered}
                            {/* Value field for DETAILS - positioned right after Labels */}
                            {shouldShowDetailsValueRow && (
                              <div 
                                className="mb-2"
                                onMouseEnter={() => {
                                  if (!isEditingValueDetails) {
                                    setIsHoveringValueDetails(true);
                                  }
                                }}
                                onMouseLeave={() => {
                                  if (!isEditingValueDetails) {
                                    setIsHoveringValueDetails(false);
                                  }
                                }}
                                style={{ 
                                  padding: '0.5rem',
                                  borderRadius: '4px',
                                  backgroundColor: isHoveringValueDetails && !isEditingValueDetails ? '#f8f9fa' : 'transparent',
                                  transition: 'background-color 0.2s',
                                  cursor: isHoveringValueDetails && !isEditingValueDetails ? 'pointer' : 'default',
                                  minHeight: '32px',
                                  marginTop: '0.25rem'
                                }}
                                onClick={() => {
                                  if (!isEditingValueDetails) {
                                    setIsEditingValueDetails(true);
                                  }
                                }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <span style={detailFieldIconStyle}>
                                    <CurrencyDollar size={14} />
                                  </span>
                                  <span 
                                    style={{ 
                                      fontSize: '0.75rem', 
                                      color: isHoveringValueDetails && !isEditingValueDetails ? '#0d6efd' : '#6c757d',
                                      fontWeight: isHoveringValueDetails && !isEditingValueDetails ? '500' : 'normal'
                                    }}
                                  >
                                    {valueDetails ? valueDetails : (isHoveringValueDetails && !isEditingValueDetails ? '+ Add Value' : 'Value')}
                                  </span>
                                </div>
                                {isEditingValueDetails && (
                                  <div className="d-flex align-items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                                    <Form.Control
                                      type="text"
                                      placeholder={valueDetails ? '' : 'Value'}
                                      value={valueDetails}
                                      onChange={(e) => setValueDetails(e.target.value)}
                                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 2, minWidth: '150px' }}
                                      autoFocus
                                    />
                                    <Form.Select
                                      className="currency-select-no-arrow"
                                      value={currencyDetails}
                                      onChange={(e) => setCurrencyDetails(e.target.value)}
                                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', maxWidth: '200px', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: 'none', background: 'none' }}
                                    >
                                      <option>US Dollar (USD)</option>
                                      <option>Euro (EUR)</option>
                                      <option>British Pound (GBP)</option>
                                      <option>Japanese Yen (JPY)</option>
                                      <option>Canadian Dollar (CAD)</option>
                                      <option>Australian Dollar (AUD)</option>
                                    </Form.Select>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setValueDetails('');
                                      }}
                                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                                    >
                                      Clear
                                    </Button>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEditingValueDetails(false);
                                        // Reset to selectedLead values
                                        setValueDetails(selectedLead?.value || '');
                                        setCurrencyDetails(selectedLead?.currency || 'US Dollar (USD)');
                                      }}
                                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveValue(valueDetails, currencyDetails);
                                      }}
                                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
                                    >
                                      Save
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </React.Fragment>
                        );
                      }
                      return rendered;
                    })}
                  </div>

                  <div className="mb-3" style={{ marginTop: '2rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>PERSON</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setShowAllPersonFields(!showAllPersonFields)}
                      >
                        {showAllPersonFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>
                    <div
                      className="mb-2 d-flex align-items-center gap-2"
                      hidden={!showAllPersonFields && !selectedLead.name}
                      style={{ padding: '0.5rem', borderRadius: '4px' }}
                    >
                      <span style={detailFieldIconStyle}>
                        <Person size={14} />
                      </span>
                      <span style={{ fontSize: '0.75rem' }}>{selectedLead.name}</span>
                    </div>

                    {renderPersonTextField('title', 'Title', FileText)}
                    <div className="d-flex align-items-center gap-2 mb-2" style={{ padding: '0.5rem', borderRadius: '4px' }}>
                      <span style={detailFieldIconStyle}>
                        {selectedLead.decisionMaker
                          ? <DiamondFill size={14} style={{ color: '#0d6efd' }} />
                          : <Diamond size={14} />}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#212529', flex: 1 }}>Decision Maker</span>
                      <Form.Check
                        type="switch"
                        id={`lead-decision-maker-switch-${selectedLead.id}`}
                        checked={Boolean(selectedLead.decisionMaker)}
                        onChange={async (e) => {
                          const next = e.target.checked;
                          if (Boolean(selectedLead.decisionMaker) === next) return;
                          try {
                            await updateLead({ leadId: selectedLead.id, decisionMaker: next } as any).unwrap();
                            setSelectedLead({ ...selectedLead, decisionMaker: next });
                          } catch (error: any) {
                            console.error('Error updating decisionMaker:', error);
                            alert(`Failed to update Decision Maker: ${error?.data?.message || error?.message || 'Unknown error'}`);
                          }
                        }}
                        style={{ fontSize: '0.75rem' }}
                      />
                    </div>
                    {renderPersonTextField('contactStage', 'Contact Stage', CheckSquare)}
                    {renderPersonTextField('email', 'Email', Envelope)}
                    {renderPersonTextField('workPhone', 'Work phone', Phone)}
                    {renderPersonTextField('cellPhone', 'Cell phone', Phone)}
                    {renderPersonTextField('assistantName', "Assistant's Name", Person)}
                    {renderPersonTextField('assistantPhone', "Assistant's Phone", Phone)}
                    {renderPersonTextField('assistantEmail', "Assistant's Email", Envelope)}
                  </div>

                  <div className="mb-3" style={{ marginTop: '2rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>ORGANIZATION</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setShowAllOrganizationFields(!showAllOrganizationFields)}
                      >
                        {showAllOrganizationFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>
                    {renderOrganizationTextField('organization', 'No organization', BuildingFill)}
                    {renderOrganizationTextField('contractRenewalDate', '+ Add Contract Renewal', Calendar)}
                    {renderOrganizationTextField('accountStage', 'Account Stage', Briefcase)}
                    {renderOrganizationTextField('address', 'Address', GeoAltFill)}
                    {renderOrganizationTextField('website', 'Website', Globe2)}
                    {renderOrganizationTextField('linkedInProfile', 'LinkedIn profile', Linkedin)}
                    {renderOrganizationTextField('numberOfEmployees', 'Number of employees', PeopleFill)}
                    {renderOrganizationTextField('annualRevenue', 'Annual revenue', CurrencyDollar)}
                    {renderOrganizationTextField('industry', 'Industry', Briefcase)}
                    
                  </div>
                </div>
                <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #dee2e6' }}>
                  <div className="d-flex align-items-center gap-2">
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as={IconOnlyDropdownToggle}
                        id="lead-sidebar-actions"
                      >
                        <ThreeDotsVertical size={14} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu style={{ minWidth: '180px' }}>
                        <Dropdown.Item onClick={handleMergeSelectedLead}>
                          Merge
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={handleDeleteSelectedLead}
                          style={{ color: '#dc3545' }}
                          disabled={isDeletingLead}
                        >
                          Delete lead
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>

                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="archive-lead-tooltip">
                          {selectedLead && isLeadArchived(selectedLead) ? 'Move to inbox' : 'Archive'}
                        </Tooltip>
                      }
                    >
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleArchiveSelectedLead}
                        disabled={isUpdatingLead}
                        style={{
                          width: '32px',
                          height: '32px',
                          padding: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderColor: '#ced4da'
                        }}
                      >
                        <Archive size={14} />
                      </Button>
                    </OverlayTrigger>

                    <Dropdown align="end" drop="up">
                      <Dropdown.Toggle
                        variant="primary"
                        size="sm"
                        id="lead-convert-actions"
                        disabled={!selectedLead || isConvertingLead}
                        style={{ fontSize: '0.7rem', padding: '0.35rem 0.75rem', width: 'auto', borderRadius: '10px' }}
                      >
                        <Folder className="me-1" size={12} />
                        {isConvertingLead ? 'Converting...' : 'Convert'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        style={{
                          minWidth: '240px',
                          padding: '0.35rem',
                          borderRadius: '12px',
                          border: '1px solid #e9ecef',
                          boxShadow: '0 14px 32px rgba(15, 23, 42, 0.14)'
                        }}
                      >
                        {([
                          { target: 'Contact' as const, label: 'Convert to Contact', color: '#0d6efd' },
                          { target: 'Client' as const, label: 'Convert to Client', color: '#198754' },
                          { target: 'Deal' as const, label: 'Convert to Deal', color: '#0d6efd' },
                        ]).map((option, index) => (
                          <React.Fragment key={option.target}>
                            <Dropdown.Item
                              onClick={() => handleConvertSelectedLead(option.target)}
                              disabled={isConvertingLead}
                              className="d-flex align-items-center"
                              style={{
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                padding: '0.5rem 0.8rem',
                                borderRadius: '8px',
                                lineHeight: 1.2
                              }}
                            >
                              <Lightning size={14} className="me-2" style={{ color: option.color }} />
                              {option.label}
                            </Dropdown.Item>
                            {index < 2 && <Dropdown.Divider style={{ margin: '0.15rem 0' }} />}
                          </React.Fragment>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>

              {/* Right Column - Notes/Activity/Email/Files */}
              <div style={{ flex: 1, padding: '1rem', overflowY: 'auto', overflowX: 'hidden' }}>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k || 'notes')}
                  className="mb-3"
                  style={{ fontSize: '0.75rem' }}
                >
                  <Tab eventKey="notes" title="Notes">
                    {/* Rich Text Note Editor */}
                    <div 
                      className="mb-3 note-editor-container"
                      onClick={() => {
                        if (noteEditorRef && !isNoteEditorFocused) {
                          noteEditorRef.focus();
                        }
                      }}
                      style={{
                        backgroundColor: isNoteEditorFocused ? '#fff9e6' : '#ffffff',
                        border: isNoteEditorFocused ? '2px solid #0d6efd' : '1px solid #dee2e6',
                        borderRadius: '4px',
                        transition: 'all 0.2s',
                        cursor: 'text'
                      }}
                    >
                      <div
                        ref={(el) => setNoteEditorRef(el)}
                        contentEditable
                        onFocus={() => {
                          setIsNoteEditorFocused(true);
                        }}
                        onBlur={(e) => {
                          // Don't blur if clicking on toolbar or buttons
                          setTimeout(() => {
                            const activeElement = document.activeElement;
                            if (!activeElement?.closest('.note-editor-container')) {
                              // Keep focused if there's content, otherwise allow blur
                              if (!currentNoteContent.trim()) {
                                setIsNoteEditorFocused(false);
                              }
                            }
                          }, 200);
                        }}
                        onInput={(e) => {
                          const content = e.currentTarget.innerHTML;
                          setCurrentNoteContent(content);
                          // Show/hide placeholder
                          if (content.trim() === '' || content === '<br>') {
                            e.currentTarget.setAttribute('data-placeholder', 'Take a note, @name...');
                          } else {
                            e.currentTarget.removeAttribute('data-placeholder');
                          }
                        }}
                        data-placeholder="Take a note, @name..."
                        style={{
                          minHeight: '100px',
                          padding: '0.75rem',
                          fontSize: '0.75rem',
                          outline: 'none',
                          backgroundColor: 'transparent',
                          position: 'relative'
                        }}
                        className="note-editor"
                      />
                      
                      {/* Formatting Toolbar */}
                      {isNoteEditorFocused && (
                        <div 
                          className="d-flex align-items-center gap-1 p-2 border-top"
                          style={{ backgroundColor: '#f8f9fa', fontSize: '0.75rem' }}
                          onClick={(e) => e.preventDefault()}
                        >
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              executeCommand('bold');
                            }}
                            style={{ color: '#6c757d' }}
                          >
                            <TypeBold size={14} />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              executeCommand('italic');
                            }}
                            style={{ color: '#6c757d' }}
                          >
                            <TypeItalic size={14} />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              executeCommand('underline');
                            }}
                            style={{ color: '#6c757d' }}
                          >
                            <TypeUnderline size={14} />
                          </Button>
                          <div className="vr" style={{ height: '20px', margin: '0 0.25rem' }}></div>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              const url = prompt('Enter URL:');
                              if (url) executeCommand('createLink', url);
                            }}
                            style={{ color: '#6c757d' }}
                          >
                            <Link45deg size={14} />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              executeCommand('insertText', '@');
                            }}
                            style={{ color: '#6c757d' }}
                          >
                            <At size={14} />
                          </Button>
                          <div className="vr" style={{ height: '20px', margin: '0 0.25rem' }}></div>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              executeCommand('insertUnorderedList');
                            }}
                            style={{ color: '#6c757d' }}
                          >
                            <ListUl size={14} />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              executeCommand('insertOrderedList');
                            }}
                            style={{ color: '#6c757d' }}
                          >
                            <ListOl size={14} />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              executeCommand('outdent');
                            }}
                            style={{ color: '#6c757d' }}
                            title="Decrease indent"
                          >
                            <ArrowLeft size={14} />
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              executeCommand('indent');
                            }}
                            style={{ color: '#6c757d' }}
                            title="Increase indent"
                          >
                            <ArrowRight size={14} />
                          </Button>
                          <div className="vr" style={{ height: '20px', margin: '0 0.25rem' }}></div>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              executeCommand('strikeThrough');
                            }}
                            style={{ color: '#6c757d' }}
                          >
                            <TypeStrikethrough size={14} />
                          </Button>
                          <div className="flex-grow-1"></div>
                          <div className="vr" style={{ height: '20px', margin: '0 0.25rem' }}></div>
                          <InfoCircle size={14} className="text-muted me-2" />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleCancelNote}
                            style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleSaveNote}
                            disabled={!currentNoteContent.trim() && !noteEditorRef?.textContent?.trim()}
                            style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}
                          >
                            Save
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Focus</h6>
                      {(pinnedFocusNotes.length === 0 && upcomingFocusActivities.length === 0) ? (
                        <p className="text-muted small" style={{ fontSize: '0.7rem' }}>No focus items yet.</p>
                      ) : (
                        <div className="mb-2">
                          {pinnedFocusNotes.map((note) => (
                            <div key={`focus-note-${note.id}`} className="d-flex align-items-start gap-2 mb-2">
                              <div
                                className="d-inline-flex align-items-center justify-content-center border"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '999px',
                                  backgroundColor: '#f8f9fa',
                                  color: '#0d6efd',
                                  marginTop: '2px'
                                }}
                              >
                                <Pin size={13} />
                              </div>
                              <div
                                className="flex-grow-1 border rounded"
                                style={{ backgroundColor: '#fff9e6', padding: '0.5rem 0.65rem' }}
                              >
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                  <div
                                    className="fw-semibold"
                                    style={{ fontSize: '0.75rem' }}
                                    dangerouslySetInnerHTML={{ __html: note.content }}
                                  />
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    style={{ color: '#0d6efd' }}
                                    onClick={() => handleTogglePinNote(note.id)}
                                    title="Unpin note"
                                  >
                                    <Pin size={12} />
                                  </Button>
                                </div>
                                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                  {formatDate(note.timestamp)} · {note.author}
                                </div>
                              </div>
                            </div>
                          ))}
                          {upcomingFocusActivities.slice(0, 3).map((meeting) => {
                            const activityType = getScheduledActivityTypeFromMeeting(meeting);
                            return (
                              <div key={meeting.id} className="d-flex align-items-start gap-2 mb-2">
                                <div
                                  className="d-inline-flex align-items-center justify-content-center border"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '999px',
                                    backgroundColor: '#f8f9fa',
                                    color: '#495057',
                                    marginTop: '2px'
                                  }}
                                >
                                  {renderScheduledActivityIcon(activityType)}
                                </div>
                                <div
                                  className="flex-grow-1 border rounded"
                                  style={{ backgroundColor: '#ffffff', padding: '0.5rem 0.65rem' }}
                                >
                                  <div className="d-flex align-items-start justify-content-between gap-2">
                                    <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                      {meeting.title || SCHEDULE_ACTIVITY_LABELS[activityType]}
                                    </div>
                                    <Button variant="link" size="sm" className="p-0 text-muted">
                                      <ThreeDotsVertical size={12} />
                                    </Button>
                                  </div>
                                  <div className="d-flex flex-wrap align-items-center gap-2 text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                    <span>{formatFocusDateLabel(meeting.startTime)} · PhiLL Kross</span>
                                    <span className="d-inline-flex align-items-center gap-1">
                                      <Person size={11} />
                                      {selectedLead?.name || ''}
                                    </span>
                                    {selectedLead?.organization && (
                                      <span className="d-inline-flex align-items-center gap-1">
                                        <BuildingFill size={11} />
                                        {selectedLead.organization}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-muted small mb-2" style={{ fontSize: '0.7rem' }}>Scheduled activities, pinned notes, email drafts and scheduled emails will appear here.</p>
                      <div className="d-flex align-items-stretch mb-2">
                        <Dropdown
                          show={showScheduledActivityTypeDropdown}
                          onToggle={(isOpen) => setShowScheduledActivityTypeDropdown(isOpen)}
                        >
                          <Dropdown.Toggle
                            as={IconOnlyDropdownToggle}
                            id="scheduled-activity-type-toggle"
                            style={{
                              width: '62px',
                              height: '31px',
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0,
                              borderRight: '0',
                              gap: '0.25rem'
                            }}
                          >
                            {renderScheduledActivityIcon(scheduledActivityType)}
                            <ChevronDown
                              size={9}
                              style={{
                                transition: 'transform 0.2s',
                                transform: showScheduledActivityTypeDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                              }}
                            />
                          </Dropdown.Toggle>
                          <Dropdown.Menu style={{ minWidth: '170px', padding: '0.25rem' }}>
                            {SCHEDULE_ACTIVITY_OPTIONS.map((option) => {
                              const isSelected = option.type === scheduledActivityType;
                              return (
                                <Dropdown.Item
                                  key={option.type}
                                  onClick={() => handleSelectScheduledActivityType(option.type)}
                                  style={{
                                    fontSize: '0.75rem',
                                    borderRadius: '4px',
                                    padding: '0.4rem 0.55rem',
                                    backgroundColor: isSelected ? '#0d6efd' : 'transparent',
                                    color: isSelected ? '#fff' : '#212529'
                                  }}
                                >
                                  <div className="d-flex align-items-center justify-content-between gap-2">
                                    <span className="d-flex align-items-center gap-2">
                                      <span style={{ width: '14px', display: 'inline-flex', justifyContent: 'center' }}>
                                        {renderScheduledActivityIcon(option.type)}
                                      </span>
                                      <span>{option.label}</span>
                                    </span>
                                    {isSelected && <Check size={13} />}
                                  </div>
                                </Dropdown.Item>
                              );
                            })}
                          </Dropdown.Menu>
                        </Dropdown>
                        <Form.Control
                          size="sm"
                          value={scheduledActivityText}
                          onChange={(e) => setScheduledActivityText(e.target.value)}
                          style={{
                            fontSize: '0.7rem',
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            marginLeft: '-1px'
                          }}
                        />
                      </div>
                      <div className="d-flex gap-1 mt-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('in1h')}
                          disabled={isCreatingMeeting}
                        >
                          In 1h
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('in3h')}
                          disabled={isCreatingMeeting}
                        >
                          In 3h
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('tomorrow')}
                          disabled={isCreatingMeeting}
                        >
                          Tomorrow
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('nextWeek')}
                          disabled={isCreatingMeeting}
                        >
                          Next week
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('other')}
                          disabled={isCreatingMeeting}
                        >
                          + Other
                        </Button>
                      </div>
                    </div>
                    <div>
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>History</h6>
                      <ListGroup variant="flush">
                        {/* Display saved notes */}
                        {notes.map((note) => (
                          <ListGroup.Item 
                            key={note.id} 
                            className="px-0 py-2"
                            style={{ 
                              fontSize: '0.75rem',
                              backgroundColor: '#fff9e6'
                            }}
                          >
                            <div className="d-flex align-items-start gap-2">
                              <FileText size={14} className="mt-1" style={{ color: '#6c757d' }} />
                              <div className="flex-grow-1">
                                <div 
                                  dangerouslySetInnerHTML={{ __html: note.content }}
                                  style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}
                                />
                                <div className="text-muted small" style={{ fontSize: '0.7rem' }}>
                                  {formatDate(note.timestamp)} · {note.author}
                                </div>
                              </div>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0"
                                  style={{ color: note.pinned ? '#0d6efd' : '#6c757d' }}
                                  onClick={() => handleTogglePinNote(note.id)}
                                  title={note.pinned ? 'Unpin note' : 'Pin note to Focus'}
                                >
                                  <Pin size={12} />
                                </Button>
                                <Dropdown align="end">
                                  <Dropdown.Toggle
                                    as={IconOnlyDropdownToggle}
                                    id={`note-actions-${note.id}`}
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      border: 'none',
                                      backgroundColor: 'transparent',
                                      color: '#6c757d'
                                    }}
                                  >
                                    <ThreeDotsVertical size={12} />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item
                                      onClick={() => handleDeleteNote(note.id)}
                                      style={{ fontSize: '0.75rem', color: '#dc3545' }}
                                    >
                                      Delete note
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                        {/* Lead created entry */}
                        <ListGroup.Item className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                          <div className="text-muted small" style={{ fontSize: '0.7rem' }}>Lead created</div>
                          <div style={{ fontSize: '0.75rem' }}>
                            {selectedLead?.leadCreated ? formatDate(selectedLead.leadCreated) : 'Unknown'} · PhiLL Kross
                          </div>
                        </ListGroup.Item>
                      </ListGroup>
                    </div>
                  </Tab>
                  <Tab eventKey="activity" title="Activity">
                    <div style={{ position: 'relative', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div className="mb-3">
                          <Form.Control
                            size="sm"
                            value={activitySchedulerForm.title}
                            onChange={(e) => handleActivitySchedulerFieldChange('title', e.target.value)}
                            placeholder="Activity title"
                            style={{ fontSize: '0.95rem', padding: '0.4rem 0.5rem', marginBottom: '0.5rem' }}
                          />
                          <div className="d-flex gap-1 flex-wrap">
                            {SCHEDULE_ACTIVITY_OPTIONS.map((option) => (
                              <button
                                key={option.type}
                                type="button"
                                onClick={() => handleSelectDetailedActivityType(option.type)}
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 6,
                                  border: '1px solid #dee2e6',
                                  backgroundColor: scheduledActivityType === option.type ? '#0d6efd' : '#fff',
                                  color: scheduledActivityType === option.type ? '#fff' : '#6c757d',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                }}
                                title={option.label}
                              >
                                {renderScheduledActivityIcon(option.type)}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                          <Clock size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Control
                            type="date"
                            size="sm"
                            value={activitySchedulerForm.startTime ? activitySchedulerForm.startTime.slice(0, 10) : ''}
                            onChange={(e) => {
                              const dateValue = e.target.value;
                              const startTime = activitySchedulerForm.startTime ? activitySchedulerForm.startTime.slice(11, 16) : '12:00';
                              const endTime = activitySchedulerForm.endTime ? activitySchedulerForm.endTime.slice(11, 16) : '13:00';
                              setActivitySchedulerForm((prev) => ({
                                ...prev,
                                startTime: `${dateValue}T${startTime}`,
                                endTime: `${dateValue}T${endTime}`,
                              }));
                            }}
                            style={{ width: 140, fontSize: '0.75rem' }}
                          />
                          <Form.Control
                            type="time"
                            size="sm"
                            value={activitySchedulerForm.startTime ? activitySchedulerForm.startTime.slice(11, 16) : ''}
                            onChange={(e) => {
                              const dateValue = activitySchedulerForm.startTime
                                ? activitySchedulerForm.startTime.slice(0, 10)
                                : (getDateKeyInTimezone(new Date(), tenantTimezone) || '');
                              handleActivitySchedulerFieldChange('startTime', `${dateValue}T${e.target.value}`);
                            }}
                            style={{ width: 100, fontSize: '0.75rem' }}
                          />
                          <span style={{ color: '#6c757d' }}>-</span>
                          <Form.Control
                            type="time"
                            size="sm"
                            value={activitySchedulerForm.endTime ? activitySchedulerForm.endTime.slice(11, 16) : ''}
                            onChange={(e) => {
                              const dateValue = activitySchedulerForm.endTime
                                ? activitySchedulerForm.endTime.slice(0, 10)
                                : activitySchedulerForm.startTime?.slice(0, 10)
                                  || (getDateKeyInTimezone(new Date(), tenantTimezone) || '');
                              handleActivitySchedulerFieldChange('endTime', `${dateValue}T${e.target.value}`);
                            }}
                            style={{ width: 100, fontSize: '0.75rem' }}
                          />
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Calendar size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.priority}
                            onChange={(e) => handleActivitySchedulerFieldChange('priority', e.target.value)}
                            style={{ width: 140, fontSize: '0.75rem' }}
                          >
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                          </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Briefcase size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.purposeTypeId}
                            onChange={(e) => handleActivitySchedulerFieldChange('purposeTypeId', e.target.value)}
                            style={{ flex: 1, maxWidth: 280, fontSize: '0.75rem' }}
                          >
                            {meetingPurposeOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Link45deg size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Control
                            size="sm"
                            value={activitySchedulerForm.location}
                            onChange={(e) => handleActivitySchedulerFieldChange('location', e.target.value)}
                            placeholder="Add location"
                            style={{ flex: 1, fontSize: '0.75rem' }}
                          />
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Paperclip size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.videoCallProvider}
                            onChange={(e) => {
                              const provider = e.target.value as '' | 'zoom' | 'teams' | 'google-meet';
                              setActivitySchedulerForm((prev) => ({
                                ...prev,
                                videoCallProvider: provider,
                                meetingType: provider ? 'video' : ((scheduledActivityType === 'call' || scheduledActivityType === 'text') ? 'phone' : 'in-person'),
                                meetingUrl: provider ? prev.meetingUrl : '',
                              }));
                            }}
                            style={{ flex: 1, maxWidth: 260, fontSize: '0.75rem' }}
                          >
                            <option value="">Install video call integration</option>
                            <option value="zoom">Zoom meeting</option>
                            <option value="teams">Microsoft Teams meeting</option>
                            <option value="google-meet">Google Meet meeting</option>
                          </Form.Select>
                        </div>

                        {activitySchedulerForm.videoCallProvider && (
                          <div className="d-flex align-items-center gap-2 mb-2" style={{ marginLeft: 24 }}>
                            <Form.Control
                              size="sm"
                              value={activitySchedulerForm.meetingUrl}
                              onChange={(e) => handleActivitySchedulerFieldChange('meetingUrl', e.target.value)}
                              placeholder={
                                activitySchedulerForm.videoCallProvider === 'google-meet'
                                  ? 'Paste Google Meet link here'
                                  : activitySchedulerForm.videoCallProvider === 'zoom'
                                    ? 'Paste Zoom link here'
                                    : 'Paste Teams link here'
                              }
                              style={{ flex: 1, fontSize: '0.75rem' }}
                            />
                          </div>
                        )}

                        <div className="d-flex align-items-center gap-2 mb-3">
                          <Tag size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.freeBusy}
                            onChange={(e) => handleActivitySchedulerFieldChange('freeBusy', e.target.value)}
                            style={{ width: 100, fontSize: '0.75rem' }}
                          >
                            <option value="free">Free</option>
                            <option value="busy">Busy</option>
                          </Form.Select>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Controls whether this activity blocks your schedule.</Tooltip>}>
                            <span style={{ color: '#6c757d', cursor: 'pointer', display: 'inline-flex' }}>
                              <InfoCircle size={16} />
                            </span>
                          </OverlayTrigger>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <FileText size={16} style={{ color: '#6c757d' }} />
                            <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Notes</span>
                          </div>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            size="sm"
                            value={activitySchedulerForm.notes}
                            onChange={(e) => handleActivitySchedulerFieldChange('notes', e.target.value)}
                            placeholder="Notes are visible within the app, but not to event guests."
                            style={{ fontSize: '0.75rem', padding: '0.4rem', backgroundColor: '#fffef0', border: '1px solid #dee2e6' }}
                          />
                          <div style={{ fontSize: '0.65rem', color: '#6c757d', marginTop: '0.25rem' }}>
                            Notes are visible within the app, but not to event guests.
                          </div>
                        </div>

                        <div className="mb-2">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <Person size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <Form.Control size="sm" value="You" readOnly style={{ flex: 1, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }} />
                          </div>

                          <div className="d-flex align-items-center gap-2 mb-1" style={{ position: 'relative' }}>
                            <CurrencyDollar size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <div style={{ flex: 1, position: 'relative' }}>
                              <Form.Control
                                size="sm"
                                value={activityDealLeadDisplayLabel || activityDealLeadSearch}
                                onChange={(e) => {
                                  setActivityDealLeadSearch(e.target.value);
                                  setShowActivityDealLeadDropdown(true);
                                  if (activitySchedulerForm.dealId || activitySchedulerForm.leadId) {
                                    setActivitySchedulerForm((prev) => ({ ...prev, dealId: '', leadId: '' }));
                                  }
                                }}
                                onFocus={() => setShowActivityDealLeadDropdown(true)}
                                onBlur={() => setTimeout(() => setShowActivityDealLeadDropdown(false), 200)}
                                placeholder="Deal or Lead"
                                style={{ width: '100%', fontSize: '0.75rem' }}
                              />
                              {(activitySchedulerForm.dealId || activitySchedulerForm.leadId) && (
                                <button
                                  type="button"
                                  onClick={() => setActivitySchedulerForm((prev) => ({ ...prev, dealId: '', leadId: '' }))}
                                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                                >
                                  <X size={12} />
                                </button>
                              )}
                              {showActivityDealLeadDropdown && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                                  {filteredActivityDealsAndLeads.length === 0 ? (
                                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No deals or leads found</div>
                                  ) : (
                                    filteredActivityDealsAndLeads.map((item) => (
                                      <div
                                        key={`${item.type}-${item.id}`}
                                        onClick={() => {
                                          if (item.type === 'deal') {
                                            setActivitySchedulerForm((prev) => ({ ...prev, dealId: item.id, leadId: '' }));
                                          } else {
                                            setActivitySchedulerForm((prev) => ({ ...prev, leadId: item.id, dealId: '' }));
                                          }
                                          setActivityDealLeadSearch('');
                                          setShowActivityDealLeadDropdown(false);
                                        }}
                                        style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        {item.label}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2 mb-1" style={{ position: 'relative' }}>
                            <PeopleFill size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <div style={{ flex: 1, position: 'relative' }}>
                              <Form.Control
                                size="sm"
                                value={activityContactDisplayLabel || activityPeopleSearch}
                                onChange={(e) => {
                                  setActivityPeopleSearch(e.target.value);
                                  setShowActivityPeopleDropdown(true);
                                  if (activitySchedulerForm.contactId) setActivitySchedulerForm((prev) => ({ ...prev, contactId: '' }));
                                }}
                                onFocus={() => setShowActivityPeopleDropdown(true)}
                                onBlur={() => setTimeout(() => setShowActivityPeopleDropdown(false), 200)}
                                placeholder="People"
                                style={{ width: '100%', fontSize: '0.75rem' }}
                              />
                              {activitySchedulerForm.contactId && (
                                <button
                                  type="button"
                                  onClick={() => setActivitySchedulerForm((prev) => ({ ...prev, contactId: '' }))}
                                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                                >
                                  <X size={12} />
                                </button>
                              )}
                              {showActivityPeopleDropdown && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                                  {filteredActivityContacts.length === 0 ? (
                                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No contacts found</div>
                                  ) : (
                                    filteredActivityContacts.map((item) => (
                                      <div
                                        key={item.id}
                                        onClick={() => {
                                          setActivitySchedulerForm((prev) => ({ ...prev, contactId: item.id }));
                                          setActivityPeopleSearch('');
                                          setShowActivityPeopleDropdown(false);
                                        }}
                                        style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        {item.label}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2" style={{ position: 'relative' }}>
                            <BuildingFill size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <div style={{ flex: 1, position: 'relative' }}>
                              <Form.Control
                                size="sm"
                                value={activityOrganizationDisplayLabel || activityOrganizationSearch}
                                onChange={(e) => {
                                  setActivityOrganizationSearch(e.target.value);
                                  setShowActivityOrganizationDropdown(true);
                                  if (activitySchedulerForm.clientId) setActivitySchedulerForm((prev) => ({ ...prev, clientId: '' }));
                                }}
                                onFocus={() => setShowActivityOrganizationDropdown(true)}
                                onBlur={() => setTimeout(() => setShowActivityOrganizationDropdown(false), 200)}
                                placeholder="Organization"
                                style={{ width: '100%', fontSize: '0.75rem' }}
                              />
                              {activitySchedulerForm.clientId && (
                                <button
                                  type="button"
                                  onClick={() => setActivitySchedulerForm((prev) => ({ ...prev, clientId: '' }))}
                                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                                >
                                  <X size={12} />
                                </button>
                              )}
                              {showActivityOrganizationDropdown && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                                  {filteredActivityOrganizations.length === 0 ? (
                                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No organizations found</div>
                                  ) : (
                                    filteredActivityOrganizations.map((item) => (
                                      <div
                                        key={item.id}
                                        onClick={() => {
                                          setActivitySchedulerForm((prev) => ({ ...prev, clientId: item.id }));
                                          setActivityOrganizationSearch('');
                                          setShowActivityOrganizationDropdown(false);
                                        }}
                                        style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        {item.label}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <OverlayTrigger
                        placement="top"
                        trigger={[]}
                        show={showCalendarToggleTooltip}
                        overlay={
                          <Tooltip id="lead-activity-calendar-toggle-tooltip" style={{ pointerEvents: 'none' }}>
                            {showActivityCalendar ? 'Hide calendar' : 'Show calendar'}
                          </Tooltip>
                        }
                      >
                        <button
                          type="button"
                          onClick={(event) => {
                            event.currentTarget.blur();
                            setShowCalendarToggleTooltip(false);
                            setShowActivityCalendar((prev) => !prev);
                          }}
                          onMouseEnter={() => setShowCalendarToggleTooltip(true)}
                          onMouseLeave={() => setShowCalendarToggleTooltip(false)}
                          onFocus={() => setShowCalendarToggleTooltip(true)}
                          onBlur={() => setShowCalendarToggleTooltip(false)}
                          aria-label={showActivityCalendar ? 'Hide calendar' : 'Show calendar'}
                          style={{
                            position: 'absolute',
                            right: showActivityCalendar ? ACTIVITY_CALENDAR_PANEL_WIDTH : 0,
                            top: 120,
                            transform: 'translate(50%, -50%)',
                            width: 42,
                            height: 42,
                            borderRadius: '999px',
                            border: '1px solid #ced4da',
                            backgroundColor: '#f8f9fa',
                            color: '#495057',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 3,
                            transition: 'right 0.25s ease',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                          }}
                        >
                          {showActivityCalendar ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
                        </button>
                      </OverlayTrigger>

                      <div
                        style={{
                          width: showActivityCalendar ? ACTIVITY_CALENDAR_PANEL_WIDTH : 0,
                          maxWidth: '100%',
                          flex: '0 0 auto',
                          overflow: 'hidden',
                          opacity: showActivityCalendar ? 1 : 0,
                          transition: 'width 0.25s ease, opacity 0.2s ease',
                          borderLeft: showActivityCalendar ? '1px solid #dee2e6' : 'none',
                          paddingLeft: 0,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            paddingLeft: '0.5rem',
                            paddingRight: '0.35rem',
                            pointerEvents: showActivityCalendar ? 'auto' : 'none',
                          }}
                        >
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                            {toLocaleDateStringInTimezone(
                              getActivitySchedulerDisplayDate(),
                              'en-US',
                              { weekday: 'long', month: 'long', day: 'numeric' },
                              tenantTimezone
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-1">
                            <button
                              type="button"
                              onClick={handleActivitySchedulerPrevDay}
                              style={{ border: 'none', background: 'none', padding: '0.15rem', cursor: 'pointer', color: '#6c757d' }}
                              title="Previous day"
                            >
                              <ArrowLeft size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={handleActivitySchedulerNextDay}
                              style={{ border: 'none', background: 'none', padding: '0.15rem', cursor: 'pointer', color: '#6c757d' }}
                              title="Next day"
                            >
                              <ArrowRight size={14} />
                            </button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={handleActivitySchedulerToday}
                              style={{ fontSize: '0.65rem', padding: '0.12rem 0.35rem', whiteSpace: 'nowrap' }}
                            >
                              Today
                            </Button>
                          </div>
                        </div>

                        <div style={{ display: 'flex', overflow: 'hidden', borderRadius: 4, border: '1px solid #dee2e6' }}>
                          <div style={{ width: 44, flexShrink: 0, backgroundColor: '#f8f9fa', padding: '0.25rem 0' }}>
                            {Array.from({ length: ACTIVITY_CALENDAR_VISIBLE_HOURS }, (_, i) => activityCalendarStartHour + i).map((hour) => (
                              <div
                                key={`activity-hour-label-${hour}`}
                                style={{
                                  height: ACTIVITY_CALENDAR_SLOT_HEIGHT,
                                  fontSize: '0.65rem',
                                  color: '#6c757d',
                                  paddingLeft: '0.35rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                {formatActivityCalendarHourLabel(hour)}
                              </div>
                            ))}
                          </div>
                          <div
                            onClick={handleActivityCalendarGridClick}
                            style={{ flex: 1, position: 'relative', minHeight: ACTIVITY_CALENDAR_VISIBLE_HOURS * ACTIVITY_CALENDAR_SLOT_HEIGHT, cursor: 'pointer' }}
                          >
                            {Array.from({ length: ACTIVITY_CALENDAR_VISIBLE_HOURS }, (_, i) => activityCalendarStartHour + i).map((hour) => (
                              <div
                                key={`activity-hour-grid-${hour}`}
                                style={{
                                  height: ACTIVITY_CALENDAR_SLOT_HEIGHT,
                                  borderBottom: '1px solid #eee',
                                }}
                              />
                            ))}
                            {(() => {
                              const displayDate = getActivitySchedulerDisplayDate();
                              const now = new Date(activityNowTick);
                              const isTodayLine =
                                displayDate.getDate() === now.getDate()
                                && displayDate.getMonth() === now.getMonth()
                                && displayDate.getFullYear() === now.getFullYear();
                              const nowHour = now.getHours() + now.getMinutes() / 60;
                              const visibleStartHour = activityCalendarStartHour;
                              const visibleEndHour = activityCalendarStartHour + ACTIVITY_CALENDAR_VISIBLE_HOURS;
                              const topPx = isTodayLine && nowHour >= visibleStartHour && nowHour < visibleEndHour
                                ? (nowHour - visibleStartHour) * ACTIVITY_CALENDAR_SLOT_HEIGHT
                                : -999;
                              return (
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    top: topPx,
                                    height: 2,
                                    backgroundColor: '#dc3545',
                                    display: 'flex',
                                    alignItems: 'center',
                                    zIndex: 2,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: '0.6rem',
                                      color: '#dc3545',
                                      backgroundColor: '#fff',
                                      padding: '0 0.2rem',
                                      marginLeft: 4,
                                    }}
                                  >
                                    {toLocaleTimeStringInTimezone(
                                      now,
                                      'en-US',
                                      { hour: 'numeric', minute: '2-digit' },
                                      tenantTimezone
                                    )}
                                  </span>
                                  <span
                                    style={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      backgroundColor: '#dc3545',
                                      marginLeft: 2,
                                    }}
                                  />
                                </div>
                              );
                            })()}
                            {activitySchedulerForm.startTime && activitySchedulerForm.endTime && (() => {
                              const start = new Date(activitySchedulerForm.startTime);
                              const end = new Date(activitySchedulerForm.endTime);
                              const displayDate = getActivitySchedulerDisplayDate();
                              const sameDay =
                                start.getDate() === displayDate.getDate()
                                && start.getMonth() === displayDate.getMonth()
                                && start.getFullYear() === displayDate.getFullYear();
                              if (!sameDay || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
                              let startHour = start.getHours() + start.getMinutes() / 60;
                              let endHour = end.getHours() + end.getMinutes() / 60;
                              if (endHour < startHour) endHour += 24;
                              const visibleStartHour = activityCalendarStartHour;
                              const visibleEndHour = activityCalendarStartHour + ACTIVITY_CALENDAR_VISIBLE_HOURS;
                              if (startHour >= visibleEndHour || endHour <= visibleStartHour) return null;
                              const top = (Math.max(startHour, visibleStartHour) - visibleStartHour) * ACTIVITY_CALENDAR_SLOT_HEIGHT;
                              const height = Math.max(
                                (Math.min(endHour, visibleEndHour) - Math.max(startHour, visibleStartHour)) * ACTIVITY_CALENDAR_SLOT_HEIGHT,
                                24
                              );
                              return (
                                <div
                                  className="rounded"
                                  style={{
                                    position: 'absolute',
                                    left: 4,
                                    right: 4,
                                    top,
                                    height: Math.max(height, 24),
                                    backgroundColor: '#0d6efd',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '0 0.35rem',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    zIndex: 1,
                                  }}
                                >
                                  {renderScheduledActivityIcon(scheduledActivityType)}
                                  {activitySchedulerForm.title || 'Activity'}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                      <Form.Check
                        type="checkbox"
                        id="lead-activity-mark-done"
                        label="Mark as done"
                        checked={activitySchedulerForm.markAsDone}
                        onChange={(e) => handleActivitySchedulerFieldChange('markAsDone', e.target.checked)}
                        style={{ fontSize: '0.75rem', marginBottom: 0 }}
                      />
                      <div className="d-flex gap-2">
                        <Button variant="secondary" size="sm" onClick={handleCancelDetailedActivity}>
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleSaveDetailedActivity}
                          disabled={isCreatingMeeting || !activitySchedulerForm.title || !activitySchedulerForm.startTime || !activitySchedulerForm.endTime}
                        >
                          {isCreatingMeeting ? <Spinner size="sm" animation="border" /> : 'Save'}
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3">
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Timeline</h6>
                      {leadActivityTimeline.length === 0 ? (
                        <p className="text-muted small" style={{ fontSize: '0.7rem' }}>No activities yet for this lead.</p>
                      ) : (
                        <ListGroup variant="flush">
                          {leadActivityTimeline.map((meeting) => {
                            const activityType = getScheduledActivityTypeFromMeeting(meeting);
                            const meetingStartMs = new Date(meeting.startTime).getTime();
                            const isUpcoming = !Number.isNaN(meetingStartMs) && meetingStartMs >= Date.now();
                            return (
                              <ListGroup.Item key={`activity-entry-${meeting.id}`} className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                                <div className="d-flex align-items-start gap-2">
                                  <div
                                    className="d-inline-flex align-items-center justify-content-center border"
                                    style={{
                                      width: '30px',
                                      height: '30px',
                                      borderRadius: '999px',
                                      backgroundColor: '#f8f9fa',
                                      color: '#495057',
                                      marginTop: '2px'
                                    }}
                                  >
                                    {renderScheduledActivityIcon(activityType)}
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                      <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                        {meeting.title || SCHEDULE_ACTIVITY_LABELS[activityType]}
                                      </div>
                                      <Badge bg={isUpcoming ? 'primary' : 'secondary'} style={{ fontSize: '0.6rem' }}>
                                        {isUpcoming ? 'Upcoming' : 'Past'}
                                      </Badge>
                                    </div>
                                    <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                                      {formatDate(meeting.startTime)}
                                    </div>
                                  </div>
                                </div>
                              </ListGroup.Item>
                            );
                          })}
                        </ListGroup>
                      )}
                    </div>
                  </Tab>
                  <Tab eventKey="email" title="Email">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="small mb-0" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                        Linked threads
                      </h6>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => refetchLeadEmailThreads()}
                        disabled={!leadEmailAddress || isRefreshingLeadEmailThreads}
                        style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }}
                      >
                        {isRefreshingLeadEmailThreads ? <Spinner size="sm" animation="border" /> : 'Refresh'}
                      </Button>
                    </div>
                    {!leadEmailAddress ? (
                      <p className="text-muted small" style={{ fontSize: '0.7rem' }}>
                        Add an email address to this lead to link Unibox threads.
                      </p>
                    ) : isLoadingLeadEmailThreads ? (
                      <div className="d-flex align-items-center gap-2 text-muted small" style={{ fontSize: '0.7rem' }}>
                        <Spinner size="sm" animation="border" />
                        Loading email threads...
                      </div>
                    ) : leadEmailThreadsError ? (
                      <Alert variant="danger" className="py-2 px-3 mb-0" style={{ fontSize: '0.7rem' }}>
                        Failed to load email threads.
                      </Alert>
                    ) : leadEmailThreads.length === 0 ? (
                      <p className="text-muted small mb-0" style={{ fontSize: '0.7rem' }}>
                        No email threads found for {leadEmailAddress}.
                      </p>
                    ) : (
                      <ListGroup variant="flush">
                        {leadEmailThreads.map((thread) => (
                          <ListGroup.Item key={thread.id} className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                            <div className="d-flex align-items-start gap-2">
                              <Envelope size={14} className="mt-1" style={{ color: '#6c757d' }} />
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                  <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                    {thread.subject}
                                  </div>
                                  <div className="d-flex align-items-center gap-1">
                                    {thread.unreadCount > 0 && (
                                      <Badge bg="primary" style={{ fontSize: '0.6rem' }}>
                                        {thread.unreadCount} unread
                                      </Badge>
                                    )}
                                    {thread.messageCount > 1 && (
                                      <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                                        {thread.messageCount} msgs
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                                  {formatDate(thread.latestDate)} · {thread.latestFrom || 'Unknown sender'}
                                </div>
                                <div className="mt-2 d-flex flex-column gap-2">
                                  {thread.messages.map((message) => (
                                    <div key={`${thread.id}-${message.id}`} className="border-start ps-2">
                                      <div className="text-muted small" style={{ fontSize: '0.67rem' }}>
                                        {formatDate(message.date)} · {message.from || 'Unknown sender'}
                                      </div>
                                      <div className="small mt-1" style={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                        {message.preview || 'No preview available.'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Tab>
                  <Tab eventKey="files" title="Files">
                    <p className="text-muted small" style={{ fontSize: '0.7rem' }}>Files will appear here.</p>
                  </Tab>
                </Tabs>
              </div>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
      </div>
    </Container>
  );
};

export default Leads;
